// src/pages/ContractForm.jsx

import { useAuth } from "../context/AuthContext";
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import logo from "../assets/DesarrollandoTerritorios.png"
import "../styles/Formulario.css";

import useWindowSize from "../hooks/useWindowSize.js";

import { sendNotificationEmail } from "../utils/email-service";
import { generateContractPDF } from "../utils/pdf-generator";

import Select from "react-select";

import CDPGenerator from "../components/CDP";
import RPGenerator from "../components/RP";
import EnviarCDP from "../components/EnviarCDP";
import EnviarRP from "../components/EnviarRP";
import AdquisicionBienes from "../components/AdquisicionBienes";
import PresupuestoSolicitudesModal from "../components/PresupuestoSolicitudesModal";
import PresupuestoModal from "../components/PresupuestoModal";

import { apiRequest } from "../services/apiClient";
import { apiConfig } from "../config/api.config";
import { getToken } from "../lib/supabase";
import BackToMenu from "../components/BackToMenu.jsx";
import EnviarARL from "../components/EnviarARL"





// 🔹 Mapa de IDs de rol → nombre interno
export const ROLE_MAP = {
  1: "solicitante",
  2: "planeacion",
  3: "hacienda",
  4: "juridica",
  5: "alcalde",
  6: "administrador",
  7: "presupuesto",
  8: "almacen",
  9: "validador",
  10: "firma_secretario",
  11: "administrador_junior",
  12: "abogado",
  14: "sst",  
}

// 🔹 Normalizador de rol (id numérico o string con tildes)
export const normalizeRole = (rawRole) => {
  if (typeof rawRole === "number") return ROLE_MAP[rawRole] || "";
  if (!rawRole) return "";

  return String(rawRole)
    .toLowerCase()
    .normalize("NFD") // quita tildes: "Planeación" → "Planeacion"
    .replace(/[\u0300-\u036f]/g, "");
};

export const toInputDate = (raw) => {
  if (!raw) return "";

  // 0) Si ya viene como "YYYY-MM-DD" (lo que sale del input type="date"),
  // NO lo conviertas a Date (evita el bug UTC que resta 1 día).
  if (typeof raw === "string") {
    const s = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

    // 1) Si viene como ISO con hora (por ejemplo "2026-01-07T00:00:00.000Z"),
    // toma solo la parte de fecha y construye un Date LOCAL seguro.
    // (Así no se corre al día anterior en UTC-5)
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})T/);
    if (iso) {
      const y = Number(iso[1]);
      const mo = Number(iso[2]) - 1;
      const d = Number(iso[3]);
      const local = new Date(y, mo, d);
      const yy = local.getFullYear();
      const mm = String(local.getMonth() + 1).padStart(2, "0");
      const dd = String(local.getDate()).padStart(2, "0");
      return `${yy}-${mm}-${dd}`;
    }
  }

  // 2) Date real
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, "0");
    const d = String(raw.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  // 3) String / number general (no-ISO fecha pura)
  let d = new Date(raw);

  // Fallback si viene en formato tipo "Dec 18 2025 9:06AM"
  if (Number.isNaN(d.getTime())) {
    const str = String(raw).trim();
    const parts = str.split(/\s+/);
    if (parts.length >= 3) {
      d = new Date(`${parts[0]} ${parts[1]} ${parts[2]}`);
    }
  }

  if (Number.isNaN(d.getTime())) {
    console.warn("No se pudo parsear fecha:", raw);
    return "";
  }

  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
export function calcularDiasInclusive(inicioStr, finStr) {
  if (!inicioStr || !finStr) return "";

  const [iy, im, id] = String(inicioStr).split("-").map(Number);
  const [fy, fm, fd] = String(finStr).split("-").map(Number);

  const inicio = new Date(iy, (im || 1) - 1, id || 1);
  const fin = new Date(fy, (fm || 1) - 1, fd || 1);

  if (Number.isNaN(inicio.getTime()) || Number.isNaN(fin.getTime())) return "";

  // Normaliza horas
  inicio.setHours(0, 0, 0, 0);
  fin.setHours(0, 0, 0, 0);

  const diffMs = fin.getTime() - inicio.getTime();
  if (diffMs < 0) return "";

  const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return String(diffDias + 1); // inclusive
}

// ✅ Genera consecutivo localmente: 0001-2026
export const generateConsecutiveRadicado = async (token, year) => {
  try {
    const yr = Number(year);
    if (!yr || Number.isNaN(yr)) return null;

    const url = apiConfig.getUrl(apiConfig.endpoints.contracts || "/api/contracts");

    const res = await apiRequest(url, token, { method: "GET" });
    const rows = Array.isArray(res)
      ? res
      : Array.isArray(res?.contracts)
        ? res.contracts
        : [];

    const isYear = (n) => n >= 2000 && n <= 2100;

    // SOLO acepta formato: 0001-2026 (seq-año). Rechaza 2026-0001.
    const parseSeqYear = (radicado) => {
      if (!radicado) return null;
      const s = String(radicado).trim();

      // Busca patrones ####-#### en cualquier parte del string (tolerante a texto)
      const matches = s.match(/\b(\d{4})-(\d{4})\b/g);
      if (!matches || matches.length === 0) return null;

      // Tomamos el último match por si hay más de uno
      const last = matches[matches.length - 1];
      const m = last.match(/^(\d{4})-(\d{4})$/);
      if (!m) return null;

      const seq = Number(m[1]);
      const yr2 = Number(m[2]);

      // Validación estricta: segundo bloque debe ser año
      if (!isYear(yr2)) return null;

      // Validación de secuencia
      if (!Number.isInteger(seq) || seq < 0 || seq > 9999) return null;

      return { seq, year: yr2 };
    };

    const maxSeq = rows.reduce((acc, c) => {
      const parsed = parseSeqYear(c?.radicado);
      if (!parsed || parsed.year !== yr) return acc;
      return Math.max(acc, parsed.seq);
    }, 0);

    const next = maxSeq + 1;

    // SALIDA FINAL: SOLO números en formato 0001-2026
    return `${String(next).padStart(4, "0")}-${yr}`;
  } catch (error) {
    console.error(
      "Error crítico al generar radicado. El backend lo generará al guardar:",
      error
    );
    return null;
  }
};





const textoObjetoContractual =
  '"'

const centroCostosOptions = [
   { value: "", label: "Seleccionar..." },
                        { value: "01001", label: "01001 CONCEJO" },
                        { value: "02001", label: "02001 PERSONERIA" },
                        { value: "03001", label: "03001 JURIDICA" },
                        { value: "04001", label: "04001 ALCALDIA" },
                        { value: "05001", label: "05001 EDUCACION" },
                        { value: "05002", label: "05002 BIBLIOTECA MUNICIPAL" },
                        { value: "06001", label: "06001 GOBIERNO" },
                        { value: "06002", label: "06002 INSPECCION 1ra POLICIA" },
                        { value: "06006", label: "06006 INSPECCION 2da DE POLICIA" },
                        { value: "06007", label: "06007 INSPECCION 3ra INFRACCIONES URBANISTICAS" },
                        { value: "06008", label: "06008 INSPECCION 4ta AFECTACIONES AMBIENTALES" },
                        { value: "06003", label: "06003 COMISARIA DE FAMILIA NORTE" },
                        { value: "06009", label: "06009 COMISARIA DE FAMILIA SUR" },
                        { value: "06004", label: "06004 FONSET" },
                        { value: "06005", label: "06005 GESTION DEL RIESGO" },
                        { value: "07001", label: "07001 DESARROLLO Y BIENESTAR SOCIAL" },
                        { value: "07002", label: "07002 VICTIMAS DE CONFLICTO" },
                        { value: "07003", label: "07003 TURISMO" },
                        { value: "07004", label: "07004 JUVENTUD " },
                        { value: "07005", label: "07005 HABITANTE DE CALLE" },
                        { value: "08001", label: "08001 PLANEACIÓN" },
                        { value: "08002", label: "08002 SISBEN" },
                        { value: "08003", label: "08003 CALIDAD" },
                        { value: "09001", label: "09001 INFRAESTRUCTURA" },
                        { value: "10001", label: "10001 HACIENDA" },
                        { value: "10002", label: "10002 CATASTRO" },
                        { value: "11001", label: "11001 SERVICIOS ADMINISTRATIVOS" },
                        { value: "11002", label: "11002 PERSONAL" },
                        { value: "11003", label: "11003 ALMACEN" },
                        { value: "11004", label: "11004 ARCHIVO" },
                        { value: "12001", label: "12001 SALUD" },
                        { value: "12002", label: "12002 ADULTO MAYOR" },
                        { value: "12003", label: "12003 SANEAMIENTO" },
                        { value: "12004", label: "12004 PLAN ALIMENTARIO ESCOLAR" },
                        { value: "14001", label: "14001 CULTURA" },
                        { value: "15001", label: "15001 MOVILIDAD" },
                        { value: "16001", label: "16001 INVICOP" },
                        { value: "17001", label: "17001 JUNTA DE DEPORTES" },
                        { value: "18001", label: "18001 AGRICULTURA Y MEDIO AMBIENTE" },
                        { value: "18002", label: "18002 ALBERGUE" },
                        { value: "20001", label: "20001 CONTROL INTERNO" },
                        { value: "21001", label: "21001 COMUNICACIONES" },
                        { value: "22001", label: "22001 CONTROL DISCIPLINARIO" },
                        { value: "23001", label: "23001 DIRECCION FINANCIERA" },
                        { value: "23002", label: "23002 COBRO COACTIVO" }
];
const opcionesProgramas = [
  { value: "", label: "Seleccionar..." },
  {
    value: "1.1.1",
    label:
      "1.1.1 Fortalecimiento de los servicios de salud hacia la descentralizacion, la ampliacion de la cobertura. ",
  },
  { value: "1.1.2", label: "1.1.2 Vida Saludable en Copacabana. " },
  { value: "1.1.3", label: "1.1.3 Infraestructura innovadora y con calidad. " },
  { value: "1.1.4", label: "1.1.4  Fortalecimiento de los niveles de gobernanza de la salud.  " },
  { value: "1.2.1", label: "1.2.1 Fortalecimiento integral, pedagógico y educativo. " },
  { value: "1.2.2", label: "1.2.2 Acceso y permanencia en sistema educativo desde un enfoque territorial. " },
  {
    value: "1.2.3",
    label:
      "1.2.3 Desarrollo de un ecosistema  educativo 4.0 que apunte al fortalecimiento y encadenamineto de los diferentes niveles educativos. ",
  },
  { value: "1.3.1", label: "1.3.1 La cultura como vocación para el desarrollo en Copacabana. " },
  { value: "1.3.2", label: "1.3.2 Gestión, protección y salvaguardia del patrimonio cultural. " },
  { value: "1.3.3", label: "1.3.3 Mejoramiento y dotación de la infraestructura cultural.  " },
  { value: "1.3.4", label: "1.3.4 Gobernaza para la inclusión cultural. " },
  {
    value: "1.4.1",
    label:
      "1.4.1 Fortalecimiento de la cultura deportiva para el fomento a la recreación, la actividad física y el deporte. ",
  },
  {
    value: "1.4.2",
    label:
      "1.4.2 Fortalecimiento de la práctica deportiva, la infraestructura, la formación y preparación de deportistas aficionados y de alto rendimiento. ",
  },
  { value: "1.4.3", label: "1.4.3 Liderazgo en gobernanza para la gestión deportiva. " },
  {
    value: "2.1.1",
    label:
      "2.1.1 Protección, conservación y restauración de los sistemas naturales a través del SILAP y sus servicios ecosistemicos. ",
  },
  { value: "2.1.2", label: "2.1.2 Gestión Sostenible del Agua. " },
  { value: "2.1.3", label: "2.1.3 Adaptación al cambio climático. " },
  { value: "2.1.4", label: "2.1.4 Educación y cultura ambiental territorializada. " },
  { value: "2.1.5", label: "2.1.5 Gestión de residuos y fortalecimiento ambiental. " },
  { value: "2.1.6", label: "2.1.6 Producción verde y responsable. " },
  { value: "2.2.1", label: "2.2.1 Bienestar animal y tenencia responsable. " },
  { value: "2.3.1", label: "2.3.1 Hacia un gestión del riesgo integral. " },
  { value: "2.3.2", label: "2.3.2 Planificación e implementación técnica y normativa para el control del riesgo. " },
  {
    value: "2.3.3",
    label:
      "2.3.3 Capacidades técnicas y tecnológica para las medidas de prevención y mitigación de la gestión del riesgo. ",
  },
  { value: "2.4.1", label: "2.4.1 Vivienda digna para sus habitantes y servicios públicos. " },
  {
    value: "2.5.1",
    label: "2.5.1 Fortalecimiento de la Movilidad en Copacabana sostenible y con enfoque diferencial. ",
  },
  { value: "2.5.2", label: "2.5.2 Mejora de la Infraestructura Vial. " },
  { value: "2.5.3", label: "2.5.3 Movilidad innovadora e  inteligente. " },
  { value: "2.6.1", label: "2.6.1 Actualización del Plan Básico de Ordenamineto Territorial. " },
  { value: "2.6.2", label: "2.6.2 Ordenamiento territorial, infraestructura y desarrollo urbano. " },
  { value: "3.1.1", label: "3.1.1 Hacia una cultura del emprendimiento y el desarrollo empresarial. " },
  {
    value: "3.1.2",
    label: "3.1.2 Emprendimiento como alternativa para la generación y formalización para  empleo. ",
  },
  { value: "3.1.3", label: "3.1.3 Fortalecimiento empresarial desde un enfoque de  valor agregado. " },
  {
    value: "3.1.4",
    label: "3.1.4 Fortalecimiento del ecosistema empresarial y de innovación con enfoque metropolitano. ",
  },
  { value: "3.1.5", label: "3.1.5 Promoción del empleo y el trabajo. " },
  { value: "3.2.1", label: "3.2.1 Desarrollo agropecuario sostenible. " },
  { value: "3.2.2", label: "3.2.2 Desarrollo de cadenas de valor y promoción de productos locales. " },
  { value: "3.3.1", label: "3.3.1 Actualización del Plan de Desarrollo Turístico sostenible. " },
  {
    value: "3.3.2",
    label:
      "3.3.2 Gestión integral del ecosistema turistico y el fortalecimiento de la promoción y el mercadeo, para el mejoramiento de la competitividad turística. ",
  },
  { value: "3.3.3", label: "3.3.3 Fortalecimiento de la Infraestructura Turística. " },
  {
    value: "3.4.1",
    label:
      "3.4.1 Desarrollo empresarial de base tecnológica y su integración al distrito de CTeI Regional Metropolitano. ",
  },
  {
    value: "3.4.2",
    label: "3.4.2 Fortalecimiento de las capacidades digitales y la adopción de nuevas tecnologías 4.0. ",
  },
  { value: "3.4.3", label: "3.4.3 Fortalecimiento de la Infraestructura Tecnológica para la competitividad. " },
  { value: "3.4.4", label: "3.4.4 Hacia un territorio inteligente. " },
  { value: "4.1.1", label: "4.1.1 Desarrollo integral de la primera infancia. " },
  {
    value: "4.1.2",
    label: "4.1.2 Fortalecimiento de la infraestructura para el desarrollo integral de la primera infancia. ",
  },
  { value: "4.10.1", label: "4.10.1 Fortalecimiento integral de las víctimas del conflicto. " },
  {
    value: "4.11.1",
    label:
      "4.11.1 Programa integral de seguridad  e instrumentos de planificación y gestión como el plan de seguridad. ",
  },
  {
    value: "4.11.2",
    label:
      "4.11.2 Reconstrucción del Tejido Social a través del Arte y el Deporte para la convivencia en copacabana. ",
  },
  {
    value: "4.11.3",
    label: "4.11.3 Implementar soluciones comunitarias y tecnológicas para la prevención del delito. ",
  },
  {
    value: "4.2.1",
    label: "4.2.1 Desarrollo integral, las capacidades especiales y los talentos excepcionales de la niñez. ",
  },
  { value: "4.3.1", label: "4.3.1 Fortalecimiento y protección integral del adulto mayor. " },
  { value: "4.4.1", label: "4.4.1 Fortalecimiento integral de la juventud. " },
  { value: "4.5.1", label: "4.5.1 Desarrollo integral de las mujeres. " },
  { value: "4.6.1", label: "4.6.1 Fortalecimiento  integral de de la población LGTIQA+. " },
  { value: "4.7.1", label: "4.7.1 Fortalecimiento  integral de de la población en condición de discapacidad. " },
  { value: "4.8.1", label: "4.8.1 Fortalecimiento y atención   integral de de la población habitante de calle. " },
  { value: "4.9.1", label: "4.9.1 Inclusión del sector religioso y fortalecimiento del culto religioso. " },
  {
    value: "5.1.1",
    label:
      "5.1.1 Fortalecimiento de la hacienda publica municpal y la  gestión de recursos para el municipio de copacabana. ",
  },
  {
    value: "5.1.2",
    label:
      "5.1.2 Modernización institucional y de instrumentos técnicos y tecnológicos para mejorar la prestación de los servicios de manera cercana para los ciudadanos de copacabana. ",
  },
  {
    value: "5.2.1",
    label:
      "5.2.1 Fortalecimiento de la participación y la gestión pública para el desarrollo de procesos de articulación y transparencia en torno a la legitimidad y confianza comunitaria en la administración de Copacabana. ",
  },
  {
    value: "5.3.1",
    label:
      "5.3.1 Fortalecimiento integral del bienestar del personal articulada a la gestión pública en copacabana. ",
  },
]

const opcionesIndicadores = [
  { value: "", label: "Seleccionar..." },
  {
    value: "1.1.1.1",
    label:
      '1.1.1.1 Campañas de Salud en Zonas descentralizadas, en el marco de "Salud en mi barrio y mi vereda", realizadas. ',
  },
  {
    value: "1.1.1.2",
    label:
      "1.1.1.2 Cobertura de servicios de primer nivel de complejidad para la Población No Asegurada - PNA que demanda la atención. ",
  },
  { value: "1.1.1.3", label: "1.1.1.3 Cobertura del aseguramiento en salud del Régimen Subsidiado. " },
  {
    value: "1.1.1.4",
    label:
      "1.1.1.4 Acciones para impactar la cobertura en salud de los habitantes, realizando depuración de base de datos, encuestas Sisbén, afiliaciones al régimen subsidiado, afiliaciones a través del SAT, afiliaciones institucionales, seguimiento a recién nacidos y gestión PNA. ",
  },
  {
    value: "1.1.2.1",
    label:
      "1.1.2.1 Acciones como jornadas para la prevención, rutas de intervención, acompañamiento familiar y campañas de divulgación y formación en temas de consumo de sustancias psicoactivas implementadas. ",
  },
  { value: "1.1.2.2", label: "1.1.2.2 Víctimas de violencias sexuales identificadas, con atención integral. " },
  { value: "1.1.2.3", label: "1.1.2.3 Campañas en temas de salud sexual y reproductiva implementadas. " },
  {
    value: "1.1.2.4",
    label:
      "1.1.2.4 Difusiones de la situación de salud realizadas en las comunidades organizadas, mediante boletines epidemiológicos que serán compartidos por las redes sociales de la alcaldía municipal. ",
  },
  {
    value: "1.1.2.5",
    label:
      "1.1.2.5 Acciones de educación a través de campañas educativas, capacitaciones y acompañamientos para la salud oral realizadas. ",
  },
  { value: "1.1.2.6", label: "1.1.2.6 Visitas a establecimientos de interés sanitario realizadas. " },
  {
    value: "1.1.2.7",
    label:
      "1.1.2.7 Cumplimiento del Plan de Intervenciones Colectivas bajo la estrategia Atención Primaria en Salud. ",
  },
  {
    value: "1.1.2.8",
    label:
      "1.1.2.8 Campañas de detección temprana, de educación en salud y orientación para la promoción y prevención de condiciones crónicas prevalentes realizadas. ",
  },
  {
    value: "1.1.2.9",
    label:
      "1.1.2.9 Campañas para abordar situaciones de salud relacionadas con condiciones ambientales implementadas. ",
  },
  { value: "1.1.2.10", label: "1.1.2.10 Vacunas contra la rabia para perros y gatos aplicadas en el municipio. " },
  {
    value: "1.1.2.11",
    label: "1.1.2.11 Personas beneficiadas en las acciones para la prevención y atención de alteraciones visuales. ",
  },
  {
    value: "1.1.2.12",
    label: "1.1.2.12 Campañas para abordar situaciones prevalentes de origen laboral implementadas. ",
  },
  {
    value: "1.1.2.13",
    label:
      "1.1.2.13 Sesiones grupales de apoyo psicosocial para el trámite de experiencias asociadas con la conducta suicida realizados. ",
  },
  {
    value: "1.1.2.14",
    label:
      "1.1.2.14 Sesiones de capacitación sobre el reconocimiento de las habilidades para la vida dirigida a la comunidad en general realizadas. ",
  },
  {
    value: "1.1.2.15",
    label:
      "1.1.2.15 Acciones de salud pública como campañas, jornadas de concientización y capacitaciones  realizadas para el autocuidado y la promoción de hábitos de vida saludable con la comunidad en general. ",
  },
  { value: "1.1.2.16", label: "1.1.2.16 Encuentros y capacitaciones sobre salud mental realizados. " },
  {
    value: "1.1.2.17",
    label: "1.1.2.17 Índice de Riesgo de la Calidad del Agua - IRCA para consumo humano – municipal. ",
  },
  {
    value: "1.1.2.18",
    label: "1.1.2.18 Acciones en salud mental y hábitos de vida sanos dirigidas a los grupos de adulto mayor. ",
  },
  {
    value: "1.1.2.19",
    label:
      "1.1.2.19 Acciones como capacitaciones, acompañamientos a grupos poblacionales, divulgación y promoción en medios de comunicación, entre otros, en salud mental y hábitos de vida sanos dirigidas a las instituciones educativas. ",
  },
  {
    value: "1.1.2.20",
    label: "1.1.2.20 Personas beneficiadas con la estrategia Te escuchamos: Copacabana como un entorno protector. ",
  },
  {
    value: "1.1.3.1",
    label:
      "1.1.3.1 Transferencia para el fortalecimiento de la Red Pública de servicios de salud de la ESE Hospital Santa Margarita de Copacabana realizada por la Secretaría de Salud. ",
  },
  { value: "1.1.4.1", label: "1.1.4.1 Ejecución de los recursos del Fondo Local de Salud. " },
  {
    value: "1.1.4.2",
    label:
      "1.1.4.2 Satisfacción de los usuarios que acceden a los servicios de salud en la red prestadora del municipio. ",
  },
  {
    value: "1.1.4.3",
    label: "1.1.4.3 PQRSD relacionadas a Inspección, Vigilancia y Control Sanitario (IVC) atendidas. ",
  },
  { value: "1.1.4.4", label: "1.1.4.4 Cumplimiento en la capacidad de gestión en salud. " },
  {
    value: "1.1.4.5",
    label: "1.1.4.5 Cumplimiento del Plan de Acción de la Política Pública de Participación Social en Salud. ",
  },
  {
    value: "1.2.1.1",
    label:
      "1.2.1.1 Espacios para Aprender: Infraestructura educativa mejorada para la Innovación y Calidad Educativa. ",
  },
  {
    value: "1.2.1.2",
    label: "1.2.1.2 Sedes Educativas dotadas con mobiliario para un entorno de aprendizaje adecuado y funcional. ",
  },
  { value: "1.2.1.3", label: "1.2.1.3 Estrategia de tránsito armónico actualizada e implementada. " },
  {
    value: "1.2.1.4",
    label:
      "1.2.1.4 Estudiantes en proceso de transición, beneficiados con la estrategia de tránsito armónico en primera infancia. ",
  },
  {
    value: "1.2.1.5",
    label: "1.2.1.5 Personas beneficiadas con la estrategia Educación sin barreras: Unidad de Atención Integral. ",
  },
  {
    value: "1.2.1.6",
    label:
      "1.2.1.6 Estrategia 'Educación sin barreras' para la atención integral de estudiantes en situación de discapacidad, creada e implementada. ",
  },
  {
    value: "1.2.1.7",
    label: "1.2.1.7 Número de estudiantes beneficiados con el Programa de Alimentación Escolar (PAE) implementado. ",
  },
  {
    value: "1.2.1.8",
    label:
      "1.2.1.8 Estrategia formulada e implementada del Consultorio Educativo para el fortalecimiento académico de estudiantes de las EE. ",
  },
  { value: "1.2.1.9", label: "1.2.1.9 Estudiantes beneficiados por la estrategia del Consultorio Educativo. " },
  {
    value: "1.2.1.10",
    label:
      "1.2.1.10 Estudiantes beneficiados con tiquetes urbanos y rurales entregados para los estudiantes del municipio. ",
  },
  { value: "1.2.2.1", label: "1.2.2.1 Formulación, aprobación y socialización del Plan Educativo Municipal. " },
  {
    value: "1.2.2.2",
    label: "1.2.2.2 Foros de educación como escenarios de participación, diálogo y reflexión implementados. ",
  },
  { value: "1.2.2.3", label: "1.2.2.3 Estrategias de Bilingüismo implementadas. " },
  { value: "1.2.2.4", label: "1.2.2.4 Personas beneficiadas con estrategias de promoción del bilingüismo. " },
  { value: "1.2.2.5", label: "1.2.2.5 Cátedra municipal de Historia Local creada e implementada. " },
  { value: "1.2.2.6", label: "1.2.2.6 Sedes educativas con la cátedra municipal de Historia Local Implementada. " },
  {
    value: "1.2.2.7",
    label: "1.2.2.7 Programa de estímulos para la permanencia y calidad educativa creado y ejecutado. ",
  },
  {
    value: "1.2.2.8",
    label:
      "1.2.2.8 Jornadas de estímulos en el marco del programa de estímulos para la permanencia y calidad educativa. ",
  },
  {
    value: "1.2.2.9",
    label:
      "1.2.2.9 Docentes de las instituciones educativas oficiales capacitados en ámbitos técnicos, tecnológicos y de actualización académica/metodológica. ",
  },
  {
    value: "1.2.2.10",
    label:
      "1.2.2.10 Estrategia de reconocimiento y fortalecimiento de prácticas educativas innovadoras creado e implementado. ",
  },
  {
    value: "1.2.2.11",
    label:
      "1.2.2.11 Estrategia de bienestar para la comunidad educativa creada e implementada (físico, emocional y social). ",
  },
  {
    value: "1.2.2.12",
    label:
      "1.2.2.12 Docentes de las instituciones educativas oficiales beneficiados por la estrategia de bienestar docente. ",
  },
  {
    value: "1.2.2.13",
    label:
      "1.2.2.13 Establecimientos educativos articulados a los programas y estrategias de la administración municipal. ",
  },
  {
    value: "1.2.2.14",
    label:
      "1.2.2.14 Programas y estrategias de la administración en el marco de la articulación con las instituciones educativas. ",
  },
  {
    value: "1.2.2.15",
    label: "1.2.2.15 Procesos de articulación entre primera infancia y el sector educativo oficial implementados. ",
  },
  {
    value: "1.2.2.16",
    label:
      "1.2.2.16 Personas impactadas en los procesos de articulación entre primera infancia y el sector educativo oficial. ",
  },
  {
    value: "1.2.2.17",
    label:
      "1.2.2.17 Personas beneficiadas con estrategias de educación para el acceso a la educación técnica, tecnológica y profesional. ",
  },
  { value: "1.2.2.18", label: "1.2.2.18 Fondo de educación superior creado. " },
  { value: "1.2.2.19", label: "1.2.2.19 Puntaje promedio en Pruebas Saber. " },
  {
    value: "1.2.2.20",
    label:
      "1.2.2.20 Estudiantes de último grado de establecimientos públicos beneficiados con programas de preuniversitario y preicfes como fortalecimiento al acceso de la educación superior. ",
  },
  {
    value: "1.2.2.21",
    label:
      "1.2.2.21 Beneficiarios de estrategias o programas de fomento para el acceso a la educación superior o terciaria. ",
  },
  {
    value: "1.2.2.22",
    label:
      "1.2.2.22 Programa de Orientación Vocacional, intereses y motivaciones para el proyecto de vida de los estudiantes de las instituciones educativas oficiales, creado e implementado. ",
  },
  {
    value: "1.2.2.23",
    label:
      "1.2.2.23 Estudiantes de media académica beneficiados con el programa de orientación vocacional, intereses y motivaciones para el proyecto de vida. ",
  },
  {
    value: "1.2.3.1",
    label:
      "1.2.3.1 Acciones como capacitaciones, asesorías a instituciones educativas oficiales, integración a PEI, acompañamientos a docentes, directivos docentes y estudiantes para el fortalecimiento del Ecosistema educativo 4.0. ",
  },
  { value: "1.2.3.2", label: "1.2.3.2 Estrategia del Ecosistema Educativo 4.0 creada e implementada. " },
  {
    value: "1.2.3.3",
    label:
      "1.2.3.3 Programa STEAM: habilidades en ciencia, tecnología, educación, arte y matemáticas, creado e implementado. ",
  },
  { value: "1.2.3.4", label: "1.2.3.4 Estudiantes beneficiados por el programa STEAM Copacabana. " },
  { value: "1.2.3.5", label: "1.2.3.5 Estrategia Educación con vos creada e implementada. " },
  { value: "1.2.3.6", label: "1.2.3.6 Personas beneficiadas con la estrategia: Educación con Vos. " },
  { value: "1.2.3.7", label: "1.2.3.7 Estrategia de 'Cero anafabetismo' actualizada e implementada. " },
  { value: "1.2.3.8", label: "1.2.3.8 Personas beneficiadas con la estrategia de alfabetización. " },
  { value: "1.2.3.9", label: "1.2.3.9 Estrategia 'El Alcalde en Tu Colegio' creada e implementada. " },
  {
    value: "1.2.3.10",
    label:
      "1.2.3.10 Visitas a las instituciones educativas desde la estrategia 'El Alcalde en Tu Colegio' realizadas. ",
  },
  {
    value: "1.3.1.1",
    label: "1.3.1.1 Plan de estímulos, becas y reconocimientos para los artistas locales implementado. ",
  },
  { value: "1.3.1.2", label: "1.3.1.2 Capacitaciones técnicas sobre normativas y proyectos anualmente. " },
  { value: "1.3.1.3", label: "1.3.1.3 Estrategia del nodo de economía cultural y creativa gestionado. " },
  {
    value: "1.3.1.4",
    label: "1.3.1.4 Estrategia InnovArte para Impulso a la Economía Creativa de Copacabana implementada. ",
  },
  { value: "1.3.1.5", label: "1.3.1.5 Sistema de circulación artístico y cultural implementado. " },
  {
    value: "1.3.1.6",
    label: "1.3.1.6 Programa de desarrollo de habilidades artísticas para la profesionalización. ",
  },
  {
    value: "1.3.2.1",
    label:
      "1.3.2.1 Acciones de divulgación e intervención para gestión, protección y salvaguardia del patrimonio cultural realizadas. ",
  },
  {
    value: "1.3.2.2",
    label:
      "1.3.2.2 Estrategia 'Copacabana es mi pueblo', como estrategia de preservación, conservación y divulgación del patrimonio cultural, implementada. ",
  },
  { value: "1.3.2.3", label: "1.3.2.3 Escuela de vigías del patrimonio cultural creada. " },
  { value: "1.3.2.4", label: "1.3.2.4 Inventario del Patrimonio material e inmaterial realizado. " },
  { value: "1.3.2.5", label: "1.3.2.5 Planes de manejo Arqueológico realizados. " },
  { value: "1.3.2.6", label: "1.3.2.6 Planes Especiales de Manejo y Protección implementados. " },
  { value: "1.3.2.7", label: "1.3.2.7 Plan Especial de Salvaguardia implementado. " },
  {
    value: "1.3.2.8",
    label:
      "1.3.2.8 Estrategia de recuperación e integración de caminos ancestrales, en alianza entre la secretaría de medio ambiente y agricultura con la secretaría de educación y cultura, implementada. ",
  },
  { value: "1.3.3.1", label: "1.3.3.1 Espacios descentralizados para la cultura dotados. " },
  { value: "1.3.3.2", label: "1.3.3.2 Espacios de divulgación e integración cultural fortalecidos y creados. " },
  { value: "1.3.3.3", label: "1.3.3.3 Red municipal de bibliotecas en barrios y veredas creada y fortalecida. " },
  { value: "1.3.3.4", label: "1.3.3.4 Nuevas colecciones adquiridas para la red municipal de bibliotecas. " },
  { value: "1.3.3.5", label: "1.3.3.5 Espacios con Sentido: infraestructura cultural mejorada. " },
  { value: "1.3.3.6", label: "1.3.3.6 Espacios con Sentido: infraestructura cultural construida. " },
  {
    value: "1.3.4.1",
    label:
      "1.3.4.1 Programas de formación y educación cultural, inclusivos, en la Casa de la Cultura implementados. ",
  },
  {
    value: "1.3.4.2",
    label:
      "1.3.4.2 Programas del sector cultural del municipio orientados al acceso y la participación activa de la comunidad fortalecidos. ",
  },
  { value: "1.3.4.3", label: "1.3.4.3 Población en procesos artísticos y culturales formadas anualmente. " },
  {
    value: "1.3.4.4",
    label:
      "1.3.4.4 Plan decenal de Cultura como una herramienta estratégica para la articulación y orientación de las políticas culturales elaborado. ",
  },
  { value: "1.3.4.5", label: "1.3.4.5 Zonas del municipio participantes de la red municipal de bibliotecas. " },
  { value: "1.3.4.6", label: "1.3.4.6 Personas que participan de la red municipal de bibliotecas. " },
  { value: "1.3.4.7", label: "1.3.4.7 Consejo Municipal de Cultura fortalecido. " },
  { value: "1.3.4.8", label: "1.3.4.8 Alianzas para la gestión cultural realizadas. " },
  {
    value: "1.3.4.9",
    label: "1.3.4.9 Intervenciones para apoyar y fortalecer los espacios de participación ciudadana. ",
  },
  { value: "1.3.4.10", label: "1.3.4.10 Sistema municipal de información artística y cultural fortalecido. " },
  { value: "1.3.4.11", label: "1.3.4.11 Festivales y ferias artísticas y culturales realizadas. " },
  {
    value: "1.4.1.1",
    label: "1.4.1.1 Personas que acceden a servicios deportivos, recreativos y de actividad física. ",
  },
  { value: "1.4.1.2", label: "1.4.1.2 Deportes, incluyendo alternativos o no convencionales, ofertados. " },
  { value: "1.4.1.3", label: "1.4.1.3 Niños, niñas, adolescentes y jóvenes inscritos en Escuelas Deportivas. " },
  { value: "1.4.1.4", label: "1.4.1.4 Niños, niñas, adolescentes y jóvenes con talento deportivo incentivados. " },
  {
    value: "1.4.1.5",
    label:
      "1.4.1.5 Jornadas deportivas institucionales de juegos escolares, intercolegiados, departamentales y nacionales. ",
  },
  {
    value: "1.4.1.6",
    label:
      "1.4.1.6 Número de deportistas participantes en juegos escolares, intercolegiados, departamentales y nacionales. ",
  },
  {
    value: "1.4.1.7",
    label:
      "1.4.1.7 Creación de la estrategia “Noches comunitarias” como espacios de descentralización, diversión y esparcimiento para los habitantes. ",
  },
  { value: "1.4.1.8", label: "1.4.1.8 Zonas participantes de las “Noches comunitarias. ”" },
  { value: "1.4.1.9", label: "1.4.1.9 Eventos deportivos interbarriales e interveredales realizados y/o apoyados. " },
  {
    value: "1.4.1.10",
    label:
      "1.4.1.10 Torneos y festivales deportivos y recreativos realizados y/o apoyados de manera descentralizada. ",
  },
  {
    value: "1.4.1.11",
    label:
      "1.4.1.11 Eventos deportivos interinstitucionales con proyección externa al municipio realizados y/o apoyados. ",
  },
  {
    value: "1.4.2.1",
    label: "1.4.2.1 Capacitaciones a los formadores, entrenadores y expertos deportivos brindadas. ",
  },
  {
    value: "1.4.2.2",
    label: "1.4.2.2 Equipo interdisciplinar para el acompañamiento y asesoramiento a deportistas creado. ",
  },
  {
    value: "1.4.2.3",
    label:
      "1.4.2.3 Fondo económico para apoyo a deportistas que representen al municipio en competencias externas creado. ",
  },
  { value: "1.4.2.4", label: "1.4.2.4 Infraestructura deportiva mantenida y mejorada. " },
  { value: "1.4.2.5", label: "1.4.2.5 Infraestructura deportiva construida. " },
  { value: "1.4.3.1", label: "1.4.3.1 Plan Integral de Educación Física y Deporte creado e implementado. " },
  {
    value: "1.4.3.2",
    label: "1.4.3.2 Sedes Educativas con implementación del Plan Integral de Educación Física y Deporte. ",
  },
  { value: "1.4.3.3", label: "1.4.3.3 Establecimiento del INDER Copacabana. " },
  {
    value: "1.4.3.4",
    label:
      "1.4.3.4 Dotación deportiva entregada para brindar condiciones adecuadas en la realización de la actividad deportiva. ",
  },
  { value: "1.4.3.5", label: "1.4.3.5 Alianzas con diferentes actores realizadas para la gestión deportiva. " },
  { value: "2.1.1.1", label: "2.1.1.1 SILAP formulado con gestión para su implementación. " },
  { value: "2.1.1.2", label: "2.1.1.2 Generación de Espacios Públicos Verdes Urbanos. " },
  {
    value: "2.1.1.3",
    label: "2.1.1.3 Estrategia de reforestación y restauración de áreas de importancia ambiental en el municipio. ",
  },
  { value: "2.1.1.4", label: "2.1.1.4 Área total para la protección y conservación de ecosistemas naturales. " },
  { value: "2.1.2.1", label: "2.1.2.1 Áreas con PSA. " },
  { value: "2.1.2.2", label: "2.1.2.2 Intervenciones en acueductos veredales para su mejora y fortalecimiento. " },
  {
    value: "2.1.2.3",
    label:
      "2.1.2.3 Acciones integrales con capacitaciones, sensibilizaciones, acompañamientos y monitoreos para Protección y Gestión Sostenible del Agua implementadas. ",
  },
  { value: "2.1.2.4", label: "2.1.2.4 Análisis hidrológico de cuencas con enfoque regenerativo. " },
  {
    value: "2.1.2.5",
    label:
      "2.1.2.5 Intervenciones mediante descontaminación en el Sistema de alcantarillado, pozos sépticos y control de descargas directas para su fortalecimiento. ",
  },
  {
    value: "2.1.3.1",
    label:
      "2.1.3.1 Acciones de planificación, gestión, control y monitoreo ambiental en Copacabana implementadas para enfrentar los fenómenos del cambio climático. ",
  },
  { value: "2.1.3.2", label: "2.1.3.2 Diseño de ruta estratégica para adaptación al cambio climático. " },
  {
    value: "2.1.3.3",
    label:
      "2.1.3.3 Diseño e implementación del plan de ahorro y eficiencia energética para la administración pública, y la promoción de energías renovables. ",
  },
  {
    value: "2.1.3.4",
    label:
      "2.1.3.4 Acciones enfocadas en la implementación de medidas de adaptación y resiliencia de la infraestructura. ",
  },
  {
    value: "2.1.4.1",
    label:
      "2.1.4.1 Campañas de capacitación y sensibilización en cambio climático, adaptación y gestión del riesgo. ",
  },
  {
    value: "2.1.4.2",
    label:
      "2.1.4.2 Campañas de capacitación y sensibilización en manejo integral de residuos sólidos y sostenibilidad. ",
  },
  {
    value: "2.1.4.3",
    label:
      "2.1.4.3 Estrategia para el mejoramiento y construcción de espacios para la formación e investigación ambiental. ",
  },
  {
    value: "2.1.4.4",
    label: "2.1.4.4 Acciones enfocadas en impulsar la educación ambiental y la transformación cultural. ",
  },
  { value: "2.1.5.1", label: "2.1.5.1 Plan de Gestión Integral de Residuos actualizado. " },
  {
    value: "2.1.5.2",
    label:
      "2.1.5.2 Acciones implementadas para la formalización de recuperadores y el fortalecimiento de empresas de reciclaje. ",
  },
  { value: "2.1.6.1", label: "2.1.6.1 Acciones con enfoque en economía circular. " },
  { value: "2.1.6.2", label: "2.1.6.2 Acciones enfocadas en acompañamiento, control y vigilancia ambiental. " },
  { value: "2.1.6.3", label: "2.1.6.3 Programa participativo de Sello Ambiental. " },
  {
    value: "2.2.1.1",
    label:
      "2.2.1.1 Política pública de bienestar animal para la protección y promoción del bienestar de los animales diseñada. ",
  },
  { value: "2.2.1.2", label: "2.2.1.2 Creación de Infraestructura y Unidades de Atención y Bienestar Animal. " },
  {
    value: "2.2.1.3",
    label:
      "2.2.1.3 Cátedra de Bienestar animal implementada en las instituciones educativas oficiales del municipio. ",
  },
  {
    value: "2.2.1.4",
    label: "2.2.1.4 Atención integral a animales en condición de vulnerabilidad reportados y/o identificados. ",
  },
  {
    value: "2.2.1.5",
    label: "2.2.1.5 Campañas de salud, bienestar, adopción y sensibilización para Mascotas realizadas. ",
  },
  { value: "2.3.1.1", label: "2.3.1.1 Plan de Gestión del Riesgo Institucional actualizado. " },
  {
    value: "2.3.1.2",
    label:
      "2.3.1.2 Acciones de control y seguimiento necesario a las especies invasoras del municipio, con mapeo de zonas, seguimiento a tendencias, divulgación a la comunidad,  realizadas. ",
  },
  {
    value: "2.3.1.3",
    label:
      "2.3.1.3 Acciones Correctivas y Preventivas, a través de medios comunicacionales, visitas a sitios, capacitaciones y formaciones, realizadas en torno a la gestión integral del riesgo de desastres. ",
  },
  {
    value: "2.3.2.1",
    label:
      "2.3.2.1 Estrategia de Verificación Cumplimiento Normativo y Supervisión de Construcciones diseñada e implementada. ",
  },
  { value: "2.3.3.1", label: "2.3.3.1 Sistemas de Monitoreo y Alerta Temprana implementados. " },
  { value: "2.3.3.2", label: "2.3.3.2 Nueva Sede de Bomberos actualizada y con condiciones adecuadas. " },
  { value: "2.3.3.3", label: "2.3.3.3 Dotación de organismos de socorro y salvaguardia gestionados. " },
  { value: "2.4.1.1", label: "2.4.1.1 Copacabana con Vivienda Digna: Mejoramiento de viviendas realizados. " },
  {
    value: "2.4.1.2",
    label: "2.4.1.2 Copacabana con Vivienda Digna: construcciones de vivienda nueva viabilizadas. ",
  },
  {
    value: "2.4.1.3",
    label:
      "2.4.1.3 Personas beneficiadas con asesoría integral para acceder a beneficios económicos para proyectos de vivienda de interés social (VIS), vivienda de interés prioritario (VIP) y mejoramiento de vivienda. ",
  },
  { value: "2.4.1.4", label: "2.4.1.4 Iniciativas de saneamiento y titulación de bienes fiscales ejecutadas. " },
  {
    value: "2.5.1.1",
    label:
      "2.5.1.1 Acciones de fortalecimiento de la Movilidad en Copacabana, como campañas de sensibilización, asesorías, capacitaciones y acompañamientos, de manera sostenible y con enfoque diferencial implementadas. ",
  },
  {
    value: "2.5.1.2",
    label: "2.5.1.2 Plan integral para el mejoramiento de la movilidad en el municipio de Copacabana diseñado. ",
  },
  { value: "2.5.1.3", label: "2.5.1.3 Parque automotor Público mejorado. " },
  {
    value: "2.5.2.1",
    label: "2.5.2.1 Intervenciones para lograr la mejora y/o ampliación de la infraestructura vial. ",
  },
  { value: "2.5.2.2", label: "2.5.2.2 Mantenimiento de la infraestructura vial. " },
  {
    value: "2.5.3.1",
    label: "2.5.3.1 Estrategia 'Copacabana, hacia una movilidad innovadora e  inteligente' implementada. ",
  },
  { value: "2.5.3.2", label: "2.5.3.2 Acciones de Educación y Cultura Vial implementadas. " },
  { value: "2.6.1.1", label: "2.6.1.1 Plan Básico de Ordenamiento Territorial de Copacabana actualizado. " },
  { value: "2.6.2.1", label: "2.6.2.1 Infraestructura municipal de Copacabana mejorada o mantenida. " },
  { value: "2.6.2.2", label: "2.6.2.2 Sistema de alumbrado público municipal mejorado y ampliado. " },
  {
    value: "2.6.2.3",
    label:
      "2.6.2.3 Estrategia de integración de espacios públicos seguros, estimulantes y adecuados para niños y niñas diseñada y ejecutada. ",
  },
  { value: "2.6.2.4", label: "2.6.2.4 Espacio público de Copacabana mejorado. " },
  { value: "2.6.2.5", label: "2.6.2.5 Gestionar el aumento de cobertura de la red de gas en la zona veredal. " },
  { value: "3.1.1.1", label: "3.1.1.1 Emprendimientos fortalecidos y promovidos. " },
  { value: "3.1.1.2", label: "3.1.1.2 Estrategias de integración y financiación empresarial implementadas. " },
  {
    value: "3.1.1.3",
    label: "3.1.1.3 Capacitaciones realizadas a emprendimientos para la creación y consolidación de empresa. ",
  },
  { value: "3.1.1.4", label: "3.1.1.4 Eventos para el fomento del emprendimiento realizados. " },
  {
    value: "3.1.2.1",
    label:
      "3.1.2.1 Campañas para la estimulación de la creación, formalización y mejoramiento de emprendimientos realizadas. ",
  },
  { value: "3.1.2.2", label: "3.1.2.2 Estrategia 'Emprendimiento para el Futuro' implementada. " },
  {
    value: "3.1.2.3",
    label:
      "3.1.2.3 Acciones de formación para generación de competencias y habilidades (empleos de futuro e industria Tech) realizadas. ",
  },
  { value: "3.1.2.4", label: "3.1.2.4 Convenios público/privados para el fortalecimiento del empleo realizados. " },
  {
    value: "3.1.3.1",
    label: "3.1.3.1 Empresas entorno a la estrategia de 'Semaforización empresarial' clasificadas. ",
  },
  {
    value: "3.1.3.2",
    label: "3.1.3.2 Estrategia de semaforización empresarial para la sostenibilidad implementada. ",
  },
  {
    value: "3.1.3.3",
    label: "3.1.3.3 Incentivos tributarios para empresas que se asienten en el municipio generados. ",
  },
  { value: "3.1.3.4", label: "3.1.3.4 Estrategia 'El alcalde visita tu empresa' implementada. " },
  { value: "3.1.4.1", label: "3.1.4.1 Redes empresariales diseñadas e implementadas. " },
  { value: "3.1.4.2", label: "3.1.4.2 Capacitaciones, asesorías y alianzas con la mesa empresarial realizadas. " },
  {
    value: "3.1.4.3",
    label:
      "3.1.4.3 Red de Negocios Copacabana e integración regional con el distrito de Ciencia, Tecnología e Innovación de Medellín diseñada e implementada. ",
  },
  {
    value: "3.1.5.1",
    label: "3.1.5.1 Acciones de fortalecimiento de la oficina de empleo en Copacabana implementadas. ",
  },
  {
    value: "3.1.5.2",
    label: "3.1.5.2 Acciones para la promoción de empleo de la mano de la oficina pública de empleo implementadas. ",
  },
  { value: "3.2.1.1", label: "3.2.1.1 Plan Agropecuario Municipal (PAM) formulado e implementado. " },
  {
    value: "3.2.1.2",
    label:
      "3.2.1.2 Acciones de fortalecimiento para la producción y comercialización del sector agropecuario, como capacitaciones, acompañamiento, asesorías, entre otras, realizadas. ",
  },
  { value: "3.2.1.3", label: "3.2.1.3 Estrategia de 'eco huertas comunitarias' implementada. " },
  {
    value: "3.2.1.4",
    label:
      "3.2.1.4 Acciones de formación para el desarrollo de capacidades e iniciativas para esquemas asociativos en el sector agropecuario. ",
  },
  { value: "3.2.2.1", label: "3.2.2.1 Estrategia 'Mercado Campesino Sostenible' implementada. " },
  { value: "3.2.2.2", label: "3.2.2.2 Acciones para el desarrollo de cadenas de valor y comercialización. " },
  {
    value: "3.2.2.3",
    label:
      "3.2.2.3 Acciones para que la Plaza de mercado sea un espacio óptimo para la comercialización y el impulso de las cooperativas y/o asociaciones agrícolas y pecuarias mejorada. ",
  },
  { value: "3.3.1.1", label: "3.3.1.1 Política pública de turismo con enfoque sostenible diseñada y expedida. " },
  { value: "3.3.2.1", label: "3.3.2.1 Ecosistema Empresarial Turístico implementado. " },
  {
    value: "3.3.2.2",
    label:
      "3.3.2.2 Acciones de capacitación, acompañamientos, asesorías, divulgación, promoción para implementar el Turismo Comunitario y Cultural en el territorio. ",
  },
  { value: "3.3.2.3", label: "3.3.2.3 Acciones de Endomarketing turístico implementadas. " },
  {
    value: "3.3.2.4",
    label: "3.3.2.4 Asesorías y capacitaciones a los prestadores de servicios turísticos en el municipio. ",
  },
  { value: "3.3.2.5", label: "3.3.2.5 Marca Turística de Copacabana creada. " },
  {
    value: "3.3.2.6",
    label: "3.3.2.6 Estrategia para la promoción de la Marca Municipio de Copacabana implementada. ",
  },
  { value: "3.3.3.1", label: "3.3.3.1 Infraestructura turística mejorada. " },
  {
    value: "3.3.3.2",
    label:
      "3.3.3.2 Estudios de capacidad de carga en los puntos turísticos prioritarios del municipio de Copacabana realizados. ",
  },
  { value: "3.3.3.3", label: "3.3.3.3 Sistema de información turística implementado. " },
  { value: "3.4.1.1", label: "3.4.1.1 Oficina de cooperación internacional, innovación y desarrollo creada. " },
  {
    value: "3.4.1.2",
    label: "3.4.1.2 Reemplazo de fuentes de suministro de energía eléctrica para edificaciones públicas. ",
  },
  {
    value: "3.4.2.1",
    label:
      "3.4.2.1 Acciones de formación en capacidades digitales y la adopción de nuevas tecnologías 4.0 realizadas. ",
  },
  {
    value: "3.4.3.1",
    label:
      "3.4.3.1 Acciones ligadas a la cualificación, actualización digital y dotación tecnológica para la gestión de mejora de la infraestructura tecnológica realizadas. ",
  },
  { value: "3.4.4.1", label: "3.4.4.1 Estrategia Copacabana ciudad inteligente diseñada. " },
  {
    value: "3.4.4.2",
    label:
      "3.4.4.2 Acciones para el acceso y uso de las Tecnologías de la Información y las Comunicaciones implementadas en el marco de Ciudad Inteligente. ",
  },
  {
    value: "4.1.1.1",
    label:
      "4.1.1.1 Estrategia para desarrollo integral de la primera infancia implementada: acceso a los servicios básicos, acompañamiento psicosocial y protección de derechos. ",
  },
  { value: "4.1.1.2", label: "4.1.1.2 Estrategia para la atención integral a madres gestantes implementadas. " },
  {
    value: "4.1.1.3",
    label: "4.1.1.3 Plan Municipal de Prevención Integral de abuso sexual infantil implementada. ",
  },
  {
    value: "4.1.1.4",
    label:
      "4.1.1.4 Acciones de seguimiento, control y mejoras para la seguridad nutricional y alimentaria implementadas. ",
  },
  { value: "4.1.1.5", label: "4.1.1.5 Estrategia de entornos protectores y seguros implementada. " },
  { value: "4.1.2.1", label: "4.1.2.1 Infraestructura mejorada. " },
  {
    value: "4.2.1.1",
    label: "4.2.1.1 Estrategia para el desarrollo integral de la niñez y adolescencia implementada. ",
  },
  {
    value: "4.3.1.1",
    label:
      "4.3.1.1 Adultos mayores beneficiados con acciones de bienestar, fortalecimiento y protección integral implementadas. ",
  },
  {
    value: "4.3.1.2",
    label:
      "4.3.1.2 Política pública de envejecimiento humano y vejez, que incluya los programas de 'Resignificación y protección del adulto mayor' para entornos adecuados y 'Dignidad en la veje' que incluya actividades lúdicas, recreativas, culturales y deportivas actualizada. ",
  },
  { value: "4.3.1.3", label: "4.3.1.3 Acciones de promoción del subsidio de adulto mayor realizadas. " },
  {
    value: "4.3.1.4",
    label: "4.3.1.4 Viabilización de la Construcción y dotación del Centro del Adulto Mayor gestionado. ",
  },
  {
    value: "4.3.1.5",
    label:
      "4.3.1.5 Estrategia de estímulos, incentivos y reconocimientos a los adultos mayores reconociendo su labor y papel en la comunidad realizada. ",
  },
  { value: "4.3.1.6", label: "4.3.1.6 Festival recreativo, cultural, deportivo del adulto mayor realizado. " },
  { value: "4.4.1.1", label: "4.4.1.1 Política pública de juventud actualizada. " },
  { value: "4.4.1.2", label: "4.4.1.2 Estrategia 'STEAM Zone' para formación especializada en STEAM implementada. " },
  {
    value: "4.4.1.3",
    label:
      "4.4.1.3 Programa de acompañamiento integral a jóvenes (emocional, social, económico) creado e implementado. ",
  },
  {
    value: "4.4.1.4",
    label:
      "4.4.1.4 Acciones de seguimiento, formación, acompañamiento y asesoramiento dirigidas al fortalecimiento del Consejo municipal de Juventud y de la plataforma municipal de juventud desarrolladas. ",
  },
  {
    value: "4.4.1.5",
    label:
      "4.4.1.5 Capacitaciones y asesorías a personeros estudiantiles para fortalecer sus labores de líderes y gestores ante el municipio realizadas. ",
  },
  { value: "4.4.1.6", label: "4.4.1.6 Casa de la juventud planeada y creada. " },
  {
    value: "4.4.1.7",
    label:
      "4.4.1.7 Acciones de formación y/o capacitación juvenil para el fortalecimiento de los jóvenes emprendedores del municipio desarrolladas. ",
  },
  {
    value: "4.4.1.8",
    label:
      "4.4.1.8 Convenios público-privados para generar programas de acompañamiento y fortalecimiento de actividades no convencionales y educación no formal para los jóvenes, en el marco del programa 'Más Oportunidades para Nuestros Jóvenes'  realizados. ",
  },
  { value: "4.4.1.9", label: "4.4.1.9 Estrategia 'jóvenes talento' implementada. " },
  {
    value: "4.5.1.1",
    label:
      "4.5.1.1 Acciones de acompañamiento, formación y creación de alianzas para empoderamiento y autonomía femenina implementadas. ",
  },
  { value: "4.5.1.2", label: "4.5.1.2 Estrategia integral 'Mujeres Seguras y Libres de Violencia' implementada. " },
  {
    value: "4.5.1.3",
    label: "4.5.1.3 'La casa morada': un lugar de encuentro para las mujeres y con atención priorizada creada. ",
  },
  {
    value: "4.5.1.4",
    label:
      "4.5.1.4 Programa integral de protección de víctimas de la violencia, líderes sociales y madres cabeza de familia. ",
  },
  { value: "4.5.1.5", label: "4.5.1.5 Red de apoyo de las madres cabeza de hogar creada e implementada. " },
  {
    value: "4.5.1.6",
    label:
      "4.5.1.6 Estrategia integral de promoción y prevención en salud especializada en enfermedades de las mujeres implementada. ",
  },
  {
    value: "4.5.1.7",
    label: "4.5.1.7 Estrategia de creación de escuela de liderazgo Mujeres Líderes y Participativas. ",
  },
  { value: "4.5.1.8", label: "4.5.1.8 Política pública de la mujer actualizada. " },
  { value: "4.5.1.9", label: "4.5.1.9 Dirección municipal de las mujeres en Copacabana creada. " },
  {
    value: "4.6.1.1",
    label:
      "4.6.1.1  Acciones de fortalecimiento integral a través de acompañamiento, asesorías, alianzas estratégicas y capacitaciones de la población LGTIQA+ implementadas. ",
  },
  { value: "4.6.1.2", label: "4.6.1.2 Política pública municipal de diversidad diseñada e implementada. " },
  {
    value: "4.6.1.3",
    label:
      "4.6.1.3 Programa 'Desde mi realidad' como estrategia de comunicación multimodal para la población LGBTIQA+ implementada. ",
  },
  { value: "4.6.1.4", label: "4.6.1.4 Mesa diversa fortalecida. " },
  {
    value: "4.6.1.5",
    label: "4.6.1.5 'Casa de Colores': Espacio de Encuentro y con atención integral para la diversidad adecuada. ",
  },
  {
    value: "4.6.1.6",
    label: "4.6.1.6 Acciones de Educación Inclusiva, laboral y Sensibilización Comunitaria implementadas. ",
  },
  { value: "4.7.1.1", label: "4.7.1.1 Centro de Atención Integral para la Discapacidad (CAID) creado. " },
  { value: "4.7.1.2", label: "4.7.1.2 Capacitaciones a los cuidadores de personas con Discapacidad realizadas. " },
  {
    value: "4.7.1.3",
    label:
      "4.7.1.3 Acciones para empresas, emprendimiento y comercios con capacitaciones, divulgación, concientización, entre otras, para Inclusión Laboral para Personas con Discapacidad implementadas. ",
  },
  { value: "4.7.1.4", label: "4.7.1.4 Plan ocupacional para personas con discapacidad diseñado. " },
  { value: "4.7.1.5", label: "4.7.1.5 Acciones para la Accesibilidad y Movilidad Urbana implementadas. " },
  {
    value: "4.7.1.6",
    label:
      "4.7.1.6 Encuentros de bienestar emocional, estimulación, creatividad y expresión para las personas con discapacidad. ",
  },
  {
    value: "4.7.1.7",
    label:
      "4.7.1.7 Acciones de Empoderamiento y Participación Comunitaria de personas con discapacidad implementadas. ",
  },
  { value: "4.8.1.1", label: "4.8.1.1 Estrategia de Atención psicosocial y resocialización implementada. " },
  {
    value: "4.8.1.2",
    label:
      "4.8.1.2 Acciones de acompañamiento, formación y campañas de divulgación para promover programas de desintoxicación implementadas. ",
  },
  { value: "4.8.1.3", label: "4.8.1.3 Política pública para personas en situación de calle diseñada y expedida. " },
  {
    value: "4.9.1.1",
    label:
      "4.9.1.1 Espacios de participación liderados por el sector religioso, para la garantía del derecho con plan de acción implementado. ",
  },
  {
    value: "4.9.1.2",
    label: "4.9.1.2 Organizaciones basadas en la fe con proyectos de cooperación social y comunitarios formulados. ",
  },
  {
    value: "4.9.1.3",
    label:
      "4.9.1.3 Acciones que promuevan un entorno protector alrededor de los lugares de culto, dentro del Plan Integral de Seguridad y Convivencia Ciudadana (PISCC). ",
  },
  {
    value: "4.9.1.4",
    label: "4.9.1.4 Capacitaciones para la promoción y protección del derecho a la libertad religiosa. ",
  },
  {
    value: "4.9.1.5",
    label: "4.9.1.5 Política pública de libertad religiosa y de cultos formulada e implementada. ",
  },
  { value: "4.10.1.1", label: "4.10.1.1 Ruta Integral de Acompañamiento a Víctimas (RIAV) actualizada. " },
  {
    value: "4.10.1.2",
    label:
      "4.10.1.2 Estrategia de articulación a los programas de apoyo y atención del gobierno nacional y departamental con las víctimas de Copacabana implementada. ",
  },
  { value: "4.10.1.3", label: "4.10.1.3 Oficina de Atención a Víctimas fortalecida. " },
  {
    value: "4.10.1.4",
    label:
      "4.10.1.4 Acciones de promoción del reconocimiento y respaldo a las iniciativas de las personas y organizaciones de víctimas en el marco del proceso de reparación integral y la búsqueda de reconciliación realizadas. ",
  },
  {
    value: "4.10.1.5",
    label:
      "4.10.1.5 Acciones de Empoderamiento y Participación de las Víctimas en la Construcción de Paz implementadas. ",
  },
  { value: "4.10.1.6", label: "4.10.1.6 Política pública de víctimas implementada. " },
  {
    value: "4.11.1.1",
    label:
      "4.11.1.1 Acciones preventivas y operativas que aporten a la seguridad integral, desde la formación ciudadana, integración de la policía cívica, divulgación y promoción de seguridad, tomas de espacios, entre otros. ",
  },
  {
    value: "4.11.1.2",
    label: "4.11.1.2 Capacitaciones a la comunidad sobre autocuidado con perspectiva de prevención del delito. ",
  },
  {
    value: "4.11.1.3",
    label: "4.11.1.3 Acciones que garanticen entornos protectores y seguros para niños, niñas y adolescentes. ",
  },
  { value: "4.11.1.4", label: "4.11.1.4 Adecuación y dotación de las comisarías de familia. " },
  { value: "4.11.1.5", label: "4.11.1.5 Capacitación y asesorías técnicas a las comisarías de familias. " },
  { value: "4.11.1.6", label: "4.11.1.6 Consejos de seguridad descentralizados realizados. " },
  {
    value: "4.11.1.7",
    label: "4.11.1.7 Convenio estratégico con el transporte público para la seguridad gestionado. ",
  },
  {
    value: "4.11.1.8",
    label:
      "4.11.1.8 Convenios estratégicos con la policía nacional de carabineros y el ejército nacional gestionados. ",
  },
  {
    value: "4.11.1.9",
    label: "4.11.1.9 Planes Integrales de Seguridad y Convivencia Ciudadana -PISCC diseñados e implementados. ",
  },
  { value: "4.11.1.10", label: "4.11.1.10 CAI Móviles viabilizados. " },
  {
    value: "4.11.1.11",
    label:
      "4.11.1.11 Desarrollo de estrategia de seguridad cercana a las comunidades a través de recorridos en el municipio. ",
  },
  { value: "4.11.1.12", label: "4.11.1.12 Estrategia de reacción inmediata con la Policía Nacional implementada. " },
  {
    value: "4.11.1.13",
    label: "4.11.1.13 Articulación con los entes territoriales de las estrategias de seguridad realizada. ",
  },
  { value: "4.11.1.14", label: "4.11.1.14 Nueva estación de policía con condiciones adecuadas viabilizada. " },
  { value: "4.11.1.15", label: "4.11.1.15 Frentes de seguridad constituidos. " },
  { value: "4.11.2.1", label: "4.11.2.1 Jornadas de educación, convivencia y seguridad realizadas. " },
  {
    value: "4.11.2.2",
    label:
      "4.11.2.2 Acciones de Reconstrucción del Tejido Social a través del Arte y el Deporte para la convivencia implementadas. ",
  },
  {
    value: "4.11.2.3",
    label:
      "4.11.2.3 Estrategia de apropiación de espacios con programas de inserción cultural para la sana convivencia. ",
  },
  { value: "4.11.2.4", label: "4.11.2.4 Talleres juveniles con gestión comunitaria de la policía. " },
  { value: "4.11.3.1", label: "4.11.3.1 Seguridad informática y protección de la información pública fortalecida. " },
  { value: "4.11.3.2", label: "4.11.3.2 Estrategia de integración tecnológica para la seguridad implementada. " },
  { value: "4.11.3.3", label: "4.11.3.3 Centro de comunicaciones, comando y control modernizado. " },
  {
    value: "5.1.1.1",
    label:
      "5.1.1.1 Estrategia de fortalecimiento de la Capacidad Analítica y Gestión de la Información Financiera realizada. ",
  },
  {
    value: "5.1.1.2",
    label:
      "5.1.1.2 Estrategia para la generación de ingresos municipales y la gestión de recursos territoriales implementada. ",
  },
  { value: "5.1.2.1", label: "5.1.2.1 Acciones de promoción y fortalecimiento de los mecanismos de denuncia. " },
  {
    value: "5.1.2.2",
    label: "5.1.2.2 Estrategia para la prevención del conflicto de interés creada e implementada. ",
  },
  { value: "5.1.2.3", label: "5.1.2.3 Código de integridad para los funcionarios fortalecido. " },
  {
    value: "5.1.2.4",
    label:
      "5.1.2.4 Estrategia para el fortalecimiento de la gestión pública del municipio de Copacabana implementada. ",
  },
  {
    value: "5.1.2.5",
    label:
      "5.1.2.5 Acciones de fortalecimiento de las organizaciones de la sociedad civil implementadas, a través, de acompañamiento, formación y divulgación. ",
  },
  { value: "5.1.2.6", label: "5.1.2.6 Oficina de atención al ciudadano fortalecida. " },
  { value: "5.2.1.1", label: "5.2.1.1 Mecanismo de participación ciudadana y comunal creado e implementado. " },
  {
    value: "5.2.1.2",
    label:
      "5.2.1.2 Programa de incentivos de las buenas prácticas, gestiones y acciones de los organismos comunales creados. ",
  },
  { value: "5.2.1.3", label: "5.2.1.3 Formaciones en Auditoría Ciudadana y Control Social realizadas. " },
  {
    value: "5.2.1.4",
    label:
      "5.2.1.4 Estrategia con las Juntas de Acción Comunal para la ejecución de obras según el marco legal que lo rija creada e implementada. ",
  },
  { value: "5.2.1.5", label: "5.2.1.5 Canales de comunicación directa con la comunidad creados y fortalecidos. " },
  {
    value: "5.2.1.6",
    label: "5.2.1.6 NPS implementado adecuadamente para el mejoramiento de la atención al ciudadano. ",
  },
  { value: "5.2.1.7", label: "5.2.1.7 Dirección de Participación Ciudadana creada. " },
  {
    value: "5.3.1.1",
    label:
      "5.3.1.1 Acciones de fortalecimiento del bienestar integral del personal articulada a la gestión pública implementadas. ",
  },
  { value: "5.3.1.2", label: "5.3.1.2 Calificaciones y capacitaciones a los empleados públicos realizadas. " },
  {
    value: "5.3.1.3",
    label: "5.3.1.3 Programa de incentivos para los empleados públicos sobresalientes creado e implementado. ",
  },
  { value: "5.3.1.4", label: "5.3.1.4 Actualización de los procesos de la administración municipal realizada. " },
  {
    value: "5.3.1.5",
    label: "5.3.1.5 Programa de inducción y reinducción de los empleados públicos fortalecido y actualizado. ",
  },
  {
    value: "5.3.1.6",
    label:
      "5.3.1.6 Creación del programa 'Somos equipo' como espacios de integración, formación y fortalecimiento del bienestar integral. ",
  },
  {
    value: "5.3.1.7",
    label: "5.3.1.7 Estrategia de modernización y articulación de la gestión pública implementada. ",
  },
  { value: "5.3.1.8", label: "5.3.1.8 Capacitaciones a funcionarios en habilidades digitales realizadas. " },
  { value: "5.3.1.9", label: "5.3.1.9 Espacios físicos de la administración municipal mejorados. " },
  {
    value: "5.3.1.10",
    label:
      "5.3.1.10 Estrategia de actualización del equipo tecnológico de la administración municipal realizada e implementada. ",
  },
  { value: "5.3.1.11", label: "5.3.1.11 Plan de comunicación implementado, adaptado y socializado. " },
  { value: "5.3.1.12", label: "5.3.1.12 Rendición de cuentas planeado y ejecutado. " },
  { value: "5.3.1.13", label: "5.3.1.13 Creación y puesta en marcha de la mesa de Comunicaciones. " },
]

const opcionesBPIN = [
  { value: "", label: "Seleccionar..." },
  {
    value: "2018051290002",
    label: "2018051290002 Formación a las mujeres emprendedoras y empresarias para la paz y el progreso en el municipio de Caldas, Antioquia",
  },
  {
    value: "2018051290006",
    label: "2018051290006 Formulación del Plan Estratégico de Turismo en el municipio de Caldas, Antioquia",
  },
  {
    value: "2018051290008",
    label: "2018051290008 Elaboración y actualización de los instrumentos de control archivístico del municipio de Caldas, Antioquia",
  },
  {
    value: "2018051290005",
    label: "2018051290005 Construcción, mantenimiento y adecuación de Placas, centros deportivos y espacio público (Por compensaciones urbanísticas) en el municipio de Caldas, Antioquia",
  },
  {
    value: "2018051290003",
    label: "2018051290003 Construcción de obras de contención en la vía vereda sinifana en el municipio de Caldas, Antioquia",
  },
  {
    value: "2018051290007",
    label: "2018051290007 Fortalecimiento integral al funcionamiento de la Secretaría de Salud del Municipio de Caldas, Antioquia",
  },
]

const options = [
  { value: "", label: "Seleccionar..." },
  { value: "01001", label: "01001 CONCEJO" },
  { value: "02001", label: "02001 PERSONERIA" },
  { value: "03001", label: "03001 JURIDICA" },
  { value: "04001", label: "04001 ALCALDÍA" },
  { value: "05001", label: "05001 EDUCACIÓN" },
  { value: "05002", label: "05002 BIBLIOTECA MUNICIPAL" },
  { value: "06001", label: "06001 GOBIERNO" },
  { value: "06002", label: "06002 INSPECCIÓN 1ra POLICIA" },
  { value: "06006", label: "06006 INSPECCIÓN 2da DE POLICIA" },
  { value: "06007", label: "06007 INSPECCIÓN 3ra INFRACCIONES URBANISTICAS" },
  { value: "06008", label: "06008 INSPECCIÓN 4ta AFECTACIONES AMBIENTALES" },
  { value: "06003", label: "06003 COMISARIA DE FAMILIA NORTE" },
  { value: "06009", label: "06009 COMISARIA DE FAMILIA SUR" },
  { value: "06004", label: "06004 FONSET" },
  { value: "06005", label: "06005 GESTIÓN DEL RIESGO" },
  { value: "07001", label: "07001 DESARROLLO Y BIENESTAR SOCIAL" },
  { value: "07002", label: "07002 VICTIMAS DE CONFLICTO" },
  { value: "07003", label: "07003 TURISMO" },
  { value: "07004", label: "07004 JUVENTUD " },
  { value: "07005", label: "07005 HABITANTE DE CALLE" },
  { value: "08001", label: "08001 PLANEACIÓN" },
  { value: "08002", label: "08002 SISBEN" },
  { value: "08003", label: "08003 CALIDAD" },
  { value: "09001", label: "09001 INFRAESTRUCTURA" },
  { value: "10001", label: "10001 HACIENDA" },
  { value: "10002", label: "10002 CATASTRO" },
  { value: "11001", label: "11001 SERVICIOS ADMINISTRATIVOS" },
  { value: "11002", label: "11002 PERSONAL" },
  { value: "11003", label: "11003 ALMACEN" },
  { value: "11004", label: "11004 ARCHIVO" },
  { value: "12001", label: "12001 SALUD" },
  { value: "12002", label: "12002 ADULTO MAYOR" },
  { value: "12003", label: "12003 FONDO LOCAL DE SALUD" },
  { value: "12004", label: "12004 PLAN ALIMENTARIO ESCOLAR" },
  { value: "14001", label: "14001 CULTURA" },
  { value: "15001", label: "15001 MOVILIDAD" },
  { value: "16001", label: "16001 INVICOP" },
  { value: "17001", label: "17001 JUNTA DE DEPORTES" },
  { value: "18001", label: "18001 AGRICULTURA Y MEDIO AMBIENTE" },
  { value: "18002", label: "18002 ALBERGUE" },
  { value: "20001", label: "20001 CONTROL INTERNO" },
  { value: "21001", label: "21001 COMUNICACIONES" },
  { value: "22001", label: "22001 CONTROL DISCIPLINARIO" },
  { value: "23001", label: "23001 DIRECCIÓN FINANCIERA" },
  { value: "23002", label: "23002 COBRO COACTIVO" }
];


const opcionesRubros = {
  "1621115": {
    "nombreRubro": "Cesantias",
    "rubro": " 2.1.1.01.02.003.16.1621115.11001"
  },
  "1621136": {
    "nombreRubro": "Honorarios profesionales - servicios personales indirectos",
    "rubro": "2.1.2.02.02.008.16.1621136.11001"
  },
  "1621134": {
    "nombreRubro": "Seguros y polizas",
    "rubro": "2.1.2.02.02.007.16.1621134.11001"
  },
  "1623101": {
    "nombreRubro": "Derechos de Autor y Licencias - SGP Proposito General Libre Inversion",
    "rubro": "2.3.2.01.01.005.02.05.16.1623101.14501"
  },
  "1623102": {
    "nombreRubro": "Fortalecimiento de la gestión pública - SGP Proposito General Libre Inversion",
    "rubro": "2.3.2.01.01.003.03.01.16.1623102.14501"
  },
  "1623238": {
    "nombreRubro": "Feria del libro Mincultura - Nacion",
    "rubro": "2.3.2.02.02.009.16.1623238.31404 "
  },

  "1623103": {
    "nombreRubro": "Equipos de Comunicaciones- SGP Proposito General Libre Inversion",
    "rubro": "2.3.2.01.01.003.05.02.16.1623103.14501"
  },
  "1623104": {
    "nombreRubro": "Fortalecimiento de la Movilidad Sostenible Metro de Medellín - Sobretasa a la gasolina SSF",
    "rubro": "2.3.2.01.01.003.07.05.16.1623104.11001"
  },
  "1623105": {
    "nombreRubro": "Áreas conservadas para la protección de los recursos naturales- ICLD Ley 99/93",
    "rubro": "2.3.2.01.03.001.16.1623105.11001"
  },
  "1623106": {
    "nombreRubro": "Fortalecimiento del desarrollo cultural municipal - ICLD",
    "rubro": "2.3.2.02.02.005.16.1623106.11001"
  },
  "1623107": {
    "nombreRubro": "Gestión Sostenible del Agua - SGP Agua potable y saneamiento basico",
    "rubro": "2.3.2.02.02.005.16.1623107.14201"
  },
  "1623108": {
    "nombreRubro": "Gestión Sostenible del Agua - Transferencias del Sector Electrico",
    "rubro": "2.3.2.02.02.005.16.1623108.61001"
  },
  "1623109": {
    "nombreRubro": "Infraestructura Gestión del Riesgo - ICLD",
    "rubro": "2.3.2.02.02.005.16.1623109.11001"
  },
  "1623110": {
    "nombreRubro": "Infraestructura Vial - Impuesto Telefónico",
    "rubro": "2.3.2.02.02.005.16.1623110.12009"
  },
  "1623111": {
    "nombreRubro": "Infraestructura territorial - ICLD",
    "rubro": "2.3.2.02.02.005.16.1623111.11001"
  },
  "1623112": {
    "nombreRubro": "Infraestructura territorial  - Alumbrado Público",
    "rubro": "2.3.2.02.02.005.16.1623112.12002"
  },
  "1623113": {
    "nombreRubro": "Infraestructura espacio público - Cesiones Urbanisticas",
    "rubro": "2.3.2.02.02.005.16.1623113.12011"
  },
  "1623114": {
    "nombreRubro": "Equipamento - Cesiones Urbanisticas",
    "rubro": "2.3.2.02.02.005.16.1623114.12011"
  },
  "1623115": {
    "nombreRubro": "Infraestructura territorial  -SGP Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.005.16.1623115.14501"
  },
  "1623116": {
    "nombreRubro": "Servicio de educación informal- SGP Educacion Calidad - Servicios Publicos Instituciones Educativas",
    "rubro": "2.3.2.02.02.006.16.1623116.14001"
  },
  "1623117": {
    "nombreRubro": "Fortalecimiento Educativo - ICLD- Servicios Publicos Instituciones Educativas",
    "rubro": "2.3.2.02.02.006.16.1623117.11001"
  },
  "1623118": {
    "nombreRubro": "Servicios Publicos Establecimientos Deportivos - SGP  Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.006.16.1623118.14501"
  },
  "1623119": {
    "nombreRubro": "Transporte como componente en proyectos de inversión - ICLD",
    "rubro": "2.3.2.02.02.006.16.1623119.11001"
  },
  "1623120": {
    "nombreRubro": "Fortalecimiento del Sector Turístico - ICLD",
    "rubro": "2.3.2.02.02.008.16.1623120.11001"
  },
  "1623121": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 3.4.2 - ICLD",
    "rubro": "2.3.2.02.02.008.16.1623121.11001"
  },
  "1623122": {
    "nombreRubro": "Gobierno Digital - SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.008.16.1623122.14501"
  },
  "1623123": {
    "nombreRubro": "Apoyo técnico y profesional a la Dirección de Planeación SISBEN - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623123.11001"
  },
  "1623124": {
    "nombreRubro": "Mejoramiento de la calidad de la educación para el trabajo y el desarrollo humano- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623124.11001"
  },
  "1623125": {
    "nombreRubro": "Fortalecimiento integral, pedagógico y educativo - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623125.11001"
  },
  "1623126": {
    "nombreRubro": "Fortalecimiento educativo - SGP - Educacion Gratuidad",
    "rubro": "2.3.2.02.02.009.16.1623126.14002"
  },
  "1623127": {
    "nombreRubro": "Acceso y permanencia en sistema educativo- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623127.11001"
  },
  "1623128": {
    "nombreRubro": "Fortalecimiento y encadenamineto de los diferentes niveles educativos - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623128.11001"
  },
  "1623129": {
    "nombreRubro": "Educación  artística y cultural- SGP - Proposito General Cultura Once Doceavas",
    "rubro": "2.3.2.02.02.009.16.1623129.14401"
  },
  "1623130": {
    "nombreRubro": "Educación  artística y cultural- SGP  Proposito General Cultura Ultima Doceava",
    "rubro": "2.3.2.02.02.009.16.1623130.14401"
  },
  "1623131": {
    "nombreRubro": "Educación  artística y cultural- SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623131.14501"
  },
  "1623132": {
    "nombreRubro": "Gestión, protección y salvaguardia del patrimonio cultural- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623132.11001"
  },
  "1623133": {
    "nombreRubro": "Mejoramiento y dotación de la infraestructura cultural - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623133.11001"
  },
  "1623134": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 1.3.4 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623134.11001"
  },
  "1623135": {
    "nombreRubro": "Fortalecimiento de la cultura deportiva - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623135.11001"
  },
  "1623136": {
    "nombreRubro": "Fortalecimiento de la cultura deportiva - Tasa prodeporte y recreación 1,5%",
    "rubro": "2.3.2.02.02.009.16.1623136.13006"
  },
  "1623137": {
    "nombreRubro": "Fortalecimiento de la cultura deportiva - SGP  Proposito General Deportes Once Doceavas",
    "rubro": "2.3.2.02.02.009.16.1623137.14301"
  },
  "1623138": {
    "nombreRubro": "Fortalecimiento de la cultura deportiva - SGP  Proposito General Deportes Última Doceava",
    "rubro": "2.3.2.02.02.009.16.1623138.14301"
  },
  "1623139": {
    "nombreRubro": "Fortalecimiento de la cultura deportiva - SGP  Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623139.14501"
  },
  "1623140": {
    "nombreRubro": "Fortalecimiento deportivo - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623140.11001"
  },
  "1623141": {
    "nombreRubro": "Liderazgo en gobernanza para la gestión deportiva - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623141.11001"
  },
  "1623142": {
    "nombreRubro": "Conservación de la biodiversidad y sus servicios ecosistemicos- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623142.11001"
  },
  "1623143": {
    "nombreRubro": "Pretección del recurso hídrico - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623143.11001"
  },
  "1623144": {
    "nombreRubro": "Adaptación al cambio climatico - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623144.11001"
  },
  "1623145": {
    "nombreRubro": "Educación y cultura ambiental territorializada - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623145.11001"
  },
  "1623146": {
    "nombreRubro": "Gestión de residuos y fortalecimiento ambiental- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623146.11001"
  },
  "1623147": {
    "nombreRubro": "Incentivo al aprovechamiento y tratamiento de residuos sólidos IAT - Otras Entidades",
    "rubro": "2.3.2.02.02.009.16.1623147.31402"
  },
  "1623148": {
    "nombreRubro": "Producción verde y responsable - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623148.11001"
  },
  "1623149": {
    "nombreRubro": "Bienestar animal y tenencia responsable - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623149.11001"
  },
  "1623150": {
    "nombreRubro": "Plan municipal de gestion del riesgo y de desastres- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623150.11001"
  },
  "1623151": {
    "nombreRubro": "Gestión del Riesgo - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623151.11001"
  },
  "1623152": {
    "nombreRubro": "Planificación e implementación técnica y normativa para el control del riesgo - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623152.11001"
  },
  "1623153": {
    "nombreRubro": "Vivienda digna para sus habitantes y servicios públicos- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623153.11001"
  },
  "1623154": {
    "nombreRubro": "Movilidad territorial sostenible - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623154.11001"
  },
  "1623155": {
    "nombreRubro": "Movilidad territorial sostenible - Multas de transito",
    "rubro": "2.3.2.02.02.009.16.1623155.12003"
  },
  "1623156": {
    "nombreRubro": "Movilidad innovadora e  inteligente - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623156.11001"
  },
  "1623157": {
    "nombreRubro": "Plan basico de ordenamiento territorial- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623157.11001"
  },
  "1623158": {
    "nombreRubro": "Emprendimiento SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623158.14501"
  },
  "1623159": {
    "nombreRubro": "Servicio de orientación laboral- SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623159.14501"
  },
  "1623160": {
    "nombreRubro": "Fortalecimiento empresarial SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623160.14501"
  },
  "1623161": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 3.1.4 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623161.11001"
  },
  "1623162": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 3.1.5 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623162.11001"
  },
  "1623163": {
    "nombreRubro": "Desarrollo agropecuario sostenible- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623163.11001"
  },
  "1623164": {
    "nombreRubro": "Desarrollo de cadenas de valor y promoción de productos locales - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623164.11001"
  },
  "1623165": {
    "nombreRubro": "Desarrollo Turistico Sostenible SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623165.14501"
  },
  "1623166": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 3.3.3 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623166.11001"
  },
  "1623167": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 3.4.1- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623167.11001"
  },
  "1623168": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 3.4.3 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623168.11001"
  },
  "1623169": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 3.4.4- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623169.11001"
  },
  "1623170": {
    "nombreRubro": "Desarrollo integral de la primera infancia - SGP Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623170.14501"
  },
  "1623171": {
    "nombreRubro": "Programa de Alimentación Escolar PAE  - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623171.11001"
  },
  "1623172": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 4.1.2 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623172.11001"
  },
  "1623173": {
    "nombreRubro": "Ayudas Humanitarias SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623173.14501"
  },
  "1623174": {
    "nombreRubro": "Fortalecimiento integral de las víctimas del conflicto - SGP  Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623174.14501"
  },
  "1623175": {
    "nombreRubro": "Comisarías de familia - Estampilla para la justicia familiar 2%",
    "rubro": "2.3.2.02.02.009.16.1623175.13005"
  },
  "1623176": {
    "nombreRubro": "Programa integral de seguridad - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623176.11001"
  },
  "1623177": {
    "nombreRubro": "Programa integral de seguridad - FONSET",
    "rubro": "2.3.2.02.02.009.16.1623177.21401"
  },
  "1623178": {
    "nombreRubro": "Multas y sanciones codigo de policia",
    "rubro": "2.3.2.02.02.009.16.1623178.12006"
  },
  "1623179": {
    "nombreRubro": "Fortalecimiento de los espacios para convivencia ciudadana - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623179.11001"
  },
  "1623180": {
    "nombreRubro": "Fortalecimiento FONSET",
    "rubro": "2.3.2.02.02.009.16.1623180.21401"
  },
  "1623181": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 4.2.1 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623181.11001"
  },
  "1623185": {
    "nombreRubro": "Fortalecimiento Juventud SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623185.14501"
  },
  "1623186": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 4.5.1 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623186.11001"
  },
  "1623187": {
    "nombreRubro": "Desarrollo integral de las mujeres . - SGP  Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623187.14501"
  },
  "1623188": {
    "nombreRubro": "Fortalecimiento Poblacion LGBTIQA+ SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623188.14501"
  },
  "1623189": {
    "nombreRubro": "Personas con discapacidad atendidas con servicios integrales - SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623189.14501"
  },
  "1623190": {
    "nombreRubro": "Fortalecimiento y atención   integral de de la población habitante de calle  - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623190.11001"
  },
  "1623191": {
    "nombreRubro": "Fortalecimiento Poblacion Habitante de Calle SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623191.14501"
  },
  "1623192": {
    "nombreRubro": "Culto Religioso- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623192.11001"
  },
  "1623193": {
    "nombreRubro": "Innovación Fiscal y Financiera- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623193.11001"
  },
  "1623194": {
    "nombreRubro": "Apoyo técnico y profesional a la Dirección de Planeación - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623194.11001"
  },
  "1623195": {
    "nombreRubro": "Sistema de Gestión de Calidad- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623195.11001"
  },
  "1623196": {
    "nombreRubro": "Reordenamiento administrativo- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623196.11001"
  },
  "1623236": {
    "nombreRubro": "Reordenamiento administrativo- SGP - Proposito General Libre Inversion.",
    "rubro": "2.3.2.02.02.009.1.16.1623236.14501"
  },
  "1623197": {
    "nombreRubro": "Modernizacion institucional  -SGP Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623197.14501"
  },
  "1623198": {
    "nombreRubro": "Promoción a la participación ciudadana- SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623198.14501"
  },
  "1623199": {
    "nombreRubro": "Estrategias de comunicacion, de informacion y relaciones publicas - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623199.11001"
  },
  "1623200": {
    "nombreRubro": "Fortalecimiento de la gestión pública - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623200.11001"
  },
  "1623201": {
    "nombreRubro": "Aportes Fondo de Solidaridad y Redistribucion del ingreso - Subsidios Acueducto",
    "rubro": "2.3.3.01.02.004.01.16.1623201.21201"
  },
  "1623202": {
    "nombreRubro": "Subsidios acueducto y alcantarillado- SGP Agua potable y saneamiento basico",
    "rubro": "2.3.3.01.02.004.01.16.1623202.14201"
  },
  "1623203": {
    "nombreRubro": "Subsidios aseo -  SGP Agua potable y saneamiento basico",
    "rubro": "2.3.3.01.02.004.03.16.1623203.14201"
  },
  "1623204": {
    "nombreRubro": "Aportes Fondo de Solidaridad y Redistribucion del ingreso - Subsidios Aseo",
    "rubro": "2.3.3.01.02.004.03.16.1623204.21201"
  },
  "1623205": {
    "nombreRubro": "Plan Departamental de Agua -SGP Agua potable y saneamiento basico",
    "rubro": "2.3.3.01.02.004.03.16.1623205.14201"
  },
  "1623207": {
    "nombreRubro": "Programa de Alimentación Escolar PAE - SGP Alimentacion Escolar",
    "rubro": "2.3.3.05.07.013.16.1623207.14101"
  },
  "1623208": {
    "nombreRubro": "Programa de Alimentación Escolar PAE  - Departamento",
    "rubro": "2.3.3.05.07.013.16.1623208.31001"
  },
  "1623209": {
    "nombreRubro": "Organismos de socorro de prevención y atencion de desastres con apoyo institucional- Sobretasa Bomberil",
    "rubro": "2.3.3.09.16.16.1623209.21007"
  },
  "1623210": {
    "nombreRubro": "Infraestructura IE Presbitero - Departamento",
    "rubro": "2.3.2.02.02.005.4599.16.1623210.31401"
  },
  "1623213": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 1.4.1 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623213.11001"
  },
  "1623218": {
    "nombreRubro": "Componente de seguridad del proyecto de inversión en educación",
    "rubro": "2.3.2.02.02.009.16.1623218.11001"
  },
  "1623219": {
    "nombreRubro": "Componente de seguridad del proyecto de inversión de medio ambiente y agricultura",
    "rubro": "2.3.2.02.02.009.16.1623219.11001"
  },
  "1623220": {
    "nombreRubro": "Protección Ambiental - Otras Entidades Corantioquia",
    "rubro": "2.3.2.02.02.009.16.1623220.31402"
  },
  "1623221": {
    "nombreRubro": "Unidades productivas agropecuarias - Departamento",
    "rubro": "2.3.2.02.02.009.16.1623221.31401"
  },
  "1623222": {
    "nombreRubro": "Gestión del Riesgo Aportes Departamento",
    "rubro": "2.3.2.02.02.009.16.1623222.31401"
  },
  "1623223": {
    "nombreRubro": "Fortalecimiento cultural municipal - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623223.11001"
  },
  "1623224": {
    "nombreRubro": "Arrendamientos Gestión del Riesgo - ICLD",
    "rubro": "2.3.2.02.02.007.16.1623224.11001"
  },
  "1623225": {
    "nombreRubro": "Aportes Fondo de Solidaridad y Redistribucion del ingreso - Subsidios Alcantarillado",
    "rubro": "2.3.3.01.02.004.02.16.1623225.21201"
  },
  "1623226": {
    "nombreRubro": "Componente de seguridad del proyecto de inversión en adulto mayor",
    "rubro": "2.3.2.02.02.009.16.1623226.11001"
  },
  "1623227": {
    "nombreRubro": "Componente de seguridad del proyecto de inversión en cultura",
    "rubro": "2.3.2.02.02.009.16.1623227.11001"
  },
  "1623228": {
    "nombreRubro": "Componente de seguridad del proyecto de inversión de deporte y recreación",
    "rubro": "2.3.2.02.02.009.16.1623228.11001"
  },
  "1623229": {
    "nombreRubro": "Componente de seguridad del proyecto de inversión en desarrollo social y comunitario",
    "rubro": "2.3.2.02.02.009.16.1623229.11001"
  },
  "1623230": {
    "nombreRubro": "Infraestructura Vial - ICLD",
    "rubro": "2.3.2.02.02.005.16.1623230.11001"
  },
  "1623601": {
    "nombreRubro": "RB Áreas conservadas para la protección de los recursos naturales- ICLD Ley 99/93",
    "rubro": "2.3.2.01.03.001.16.1623601.11001"
  },
  "1623602": {
    "nombreRubro": "RB Programa integral de seguridad - FONSET",
    "rubro": "2.3.2.02.02.009.16.1623602.21401"
  },
  "1623603": {
    "nombreRubro": "RB Aportes Fondo de Solidaridad y Redistribucion del ingreso - Subsidios Acueducto",
    "rubro": "2.3.3.01.02.004.01.1.16.1623603.21201"
  },
  "1623604": {
    "nombreRubro": "RB Aportes Fondo de Solidaridad y Redistribucion del ingreso - Subsidios Alcantarillado",
    "rubro": "2.3.3.01.02.004.02.2.16.1623604.21201"
  },
  "1623605": {
    "nombreRubro": "RB Aportes Fondo de Solidaridad y Redistribucion del ingreso - Subsidios Aseo",
    "rubro": "2.3.3.01.02.004.03.3.16.1623605.21201"
  },
  "1623606": {
    "nombreRubro": "RB Infraestructura territorial  - Alumbrado Público",
    "rubro": "2.3.2.02.02.005.16.1623606.12002"
  },
  "1623607": {
    "nombreRubro": "RB Movilidad territorial sostenible - Multas de transito",
    "rubro": "2.3.2.02.02.009.16.1623607.12003"
  },
  "1623608": {
    "nombreRubro": "RB Multas y sanciones codigo de policia",
    "rubro": "2.3.2.02.02.009.16.1623608.12006"
  },
  "1623609": {
    "nombreRubro": "RB Servicio de educación informal- SGP Educacion Calidad - Servicios Publicos Instituciones Educativas",
    "rubro": "2.3.2.02.02.006.16.1623609.14001"
  },
  "1623610": {
    "nombreRubro": "RB Programa de Alimentación Escolar PAE - SGP Alimentacion Escolar",
    "rubro": "2.3.3.05.07.013.16.1623610.14101"
  },
  "1623611": {
    "nombreRubro": "RB Gestión Sostenible del Agua - SGP Agua potable y saneamiento basico",
    "rubro": "2.3.2.02.02.005.16.1623611.14201"
  },
  "1623612": {
    "nombreRubro": "RB Educación  artística y cultural- SGP - Proposito General Cultura",
    "rubro": "2.3.2.02.02.009.16.1623612.14401"
  },
  "1623613": {
    "nombreRubro": "RB Infraestructura territorial  -SGP Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.005.16.1623613.14501"
  },
  "1623614": {
    "nombreRubro": "RB Organismos de socorro de prevención y atencion de desastres con apoyo institucional- Sobretasa Bomberil",
    "rubro": "2.3.3.09.16.16.1623614.21007"
  },
  "1623615": {
    "nombreRubro": "RB Programa de Alimentación Escolar PAE  - Departamento",
    "rubro": "2.3.3.05.07.013.16.1623615.31001"
  },
  "1623616": {
    "nombreRubro": "RB Gestión Sostenible del Agua - Transferencias del Sector Electrico",
    "rubro": "2.3.2.02.02.005.16.1623616.61001"
  },
  "1623617": {
    "nombreRubro": "RB Fortalecimiento de la cultura deportiva - Tasa prodeporte y recreación 1,5%",
    "rubro": "2.3.2.02.02.009.16.1623617.13006"
  },
  "1623618": {
    "nombreRubro": "RB Comisarías de familia - Estampilla para la justicia familiar 2%",
    "rubro": "2.3.2.02.02.009.16.1623618.13005"
  },
  "1623619": {
    "nombreRubro": "RB Transferencias para pago nomina  Pensionados - FONPET",
    "rubro": "2.3.3.07.02.001.02.16.1623619.71401"
  },
  "1623620": {
    "nombreRubro": "RB Infraestructura territorial - Retiro por devolución del SGP Propósito General",
    "rubro": "2.3.2.02.02.005.16.1623620.71501"
  },
  "1623621": {
    "nombreRubro": "RB Infraestructura territorial - Aportes Departamentales",
    "rubro": "2.3.2.02.02.005.16.1623621.31401"
  },
  "1623622": {
    "nombreRubro": "RB Incentivo al aprovechamiento y tratamiento de residuos sólidos IAT - Otras Entidades",
    "rubro": "2.3.2.02.02.009.16.1623622.31402"
  },
  "1623623": {
    "nombreRubro": "RB Convenios  - Otras Entidades",
    "rubro": "2.3.2.02.02.009.16.1623623.31402"
  },
  "1623624": {
    "nombreRubro": "RB Infraestructura Vial - Aportes Nacionales INVIAS",
    "rubro": "2.3.2.02.02.005. .16.1623624.41001"
  },
  "1623625": {
    "nombreRubro": "RB Infraestructura espacio público - Cesiones Urbanisticas",
    "rubro": "2.3.2.02.02.005.16.1623625.12011"
  },
  "1623626": {
    "nombreRubro": "RB Equipamento - Cesiones Urbanisticas",
    "rubro": "2.3.2.02.02.005.16.1623626.12011"
  },
  "1623627": {
    "nombreRubro": "RB Fortalecimiento de la cultura deportiva - Ingresos corrientes de libre destinación",
    "rubro": "2.3.2.02.02.009.16.1623627.11001"
  },
  "1623628": {
    "nombreRubro": "RB Infraestructura territorial - ICLD",
    "rubro": "2.3.2.02.02.005.16.1623628.11001"
  },
  "1623629": {
    "nombreRubro": "RB Acceso y permanencia en sistema educativo- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623629.11001"
  },
  "1623630": {
    "nombreRubro": "RB Movilidad territorial sostenible - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623630.11001"
  },
  "1623631": {
    "nombreRubro": "RB Desarrollo agropecuario sostenible- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623631.11001"
  },
  "1623632": {
    "nombreRubro": "RB Apoyos logísticos como componente de los proyectos de inversión - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623632.11001"
  },
  "1623633": {
    "nombreRubro": "RB Programa integral de seguridad - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623633.11001"
  },
  "1623634": {
    "nombreRubro": "RB Innovación Fiscal y Financiera- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623634.11001"
  },
  "1623635": {
    "nombreRubro": "RB Apoyo técnico y profesional a la Dirección de Planeación - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623635.11001"
  },
  "1623636": {
    "nombreRubro": "RB Fortalecimiento de la gestión pública - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623636.11001"
  },
  "1623902": {
    "nombreRubro": "VE Gestión Sostenible del Agua - SGP Agua potable y saneamiento basico",
    "rubro": "2.3.2.02.02.005.16.1623902.14201"
  },
  "1623903": {
    "nombreRubro": "VE Convenios - Otras Entidades",
    "rubro": "2.3.2.02.02.005.16.1623903.31403"
  },
  "1623904": {
    "nombreRubro": "VE Gestión Sostenible del Agua - Transferencias del Sector Electrico",
    "rubro": "2.3.2.02.02.005.16.1623904.61001"
  },
  "1621128": {
    "nombreRubro": "Dotación de Obreros",
    "rubro": "2.1.2.02.01.002.16.1621128.11001"
  },
  "1623637": {
    "nombreRubro": "RB Movilidad Innovadora e Inteligente - Multas de transito",
    "rubro": "2.3.2.02.02.009.16.1623637.12003"
  },
  "1623638": {
    "nombreRubro": "RB Infraestructura Deportiva -ICLD",
    "rubro": "2.3.2.02.02.005.16.1623638.11001"
  },
  "1623235": {
    "nombreRubro": "Infraestructura Deportiva -ICLD",
    "rubro": "2.3.2.02.02.005.16.1623235.11001"
  },
  "1622103": {
    "nombreRubro": "Fondo de Contingencias",
    "rubro": "2.2.2.04.16.1622103.11001"
  },
  "1621140": {
    "nombreRubro": "Mantenimiento y Reparaciones",
    "rubro": "2.1.2.02.02.008.16.1621140.11001"
  },
  "1821123": {
    "nombreRubro": "Capacitacion",
    "rubro": "2.1.2.02.02.008.18.1821123.11001"
  },
  "1821125": {
    "nombreRubro": "Servicios publicos - concejo",
    "rubro": "2.1.2.02.02.006.18.1821125.11001"
  },
  "1821126": {
    "nombreRubro": " Convocatoria cargo secretario general del concejo",
    "rubro": "2.1.2.02.02.008.18.1821126.11001"
  },
  "1821127": {
    "nombreRubro": "Remuneracion servicios tecnicos",
    "rubro": "2.1.2.02.02.008.18.1821127.11001"
  },
  "2123101": {
    "nombreRubro": "RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN-  SGP ULTIMA DOCEAVA",
    "rubro": "2.3.2.02.02.009.19.01.20.21.2123101.23005"
  },
  "2123102": {
    "nombreRubro": "RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN-  SGP ONCE DOCEAVAS",
    "rubro": "2.3.2.02.02.009.19.01.20.21.2123102.23005"
  },
  "2123103": {
    "nombreRubro": "RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN  - ADRES SSF",
    "rubro": "2.3.2.02.02.009.19.01.20.21.2123103.23001"
  },
  "2123104": {
    "nombreRubro": "RS - E1 GOBERNABILIDAD Y GOBERNANZA - L1 DESARROLLO INSTITUCIONAL Y SECTORIAL - SUPERSALUD",
    "rubro": "2.3.2.02.02.009.19.01.19.21.2123104.23003"
  },
  "2123106": {
    "nombreRubro": "RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN - RENTAS CEDIDAS - DEPARTAMENTO REGIMEN SUBSIDIADO SSF",
    "rubro": "2.3.2.02.02.009.19.01.20.21.2123106.23006"
  },
  "2123107": {
    "nombreRubro": "RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN-  COLJUEGOS 75% SSF",
    "rubro": "2.3.2.02.02.009.19.01.20.21.2123107.23004"
  },
  "2123108": {
    "nombreRubro": "RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN-  INGRESOS CORRIENTES DE LIBRE DESTINACIÓN REGIMEN SUBSIDIADO",
    "rubro": "2.3.2.02.02.009.19.01.20.21.2123108.11001"
  },
  "2123112": {
    "nombreRubro": "PS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - SUBSIDIO A LA OFERTA - EMPRESAS SOCIALES DEL ESTADO- SGP OFERTA PRESTACION DE SERVICIOS ULTIMA DOCEAVA",
    "rubro": "2.3.2.02.02.009.19.03.28.21.2123112.23202"
  },
  "2123113": {
    "nombreRubro": "PS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - SUBSIDIO A LA OFERTA - EMPRESAS SOCIALES DEL ESTADO- SGP OFERTA PRESTACION DE SERVICIOS ONCE DOCEAVA",
    "rubro": "2.3.2.02.02.009.19.03.28.21.2123113.23202"
  },
  "2123114": {
    "nombreRubro": "PS - E2 PUEBLOS Y COMUNIDADES - L3 CUIDADO DE LA SALUD - ATENCIÓN POBLACIÓN MIGRANTE INSTITUCIONES PÚBLICAS- INGRESOS CORRIENTES DE LIBRE DESTINACIÓN - PRESTACIÓN DE SERVICIOS - OFERTA",
    "rubro": "2.3.2.02.02.009.19.03.28.21.2123114.11001"
  },
  "2123117": {
    "nombreRubro": "SPC - E2 Pueblos y comunidades - L3 Cuidado de la Salud - Garantía del derecho a la salud de pueblos y comunidades étnicas y campesinas, mujeres, sectores LGBTIQ+ y otras poblaciones por condición y/o",
    "rubro": "2.3.2.02.02.009.19.02.99.21.2123117.11001"
  },
  "2123118": {
    "nombreRubro": "SPC - E3 Determinantes - L3 Cuidado de la Salud - Información en salud para la afectación positiva de los determinantes sociales de la salud en el territorio- ICLD 60%",
    "rubro": "2.3.2.02.02.009.19.02.103.21.2123118.11001"
  },
  "2123119": {
    "nombreRubro": "SPC - E4 Atención Primaria Salud - L3 Cuidado de la Salud - Cuidado de la Salud - familias, colectivos y comunidades- ICLD 60%",
    "rubro": "2.3.2.02.02.009.19.02.107.21.2123119.11001"
  },
  "2123120": {
    "nombreRubro": "SPC - E4 Atención Primaria Salud - L1 Desarrollo Institucional y Sectorial - Desarrollo de capacidades en salud pública para la implementación de la estrategia de Atención Primaria en Salud- ICLD 60%",
    "rubro": "2.3.2.02.02.009.19.02.104.21.2123120.11001"
  },
  "2123121": {
    "nombreRubro": "SPC - E5 Cambio Climático - L3 Cuidado de la Salud - Respuesta en salud pública ante los efectos en la salud generados por la variabilidad y el cambio climático y la ocurrencia de emergencias, desastr",
    "rubro": "2.3.2.02.02.009.19.02.118.21.2123121.11001"
  },
  "2123122": {
    "nombreRubro": "SPC - E7 Personal Salud - L3 Cuidado de la Salud - Cuidado de la salud en el entorno laboral- ICLD 60%",
    "rubro": "2.3.2.02.02.009.19.02.127.21.2123122.11001"
  },
  "2123123": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L1 Desarrollo Institucional y Sectorial - Desarrollo de  capacidades para la Gobernanza en Salud Pública- ICLD 40%",
    "rubro": "2.3.2.02.02.009.19.02.91.21.2123123.11001"
  },
  "2123124": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L1 Desarrollo Institucional y Sectorial - Inspección, vigilancia y control sanitarios- ICLD 40%",
    "rubro": "2.3.2.02.02.009.19.02.92.21.2123124.11001"
  },
  "2123125": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L2 Acuerdos Sociales y Comunitarios - Participación social con enfoque diferencial para la gobernanza en salud pública- SGP 40%",
    "rubro": "2.3.2.02.02.009.19.02.93.21.2123125.11001"
  },
  "2123126": {
    "nombreRubro": "SPC - E2 Pueblos y comunidades - L3 Cuidado de la Salud - Garantía del derecho a la salud de pueblos y comunidades étnicas y campesinas, mujeres, sectores LGBTIQ+ y otras poblaciones por condición y/o",
    "rubro": "2.3.2.02.02.009.19.02.99.21.2123126.23101"
  },
  "2123127": {
    "nombreRubro": "SPC - E3 Determinantes - L3 Cuidado de la Salud - Información en salud para la afectación positiva de los determinantes sociales de la salud en el territorio- SGP 60% Once Doceavas",
    "rubro": "2.3.2.02.02.009.19.02.103.21.2123127.23101"
  },
  "2123128": {
    "nombreRubro": "SPC - E4 Atención Primaria Salud - L3 Cuidado de la Salud - Cuidado de la Salud - familias, colectivos y comunidades- SGP 60%Once Doceavas",
    "rubro": "2.3.2.02.02.009.19.02.107.21.2123128.23101"
  },
  "2123129": {
    "nombreRubro": "SPC - E4 Atención Primaria Salud - L1 Desarrollo Institucional y Sectorial - Desarrollo de capacidades en salud pública para la implementación de la estrategia de Atención Primaria en Salud- SPG 60% O",
    "rubro": "2.3.2.02.02.009.19.02.104.21.2123129.23101"
  },
  "2123130": {
    "nombreRubro": "SPC - E4 Atención Primaria Salud - L1 Desarrollo Institucional y Sectorial - Desarrollo de capacidades en salud pública para la implementación de la estrategia de Atención Primaria en Salud- SPG 60% U",
    "rubro": "2.3.2.02.02.009.19.02.104.21.2123130.23101"
  },
  "2123131": {
    "nombreRubro": "SPC - E5 Cambio Climático - L3 Cuidado de la Salud - Respuesta en salud pública ante los efectos en la salud generados por la variabilidad y el cambio climático y la ocurrencia de emergencias, desastr",
    "rubro": "2.3.2.02.02.009.19.02.118.21.2123131.23101"
  },
  "2123132": {
    "nombreRubro": "SPC - E7 Personal Salud - L3 Cuidado de la Salud - Cuidado de la salud en el entorno laboral- SGP 60% Once Doceavas",
    "rubro": "2.3.2.02.02.009.19.02.127.21.2123132.23101"
  },
  "2123133": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L1 Desarrollo Institucional y Sectorial - Vigilancia en Salud Pública y fortalecimiento de los laboratorios en salud pública- SGP 40% Doce Doceavas",
    "rubro": "2.3.2.02.02.009.19.02.91.21.2123133.23101"
  },
  "2123134": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L1 Desarrollo Institucional y Sectorial - Inspección, vigilancia y control sanitarios- SGP 40% Doce Doceavas",
    "rubro": "2.3.2.02.02.009.19.02.92.21.2123134.23101"
  },
  "2123135": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L2 Acuerdos Sociales y Comunitarios - Participación social con enfoque diferencial para la gobernanza en salud pública- SGP 40%",
    "rubro": "2.3.2.02.02.009.19.02.93.21.2123135.23101"
  },
  "2123136": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L3 Cuidado de la Salud - Gestión de las políticas públicas en salud- SGP 40% Doce Doceavas",
    "rubro": "2.3.2.02.02.009.19.02.95.21.2123136.23101"
  },
  "2123137": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L3 Cuidado de la Salud - Gestión de las políticas públicas en salud- SGP 40% Ultima Doceava",
    "rubro": "2.3.2.02.02.009.19.02.95.21.2123137.23101"
  },
  "2123140": {
    "nombreRubro": "OGS - FUNCIONAMIENTO",
    "rubro": "2.3.2.02.02.009.19.05.58.21.2123140.23301"
  },
  "2123143": {
    "nombreRubro": "RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN  - ADRES SSF - RENDIMIENTOS FINANCIEROS",
    "rubro": "2.3.2.02.02.009.16.2123143.23001"
  },
  "2123182": {
    "nombreRubro": "Fortalecimiento y protección integral del adulto mayor - ICLD",
    "rubro": "2.3.2.02.02.009.21.2123182.11001"
  },
  "2123183": {
    "nombreRubro": "Fortalecimiento y protección integral del adulto mayor - Estampilla Pro Bienestar del Anciano 30%",
    "rubro": "2.3.2.02.02.009.21.2123183.13001"
  },
  "2123184": {
    "nombreRubro": "Fortalecimiento y protección integral del adulto mayor- SGP  Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.21.2123184.14501"
  },
  "2123185": {
    "nombreRubro": "Fortalecimiento y protección integral del adulto mayor - Estampilla Pro Bienestar del Anciano 70%",
    "rubro": "2.3.2.02.02.009.21.2123185.13001"
  },
  "2123206": {
    "nombreRubro": "Mejoramiento de la infraestructura de atención en salud- ESE ICLD",
    "rubro": "2.3.3.01.02.005.21.2123206.11001"
  },
  "2123601": {
    "nombreRubro": "RB Fortalecimiento y protección integral del adulto mayor - Estampilla Pro Bienestar del Anciano 70%",
    "rubro": "2.3.2.02.02.009.21.2123601.13001"
  },
  "2123602": {
    "nombreRubro": "RB Fortalecimiento y protección integral del adulto mayor - Estampilla Pro Bienestar del Anciano 30%",
    "rubro": "2.3.2.02.02.009.21.2123602.13001"
  },
  "2123603": {
    "nombreRubro": "RB Desarrollo integral de la primera infancia - SGP Primera Infancia",
    "rubro": "2.3.2.02.02.009.21.2123603.14701"
  },
  "2123604": {
    "nombreRubro": "RB Fortalecimiento y protección integral del adulto mayor - ICLD",
    "rubro": "2.3.2.02.02.009.21.2123604.11001"
  },
  "2123605": {
    "nombreRubro": "RB RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN - EXISTENCIA CAJA Y BANCOS RS",
    "rubro": "2.3.2.02.02.009.21.2123605.23005"
  },
  "2123606": {
    "nombreRubro": "RB RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN - RENDIMIENTOS FINANCIEROS RS",
    "rubro": "2.3.2.02.02.009.21.2123606.23005"
  },
  "2123607": {
    "nombreRubro": "RB SPC - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - CUIDADO DE LA SALUD - FAMILIAS, COLECTIVOS Y COMUNIDADES- SGP 60% EXISTENCIAS CAJA Y BANCOS",
    "rubro": "2.3.2.02.02.009.19.02.107.21.2123607.23101"
  },
  "2123608": {
    "nombreRubro": "RB SPC - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - CUIDADO DE LA SALUD - FAMILIAS, COLECTIVOS Y COMUNIDADES- SGP 60% RENDIMIENTOS FINANCIEROS",
    "rubro": "2.3.2.02.02.009.19.02.107.16.2123608.23101"
  },
  "2123609": {
    "nombreRubro": "RB SPC - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - CUIDADO DE LA SALUD - FAMILIAS, COLECTIVOS Y COMUNIDADES- SGP 40% EXISTENCIAS CAJA Y BANCOS",
    "rubro": "2.3.2.02.02.009.19.02.107.21.2123609.23101"
  },
  "2123610": {
    "nombreRubro": "RB SPC - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - CUIDADO DE LA SALUD - FAMILIAS, COLECTIVOS Y COMUNIDADES- SGP 40% RENDIMIENTOS FINANCIEROS",
    "rubro": "2.3.2.02.02.009.19.02.107.21.2123610.23101"
  },
  "2123611": {
    "nombreRubro": "RB PS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - SUBSIDIO A LA OFERTA - EMPRESAS SOCIALES DEL ESTADO- EXISTENCIA CAJA Y BANCOS",
    "rubro": "2.3.2.02.02.009.21.2123611.23202"
  },
  "2123612": {
    "nombreRubro": "RB PS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - SUBSIDIO A LA OFERTA - EMPRESAS SOCIALES DEL ESTADO- RENDIMIENTOS FINANCIEROS SGP",
    "rubro": "2.3.2.02.02.009.21.2123612.23202"
  },
  "2123613": {
    "nombreRubro": "RB OGS - FUNCIONAMIENTO- EXISTENCIA CAJA Y BANCOS",
    "rubro": "2.3.2.02.02.009.21.2123613.23301"
  },
  "2123614": {
    "nombreRubro": "RB OGS - FUNCIONAMIENTO- RENDIMIENTOS FINANCIEROS",
    "rubro": "2.3.2.02.02.009.21.2123614.23301"
  },
  "2123615": {
    "nombreRubro": "RB OGS - E2 PUEBLOS Y COMUNIDADES - L3 CUIDADO DE LA SALUD - ATENCIÓN INTEGRAL CENTROS VIDA O DÍA ADULTO MAYOR/PERSONAS MAYORES- EXISTENCIA CAJA Y BANCOS",
    "rubro": "2.3.2.02.02.009.21.2123615.23302"
  },
  "2123616": {
    "nombreRubro": "RB OGS - E2 PUEBLOS Y COMUNIDADES - L3 CUIDADO DE LA SALUD - ATENCIÓN INTEGRAL CENTROS VIDA O DÍA ADULTO MAYOR/PERSONAS MAYORES- RENDIMIENTOS FINANCIEROS",
    "rubro": "2.3.2.02.02.009.21.2123616.23302"
  }
  ,
  "2123186": {
    "nombreRubro": "SPC-E6 Conocimiento Salud -L3 Cuidado de la Salud- Acciones estratégicas de información en Salud- ICLD 60 %",
    "rubro": "2.3.2.02.02.009.19.02.123.21.2123186.11001"
  }
  ,
  "2021119": {
    "nombreRubro": "Materiales y Suministros",
    "rubro": "2.1.2.02.01.003.20.2021119.11001"
  }
  ,
  "1621131": {
    "nombreRubro": "Materiales y Suministros",
    "rubro": "2.1.2.02.01.003.16.1621131.11001"
  },
  "1621128": {
    "nombreRubro": "Dotacion obreros",
    "rubro": "2.1.2.02.01.002.16.1621128.11001"
  },
  "1621129": {
    "nombreRubro": "Relaciones publicas",
    "rubro": "2.1.2.02.01.003.16.1621129.11001"
  },
  "1621130": {
    "nombreRubro": "Combustibles y lubricantes",
    "rubro": "2.1.2.02.01.003.16.1621130.11001"
  },
  "1621131": {
    "nombreRubro": "Materiales y suministros",
    "rubro": "2.1.2.02.01.003.16.1621131.11001"
  },
  "1621132": {
    "nombreRubro": "Comunicaciones y transporte",
    "rubro": "2.1.2.02.02.006.16.1621132.11001"
  },
  "1621134": {
    "nombreRubro": "Seguros y polizas",
    "rubro": "2.1.2.02.02.007.16.1621134.11001"
  },
  "1621135": {
    "nombreRubro": "Arrendamiento bienes muebles",
    "rubro": "2.1.2.02.02.007.16.1621135.11001"
  },
  "1621136": {
    "nombreRubro": "Honorarios profesionales  - servicios personales indirectos",
    "rubro": "2.1.2.02.02.008.16.1621136.11001"
  },
  "1621137": {
    "nombreRubro": "Concejo territorial de planeacion",
    "rubro": "2.1.2.02.02.008.16.1621137.11001"
  },
  "1621138": {
    "nombreRubro": "Impresos y publicaciones",
    "rubro": "2.1.2.02.02.008.16.1621138.11001"
  },
  "1621139": {
    "nombreRubro": "Capacitacion",
    "rubro": "2.1.2.02.02.008.16.1621139.11001"
  },
  "1621140": {
    "nombreRubro": "Mantenimiento y reparaciones",
    "rubro": "2.1.2.02.02.008.16.1621140.11001"
  },
  "1621141": {
    "nombreRubro": "Servicios publicos - telecomunicaciones",
    "rubro": "2.1.2.02.02.008.16.1621141.11001"
  },
  "1621142": {
    "nombreRubro": "Estimulo hogares comunitarios",
    "rubro": "2.1.2.02.02.009.16.1621142.11001"
  },
  "1621145": {
    "nombreRubro": "Asociacion redes casa de la cultura",
    "rubro": "2.1.3.04.05.001.16.1621145.11001"
  },
  "1621150": {
    "nombreRubro": "Convencion colectiva",
    "rubro": "2.1.3.07.02.030.16.1621150.11001"
  },
  "1621151": {
    "nombreRubro": "Salud ocupacional",
    "rubro": "2.1.3.07.02.031.16.1621151.11001"
  },
  "1621152": {
    "nombreRubro": "Bienestar social - laboral",
    "rubro": "2.1.3.07.02.031.16.1621152.11001"
  },
  "1621159": {
    "nombreRubro": "Tasa especial de seguridad y convivencia ciudadana Ordenanza 050 de 2024",
    "rubro": "2.1.8.03.16.1621159.11001"
  },
  "1621160": {
    "nombreRubro": "Tasa Utilizacion del Agua - Corantioquia",
    "rubro": "2.1.8.03.16.1621160.11001"
  },
  "1623101": {
    "nombreRubro": "Derechos de Autor y Licencias - SGP Proposito General Libre Inversion",
    "rubro": "2.3.2.01.01.005.02.05.16.1623101.14501"
  },
  "1623102": {
    "nombreRubro": "Fortalecimiento de la gestión pública - SGP Proposito General Libre Inversion",
    "rubro": "2.3.2.01.01.003.03.01.16.1623102.14501"
  },
  "1623103": {
    "nombreRubro": "Equipos de Comunicaciones- SGP Proposito General Libre Inversion",
    "rubro": "2.3.2.01.01.003.05.02.16.1623103.14501"
  },
  "1623104": {
    "nombreRubro": "Fortalecimiento de la Movilidad Sostenible Metro de Medellín - Sobretasa a la gasolina SSF",
    "rubro": "2.3.2.01.01.003.07.05.16.1623104.11001"
  },
  "1623105": {
    "nombreRubro": "Áreas conservadas para la protección de los recursos naturales- ICLD Ley 99/93",
    "rubro": "2.3.2.01.03.001.16.1623105.11001"
  },
  "1623106": {
    "nombreRubro": "Fortalecimiento del desarrollo cultural municipal - ICLD",
    "rubro": "2.3.2.02.02.005.16.1623106.11001"
  },
  "1623107": {
    "nombreRubro": "Gestión Sostenible del Agua - SGP Agua potable y saneamiento basico",
    "rubro": "2.3.2.02.02.005.16.1623107.14201"
  },
  "1623108": {
    "nombreRubro": "Gestión Sostenible del Agua - Transferencias del Sector Electrico",
    "rubro": "2.3.2.02.02.005.16.1623108.61001"
  },
  "1623109": {
    "nombreRubro": "Infraestructura Gestión del Riesgo - ICLD",
    "rubro": "2.3.2.02.02.005.16.1623109.11001"
  },
  "1623110": {
    "nombreRubro": "Infraestructura Vial - ICLD",
    "rubro": "2.3.2.02.02.005.16.1623110.11001"
  },
  "1623111": {
    "nombreRubro": "Infraestructura territorial - ICLD",
    "rubro": "2.3.2.02.02.005.16.1623111.11001"
  },
  "1623112": {
    "nombreRubro": "Infraestructura territorial  - Alumbrado Público",
    "rubro": "2.3.2.02.02.005.16.1623112.12002"
  },
  "1623113": {
    "nombreRubro": "Infraestructura espacio público - Cesiones Urbanisticas",
    "rubro": "2.3.2.02.02.005.16.1623113.12011"
  },
  "1623114": {
    "nombreRubro": "Equipamento - Cesiones Urbanisticas",
    "rubro": "2.3.2.02.02.005.16.1623114.12011"
  },
  "1623115": {
    "nombreRubro": "Infraestructura territorial  -SGP Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.005.16.1623115.14501"
  },
  "1623116": {
    "nombreRubro": "Servicio de educación informal- SGP Educacion Calidad - Servicios Publicos Instituciones Educativas",
    "rubro": "2.3.2.02.02.006.16.1623116.14001"
  },
  "1623117": {
    "nombreRubro": "Fortalecimiento Educativo - ICLD- Servicios Publicos Instituciones Educativas",
    "rubro": "2.3.2.02.02.006.16.1623117.11001"
  },
  "1623118": {
    "nombreRubro": "Servicios Publicos Establecimientos Deportivos - SGP  Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.006.16.1623118.14501"
  },
  "1623119": {
    "nombreRubro": "Transporte como componente en proyectos de inversión - ICLD",
    "rubro": "2.3.2.02.02.006.16.1623119.11001"
  },
  "1623120": {
    "nombreRubro": "Fortalecimiento del Sector Turístico - ICLD",
    "rubro": "2.3.2.02.02.008.16.1623120.11001"
  },
  "1623121": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 3.4.2 - ICLD",
    "rubro": "2.3.2.02.02.008.16.1623121.11001"
  },
  "1623122": {
    "nombreRubro": "Gobierno Digital - SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.008.16.1623122.14501"
  },
  "1623123": {
    "nombreRubro": "Apoyo técnico y profesional a la Dirección de Planeación SISBEN - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623123.11001"
  },
  "1623124": {
    "nombreRubro": "Mejoramiento de la calidad de la educación para el trabajo y el desarrollo humano- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623124.11001"
  },
  "1623125": {
    "nombreRubro": "Fortalecimiento integral, pedagógico y educativo - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623125.11001"
  },
  "1623126": {
    "nombreRubro": "Fortalecimiento educativo - SGP - Educacion Gratuidad",
    "rubro": "2.3.2.02.02.009.16.1623126.14002"
  },
  "1623127": {
    "nombreRubro": "Acceso y permanencia en sistema educativo- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623127.11001"
  },
  "1623128": {
    "nombreRubro": "Fortalecimiento y encadenamineto de los diferentes niveles educativos - ICLD",
    "rubro": "2.3.2.02.02.009.92919.16.1623128.11001"
  },
  "1623129": {
    "nombreRubro": "Educación  artística y cultural- SGP - Proposito General Cultura Once Doceavas",
    "rubro": "2.3.2.02.02.009.16.1623129.14401"
  },
  "1623130": {
    "nombreRubro": "Educación  artística y cultural- SGP  Proposito General Cultura Ultima Doceava",
    "rubro": "2.3.2.02.02.009.16.1623130.14401"
  },
  "1623131": {
    "nombreRubro": "Educación  artística y cultural- SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623131.14501"
  },
  "1623132": {
    "nombreRubro": "Gestión, protección y salvaguardia del patrimonio cultural- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623132.11001"
  },
  "1623133": {
    "nombreRubro": "Mejoramiento y dotación de la infraestructura cultural - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623133.11001"
  },
  "1623134": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 1.3.4 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623134.11001"
  },
  "1623135": {
    "nombreRubro": "Fortalecimiento de la cultura deportiva - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623135.11001"
  },
  "1623136": {
    "nombreRubro": "Fortalecimiento de la cultura deportiva - Tasa prodeporte y recreación 1,5%",
    "rubro": "2.3.2.02.02.009.16.1623136.13006"
  },
  "1623137": {
    "nombreRubro": "Fortalecimiento de la cultura deportiva - SGP  Proposito General Deportes Once Doceavas",
    "rubro": "2.3.2.02.02.009.16.1623137.14301"
  },
  "1623138": {
    "nombreRubro": "Fortalecimiento de la cultura deportiva - SGP  Proposito General Deportes Última Doceava",
    "rubro": "2.3.2.02.02.009.16.1623138.14301"
  },
  "1623139": {
    "nombreRubro": "Fortalecimiento de la cultura deportiva - SGP  Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623139.14501"
  },
  "1623140": {
    "nombreRubro": "Fortalecimiento deportivo - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623140.11001"
  },
  "1623141": {
    "nombreRubro": "Liderazgo en gobernanza para la gestión deportiva - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623141.11001"
  },
  "1623142": {
    "nombreRubro": "Conservación de la biodiversidad y sus servicios ecosistemicos- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623142.11001"
  },
  "1623143": {
    "nombreRubro": "Protección del recurso hídrico- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623143.11001"
  },
  "1623144": {
    "nombreRubro": "Adaptación al cambio climatico - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623144.11001"
  },
  "1623145": {
    "nombreRubro": "Educación y cultura ambiental territorializada - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623145.11001"
  },
  "1623146": {
    "nombreRubro": "Gestión de residuos y fortalecimiento ambiental- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623146.11001"
  },
  "1623147": {
    "nombreRubro": "Incentivo al aprovechamiento y tratamiento de residuos sólidos IAT - Otras Entidades",
    "rubro": "2.3.2.02.02.009.16.1623147.31402"
  },
  "1623148": {
    "nombreRubro": "Produccion verde y responsable - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623148.11001"
  },
  "1623149": {
    "nombreRubro": "Bienestar animal y tenencia responsable - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623149.11001"
  },
  "1623150": {
    "nombreRubro": "Plan municipal de gestion del riesgo y de desastres- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623150.11001"
  },
  "1623151": {
    "nombreRubro": "Gestión del Riesgo - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623151.11001"
  },
  "1623152": {
    "nombreRubro": "Planificación e implementación técnica y normativa para el control del riesgo - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623152.11001"
  },
  "1623153": {
    "nombreRubro": "Vivienda digna para sus habitantes y servicios públicos- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623153.11001"
  },
  "1623154": {
    "nombreRubro": "Movilidad territorial sostenible - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623154.11001"
  },
  "1623155": {
    "nombreRubro": "Movilidad territorial sostenible - Multas de transito",
    "rubro": "2.3.2.02.02.009.16.1623155.12003"
  },
  "1623156": {
    "nombreRubro": "Movilidad innovadora e  inteligente - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623156.11001"
  },
  "1623157": {
    "nombreRubro": "Plan basico de ordenamiento territorial- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623157.11001"
  },
  "1623158": {
    "nombreRubro": "Emprendimiento SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623158.14501"
  },
  "1623159": {
    "nombreRubro": "Servicio de orientación laboral- SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623159.14501"
  },
  "1623160": {
    "nombreRubro": "Fortalecimiento empresarial SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623160.14501"
  },
  "1623161": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 3.1.4 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623161.11001"
  },
  "1623162": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 3.1.5 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623162.11001"
  },
  "1623163": {
    "nombreRubro": "Desarrollo agropecuario sostenible- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623163.11001"
  },
  "1623164": {
    "nombreRubro": "Desarrollo de cadenas de valor y promoción de productos locales - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623164.11001"
  },
  "1623165": {
    "nombreRubro": "Desarrollo Turistico Sostenible SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623165.14501"
  },
  "1623166": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 3.3.3 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623166.11001"
  },
  "1623167": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 3.4.1- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623167.11001"
  },
  "1623168": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 3.4.3 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623168.11001"
  },
  "1623169": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 3.4.4- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623169.11001"
  },
  "1623170": {
    "nombreRubro": "Desarrollo integral de la primera infancia - SGP Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623170.14501"
  },
  "1623171": {
    "nombreRubro": "Programa de Alimentación Escolar PAE  - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623171.11001"
  },
  "1623172": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 4.1.2 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623172.11001"
  },
  "1623173": {
    "nombreRubro": "Ayudas Humanitarias SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623173.14501"
  },
  "1623174": {
    "nombreRubro": "Fortalecimiento integral de las víctimas del conflicto - SGP  Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623174.14501"
  },
  "1623175": {
    "nombreRubro": "Comisarías de familia - Estampilla para la justicia familiar 2%",
    "rubro": "2.3.2.02.02.009.16.1623175.13005"
  },
  "1623176": {
    "nombreRubro": "Programa integral de seguridad - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623176.11001"
  },
  "1623177": {
    "nombreRubro": "Programa integral de seguridad - FONSET",
    "rubro": "2.3.2.02.02.009.16.1623177.21401"
  },
  "1623178": {
    "nombreRubro": "Multas y sanciones codigo de policia",
    "rubro": "2.3.2.02.02.009.16.1623178.12006"
  },
  "1623179": {
    "nombreRubro": "Fortalecimiento de los espacios para convivencia ciudadana - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623179.11001"
  },
  "1623180": {
    "nombreRubro": "Fortalecimiento FONSET",
    "rubro": "2.3.2.02.02.009.16.1623180.21401"
  },
  "1623181": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 4.2.1 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623181.11001"
  },
  "1623185": {
    "nombreRubro": "Fortalecimiento Juventud SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623185.14501"
  },
  "1623186": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 4.5.1 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623186.11001"
  },
  "1623187": {
    "nombreRubro": "Desarrollo integral de las mujeres . - SGP  Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623187.14501"
  },
  "1623188": {
    "nombreRubro": "Fortalecimiento Poblacion LGBTIQA+ SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623188.14501"
  },
  "1623189": {
    "nombreRubro": "Personas con discapacidad atendidas con servicios integrales - SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623189.14501"
  },
  "1623190": {
    "nombreRubro": "Fortalecimiento y atención   integral de de la población habitante de calle  - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623190.11001"
  },
  "1623191": {
    "nombreRubro": "Fortalecimiento Poblacion Habitante de Calle SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623191.14501"
  },
  "1623192": {
    "nombreRubro": "Culto Religioso- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623192.11001"
  },
  "1623193": {
    "nombreRubro": "Innovación Fiscal y Financiera- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623193.11001"
  },
  "1623194": {
    "nombreRubro": "Apoyo técnico y profesional a la Dirección de Planeación - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623194.11001"
  },
  "1623195": {
    "nombreRubro": "Sistema de Gestión de Calidad- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623195.11001"
  },
  "1623196": {
    "nombreRubro": "Reordenamiento administrativo- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623196.11001"
  },
  "1623197": {
    "nombreRubro": "Modernizacion institucional  -SGP Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623197.14501"
  },
  "1623198": {
    "nombreRubro": "Promoción a la participación ciudadana- SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.16.1623198.14501"
  },
  "1623199": {
    "nombreRubro": "Estrategias de comunicacion, de informacion y relaciones publicas - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623199.11001"
  },
  "1623200": {
    "nombreRubro": "Fortalecimiento de la gestión pública - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623200.11001"
  },
  "1623201": {
    "nombreRubro": "Aportes Fondo de Solidaridad y Redistribucion del ingreso - Subsidios Acueducto",
    "rubro": "2.3.3.01.02.004.01.16.1623201.21201"
  },
  "1623202": {
    "nombreRubro": "Subsidios acueducto y alcantarillado- SGP Agua potable y saneamiento basico",
    "rubro": "2.3.3.01.02.004.01.16.1623202.14201"
  },
  "1623203": {
    "nombreRubro": "Subsidios aseo -  SGP Agua potable y saneamiento basico",
    "rubro": "2.3.3.01.02.004.03.16.1623203.14201"
  },
  "1623204": {
    "nombreRubro": "Aportes Fondo de Solidaridad y Redistribucion del ingreso - Subsidios Aseo",
    "rubro": "2.3.3.01.02.004.03.16.1623204.21201"
  },
  "1623205": {
    "nombreRubro": "Plan Departamental de Agua PDA -SGP Agua potable y saneamiento basico",
    "rubro": "2.3.3.01.02.004.03.16.1623205.14201"
  },
  "1623207": {
    "nombreRubro": "Programa de Alimentación Escolar PAE - SGP Alimentacion Escolar",
    "rubro": "2.3.3.05.07.013.16.1623207.14101"
  },
  "1623208": {
    "nombreRubro": "Programa de Alimentación Escolar PAE  - Departamento",
    "rubro": "2.3.3.05.07.013.16.1623208.31001"
  },
  "1623209": {
    "nombreRubro": "Organismos de socorro de prevención y atencion de desastres con apoyo institucional- Sobretasa Bomberil",
    "rubro": "2.3.3.09.16.16.1623209.21007"
  },
  "1623210": {
    "nombreRubro": "Infraestructura IE Presbitero - Departamento",
    "rubro": "2.3.2.02.02.005.4599.16.1623210.31401"
  },
  "1623213": {
    "nombreRubro": "Apoyos logísticos como componente de los proyectos de inversión 1.4.1 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623213.11001"
  },
  "1623218": {
    "nombreRubro": "Componente de seguridad del proyecto de inversión en educación",
    "rubro": "2.3.2.02.02.009.16.1623218.11001"
  },
  "1623219": {
    "nombreRubro": "Componente de seguridad del proyecto de inversión de medio ambiente y agricultura",
    "rubro": "2.3.2.02.02.009.16.1623219.11001"
  },
  "1623220": {
    "nombreRubro": "Protección Ambiental - Otras Entidades Corantioquia",
    "rubro": "2.3.2.02.02.009.16.1623220.31402"
  },
  "1623221": {
    "nombreRubro": "Unidades productivas agropecuarias - Departamento",
    "rubro": "2.3.2.02.02.009.16.1623221.31401"
  },
  "1623222": {
    "nombreRubro": "Gestión del Riesgo Aportes Departamento",
    "rubro": "2.3.2.02.02.009.16.1623222.31401"
  },
  "1623223": {
    "nombreRubro": "Fortalecimiento cultural municipal - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623223.11001"
  },
  "1623224": {
    "nombreRubro": "Arrendamientos Gestión del Riesgo - ICLD",
    "rubro": "2.3.2.02.02.007.16.1623224.11001"
  },
  "1623225": {
    "nombreRubro": "Aportes Fondo de Solidaridad y Redistribucion del ingreso - Subsidios Alcantarillado",
    "rubro": "2.3.3.01.02.004.02.16.1623225.21201"
  },
  "1623226": {
    "nombreRubro": "Componente de seguridad del proyecto de inversión en adulto mayor",
    "rubro": "2.3.2.02.02.009.16.1623226.11001"
  },
  "1623227": {
    "nombreRubro": "Componente de seguridad del proyecto de inversión en cultura",
    "rubro": "2.3.2.02.02.009.16.1623227.11001"
  },
  "1623228": {
    "nombreRubro": "Componente de seguridad del proyecto de inversión de deporte y recreación",
    "rubro": "2.3.2.02.02.009.16.1623228.11001"
  },
  "1623229": {
    "nombreRubro": "Componente de seguridad del proyecto de inversión en desarrollo social y comunitario",
    "rubro": "2.3.2.02.02.009.16.1623229.11001"
  },

  "1623234": {
    "nombreRubro": "Tasas retributivas Proteccion del recurso hídrico - ICLD",
    "rubro": "2.3.8.03.16.1623234.11001"
  },
  "1623235": {
    "nombreRubro": "Infraestructura Deportiva - ICLD",
    "rubro": "2.3.2.02.02.005.16.1623235.11001"
  },
  "1623236": {
    "nombreRubro": "Reordenamiento administrativo- SGP - Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.1.16.1623236.14501"
  },
  "1623238": {
    "nombreRubro": "Feria del libro Mincultura - Nacion",
    "rubro": "2.3.2.02.02.009.16.1623238.31404"
  },
  "1623240": {
    "nombreRubro": "Gestión cultural y artística- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623240.11001"
  },
  "1623242": {
    "nombreRubro": "Servicios Publicos Establecimientos Deportivos - ICLD",
    "rubro": "2.3.2.02.02.006.16.1623242.11001"
  },
  "1623601": {
    "nombreRubro": "RB Áreas conservadas para la protección de los recursos naturales- ICLD Ley 99/93",
    "rubro": "2.3.2.01.03.001.16.1623601.11001"
  },
  "1623602": {
    "nombreRubro": "RB Programa integral de seguridad - FONSET",
    "rubro": "2.3.2.02.02.009.16.1623602.21401"
  },
  "1623603": {
    "nombreRubro": "RB Aportes Fondo de Solidaridad y Redistribucion del ingreso - Subsidios Acueducto",
    "rubro": "2.3.3.01.02.004.01.1.16.1623603.21201"
  },
  "1623604": {
    "nombreRubro": "RB Aportes Fondo de Solidaridad y Redistribucion del ingreso - Subsidios Alcantarillado",
    "rubro": "2.3.3.01.02.004.02.2.16.1623604.21201"
  },
  "1623605": {
    "nombreRubro": "RB Aportes Fondo de Solidaridad y Redistribucion del ingreso - Subsidios Aseo",
    "rubro": "2.3.3.01.02.004.03.3.16.1623605.21201"
  },
  "1623606": {
    "nombreRubro": "RB Infraestructura territorial  - Alumbrado Público",
    "rubro": "2.3.2.02.02.005.16.1623606.12002"
  },
  "1623607": {
    "nombreRubro": "RB Movilidad territorial sostenible - Multas de transito",
    "rubro": "2.3.2.02.02.009.16.1623607.12003"
  },
  "1623608": {
    "nombreRubro": "RB Multas y sanciones codigo de policia",
    "rubro": "2.3.2.02.02.009.16.1623608.12006"
  },
  "1623609": {
    "nombreRubro": "RB Servicio de educación informal- SGP Educacion Calidad - Servicios Publicos Instituciones Educativas",
    "rubro": "2.3.2.02.02.006.16.1623609.14001"
  },
  "1623610": {
    "nombreRubro": "RB Programa de Alimentación Escolar PAE - SGP Alimentacion Escolar",
    "rubro": "2.3.3.05.07.013.16.1623610.14101"
  },
  "1623611": {
    "nombreRubro": "RB Gestión Sostenible del Agua - SGP Agua potable y saneamiento basico",
    "rubro": "2.3.2.02.02.005.16.1623611.14201"
  },
  "1623612": {
    "nombreRubro": "RB Educación  artística y cultural- SGP - Proposito General Cultura",
    "rubro": "2.3.2.02.02.009.16.1623612.14401"
  },
  "1623613": {
    "nombreRubro": "RB Infraestructura territorial  -SGP Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.005.16.1623613.14501"
  },
  "1623614": {
    "nombreRubro": "RB Organismos de socorro de prevención y atencion de desastres con apoyo institucional- Sobretasa Bomberil",
    "rubro": "2.3.3.09.16.16.1623614.21007"
  },
  "1623615": {
    "nombreRubro": "RB Programa de Alimentación Escolar PAE  - Departamento",
    "rubro": "2.3.3.05.07.013.16.1623615.31001"
  },
  "1623616": {
    "nombreRubro": "RB Gestión Sostenible del Agua - Transferencias del Sector Electrico",
    "rubro": "2.3.2.02.02.005.16.1623616.61001"
  },
  "1623617": {
    "nombreRubro": "RB Fortalecimiento de la cultura deportiva - Tasa prodeporte y recreación 1,5%",
    "rubro": "2.3.2.02.02.009.16.1623617.13006"
  },
  "1623618": {
    "nombreRubro": "RB Comisarías de familia - Estampilla para la justicia familiar 2%",
    "rubro": "2.3.2.02.02.009.16.1623618.13005"
  },
  "1623619": {
    "nombreRubro": "RB Transferencias para pago nomina  Pensionados - FONPET",
    "rubro": "2.3.3.07.02.001.02.16.1623619.71401"
  },
  "1623620": {
    "nombreRubro": "RB Infraestructura territorial - Retiro por devolución del SGP Propósito General",
    "rubro": "2.3.2.02.02.005.16.1623620.71501"
  },
  "1623621": {
    "nombreRubro": "RB Infraestructura territorial - Aportes Departamentales",
    "rubro": "2.3.2.02.02.005.16.1623621.31401"
  },
  "1623622": {
    "nombreRubro": "RB Incentivo al aprovechamiento y tratamiento de residuos sólidos IAT - Otras Entidades",
    "rubro": "2.3.2.02.02.009.16.1623622.31402"
  },
  "1623623": {
    "nombreRubro": "RB Convenios  - Otras Entidades",
    "rubro": "2.3.2.02.02.009.16.1623623.31402"
  },
  "1623624": {
    "nombreRubro": "RB Infraestructura Vial - Aportes Nacionales INVIAS",
    "rubro": "2.3.2.02.02.005. .16.1623624.41001"
  },
  "1623625": {
    "nombreRubro": "RB Infraestructura espacio público - Cesiones Urbanisticas",
    "rubro": "2.3.2.02.02.005.16.1623625.12011"
  },
  "1623626": {
    "nombreRubro": "RB Equipamento - Cesiones Urbanisticas",
    "rubro": "2.3.2.02.02.005.16.1623626.12011"
  },
  "1623627": {
    "nombreRubro": "RB Fortalecimiento de la cultura deportiva - Ingresos corrientes de libre destinación",
    "rubro": "2.3.2.02.02.009.16.1623627.11001"
  },
  "1623628": {
    "nombreRubro": "RB Infraestructura territorial - ICLD",
    "rubro": "2.3.2.02.02.005.16.1623628.11001"
  },
  "1623629": {
    "nombreRubro": "RB Acceso y permanencia en sistema educativo- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623629.11001"
  },
  "1623630": {
    "nombreRubro": "RB Movilidad territorial sostenible - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623630.11001"
  },
  "1623631": {
    "nombreRubro": "RB Desarrollo agropecuario sostenible- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623631.11001"
  },
  "1623632": {
    "nombreRubro": "RB Apoyos logísticos como componente de los proyectos de inversión - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623632.11001"
  },
  "1623633": {
    "nombreRubro": "RB Programa integral de seguridad - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623633.11001"
  },
  "1623634": {
    "nombreRubro": "RB Innovación Fiscal y Financiera- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623634.11001"
  },
  "1623635": {
    "nombreRubro": "RB Apoyo técnico y profesional a la Dirección de Planeación - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623635.11001"
  },
  "1623636": {
    "nombreRubro": "RB Fortalecimiento de la gestión pública - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623636.11001"
  },
  "1623637": {
    "nombreRubro": "RB Movilidad Innovadora e Inteligente - Multas de transito",
    "rubro": "2.3.2.02.02.009.16.1623637.12003"
  },
  "1623638": {
    "nombreRubro": "RB Infraestructura Deportiva - ICLD",
    "rubro": "2.3.2.02.02.005.16.1623638.11001"
  },
  "1623641": {
    "nombreRubro": "RB Apoyos logísticos como componente de los proyectos de inversión 3.4.2 - ICLD",
    "rubro": "2.3.2.02.02.008.16.1623641.11001"
  },
  "1623642": {
    "nombreRubro": "RB Apoyos logísticos como componente de los proyectos de inversión 1.3.4 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623642.11001"
  },
  "1623643": {
    "nombreRubro": "RB Apoyos logísticos como componente de los proyectos de inversión 3.1.4 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623643.11001"
  },
  "1623644": {
    "nombreRubro": "RB Apoyos logísticos como componente de los proyectos de inversión 3.1.5 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623644.11001"
  },
  "1623645": {
    "nombreRubro": "RB Apoyos logísticos como componente de los proyectos de inversión 3.3.3 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623645.11001"
  },
  "1623646": {
    "nombreRubro": "RB Apoyos logísticos como componente de los proyectos de inversión 3.4.1- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623646.11001"
  },
  "1623647": {
    "nombreRubro": "RB Apoyos logísticos como componente de los proyectos de inversión 3.4.3 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623647.11001"
  },
  "1623648": {
    "nombreRubro": "RB Apoyos logísticos como componente de los proyectos de inversión 3.4.4- ICLD",
    "rubro": "2.3.2.02.02.009.16.1623648.11001"
  },
  "1623649": {
    "nombreRubro": "RB Apoyos logísticos como componente de los proyectos de inversión 4.1.2 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623649.11001"
  },
  "1623650": {
    "nombreRubro": "RB Apoyos logísticos como componente de los proyectos de inversión 4.2.1 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623650.11001"
  },
  "1623651": {
    "nombreRubro": "RB Apoyos logísticos como componente de los proyectos de inversión 4.5.1 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623651.11001"
  },
  "1623652": {
    "nombreRubro": "RB Apoyos logísticos como componente de los proyectos de inversión 1.4.1 - ICLD",
    "rubro": "2.3.2.02.02.009.16.1623652.11001"
  },
  "1623902": {
    "nombreRubro": "VE Gestión Sostenible del Agua - SGP Agua potable y saneamiento basico",
    "rubro": "2.3.2.02.02.005.16.1623902.14201"
  },
  "1623903": {
    "nombreRubro": "VE Convenios - Otras Entidades",
    "rubro": "2.3.2.02.02.005.16.1623903.31403"
  },
  "1623904": {
    "nombreRubro": "VE Gestión Sostenible del Agua - Transferencias del Sector Electrico",
    "rubro": "2.3.2.02.02.005.16.1623904.61001"
  },
  "1623253": {
    "nombreRubro": "Infraestructura Educativa - ICLD",
    "rubro": "2.3.2.02.02.005.16.1623253.11001"
  },
  "1623254": {
    "nombreRubro": "Aporte Area Metropolitana - Fiestas de la Naranja",
    "rubro": "2.3.2.02.02.009.16.1623254.31405"
  },
  "1623255": {
    "nombreRubro": "Aporte IDEA - Fiestas de la Naranja",
    "rubro": "2.3.2.02.02.009.16.1623255.31405"
  },
  "1623250": {
    "nombreRubro": "Infraestructura Territorial - Recursos del crédito",
    "rubro": "2.3.2.02.02.005.16.1623250.51001"
  },

  "2123101": {
    "nombreRubro": "RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN-  SGP ULTIMA DOCEAVA",
    "rubro": "2.3.2.02.02.009.19.01.20.21.2123101.23005"
  },
  "2123102": {
    "nombreRubro": "RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN-  SGP ONCE DOCEAVAS",
    "rubro": "2.3.2.02.02.009.19.01.20.21.2123102.23005"
  },
  "2123103": {
    "nombreRubro": "RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN  - ADRES SSF",
    "rubro": "2.3.2.02.02.009.19.01.20.21.2123103.23001"
  },
  "2123104": {
    "nombreRubro": "RS - E1 GOBERNABILIDAD Y GOBERNANZA - L1 DESARROLLO INSTITUCIONAL Y SECTORIAL - SUPERSALUD",
    "rubro": "2.3.2.02.02.009.19.01.19.21.2123104.23003"
  },
  "2123106": {
    "nombreRubro": "RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN - RENTAS CEDIDAS - DEPARTAMENTO REGIMEN SUBSIDIADO SSF",
    "rubro": "2.3.2.02.02.009.19.01.20.21.2123106.23006"
  },
  "2123107": {
    "nombreRubro": "RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN-  COLJUEGOS 75% SSF",
    "rubro": "2.3.2.02.02.009.19.01.20.21.2123107.23004"
  },
  "2123108": {
    "nombreRubro": "RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN-  INGRESOS CORRIENTES DE LIBRE DESTINACIÓN REGIMEN SUBSIDIADO",
    "rubro": "2.3.2.02.02.009.19.01.20.21.2123108.11001"
  },
  "2123112": {
    "nombreRubro": "PS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - SUBSIDIO A LA OFERTA - EMPRESAS SOCIALES DEL ESTADO- SGP OFERTA PRESTACION DE SERVICIOS ULTIMA DOCEAVA",
    "rubro": "2.3.2.02.02.009.19.03.28.21.2123112.23202"
  },
  "2123113": {
    "nombreRubro": "PS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - SUBSIDIO A LA OFERTA - EMPRESAS SOCIALES DEL ESTADO- SGP OFERTA PRESTACION DE SERVICIOS ONCE DOCEAVA",
    "rubro": "2.3.2.02.02.009.19.03.28.21.2123113.23202"
  },
  "2123114": {
    "nombreRubro": "PS - E2 PUEBLOS Y COMUNIDADES - L3 CUIDADO DE LA SALUD - ATENCIÓN POBLACIÓN MIGRANTE INSTITUCIONES PÚBLICAS- INGRESOS CORRIENTES DE LIBRE DESTINACIÓN - PRESTACIÓN DE SERVICIOS - OFERTA",
    "rubro": "2.3.2.02.02.009.19.03.28.21.2123114.11001"
  },
  "2123117": {
    "nombreRubro": "SPC - E2 Pueblos y comunidades - L3 Cuidado de la Salud - Garantía del derecho a la salud de pueblos y comunidades étnicas y campesinas, mujeres, sectores LGBTIQ+ y otras poblaciones-ICLD 60%",
    "rubro": "2.3.2.02.02.009.19.02.99.21.2123117.11001"
  },
  "2123118": {
    "nombreRubro": "SPC - E3 Determinantes - L3 Cuidado de la Salud - Información en salud para la afectación positiva de los determinantes sociales de la salud en el territorio- ICLD 60%",
    "rubro": "2.3.2.02.02.009.19.02.103.21.2123118.11001"
  },
  "2123119": {
    "nombreRubro": "SPC - E4 Atención Primaria Salud - L3 Cuidado de la Salud - Cuidado de la Salud - familias, colectivos y comunidades- ICLD 60%",
    "rubro": "2.3.2.02.02.009.19.02.107.21.2123119.11001"
  },
  "2123120": {
    "nombreRubro": "SPC - E4 Atención Primaria Salud - L1 Desarrollo Institucional y Sectorial - Desarrollo de capacidades en salud pública para la implementación de la estrategia de Atención Primaria en Salud- ICLD 60%",
    "rubro": "2.3.2.02.02.009.19.02.104.21.2123120.11001"
  },
  "2123121": {
    "nombreRubro": "SPC - E5 Cambio Climático - L3 Cuidado de la Salud - Respuesta en salud pública ante los efectos en la salud generados por la variabilidad y el cambio climático y la ocurrencia de emergencias- ICLD 60%",
    "rubro": "2.3.2.02.02.009.19.02.118.21.2123121.11001"
  },
  "2123122": {
    "nombreRubro": "SPC - E7 Personal Salud - L3 Cuidado de la Salud - Cuidado de la salud en el entorno laboral- ICLD 60%",
    "rubro": "2.3.2.02.02.009.19.02.127.21.2123122.11001"
  },
  "2123123": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L1 Desarrollo Institucional y Sectorial - Desarrollo de  capacidades para la Gobernanza en Salud Pública- ICLD 40%",
    "rubro": "2.3.2.02.02.009.19.02.91.21.2123123.11001"
  },
  "2123124": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L1 Desarrollo Institucional y Sectorial - Inspeccion, vigilancia y control sanitarios- ICLD 40%",
    "rubro": "2.3.2.02.02.009.19.02.92.21.2123124.11001"
  },
  "2123125": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L2 Acuerdos Sociales y Comunitarios - Participación social con enfoque diferencial para la gobernanza en salud pública- ICLD 40%",
    "rubro": "2.3.2.02.02.009.19.02.93.21.2123125.11001"
  },
  "2123126": {
    "nombreRubro": "SPC - E2 Pueblos y comunidades - L3 Cuidado de la Salud - Garantía del derecho a la salud de pueblos y comunidades étnicas y campesinas, mujeres, sectores LGBTIQ+ y otras poblaciones -SGP 60% Once Doceavas",
    "rubro": "2.3.2.02.02.009.19.02.99.21.2123126.23101"
  },
  "2123127": {
    "nombreRubro": "SPC - E3 Determinantes - L3 Cuidado de la Salud - Información en salud para la afectación positiva de los determinantes sociales de la salud en el territorio- SGP 60% Once Doceavas",
    "rubro": "2.3.2.02.02.009.19.02.103.21.2123127.23101"
  },
  "2123128": {
    "nombreRubro": "SPC - E4 Atención Primaria Salud - L3 Cuidado de la Salud - Cuidado de la Salud - familias, colectivos y comunidades- SGP 60%Once Doceavas",
    "rubro": "2.3.2.02.02.009.19.02.107.21.2123128.23101"
  },
  "2123129": {
    "nombreRubro": "SPC - E4 Atención Primaria Salud - L1 Desarrollo Institucional y Sectorial - Desarrollo de capacidades en salud pública para la implementación de la estrategia de Atención Primaria en Salud- SPG 60% O",
    "rubro": "2.3.2.02.02.009.19.02.104.21.2123129.23101"
  },
  "2123130": {
    "nombreRubro": "SPC - E4 Atención Primaria Salud - L1 Desarrollo Institucional y Sectorial - Desarrollo de capacidades en salud pública para la implementación de la estrategia de Atención Primaria en Salud- SPG 60% U",
    "rubro": "2.3.2.02.02.009.19.02.104.21.2123130.23101"
  },
  "2123131": {
    "nombreRubro": "SPC - E5 Cambio Climático - L3 Cuidado de la Salud - Respuesta en salud pública ante los efectos en la salud generados por la variabilidad y el cambio climático y la ocurrencia de emergencias - SGP Once Doceavas 60%",
    "rubro": "2.3.2.02.02.009.19.02.118.21.2123131.23101"
  },
  "2123132": {
    "nombreRubro": "SPC - E7 Personal Salud - L3 Cuidado de la Salud - Cuidado de la salud en el entorno laboral- SGP 60% Once Doceavas",
    "rubro": "2.3.2.02.02.009.19.02.127.21.2123132.23101"
  },
  "2123133": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L1 Desarrollo Institucional y Sectorial - Vigilancia en Salud Pública y fortalecimiento de los laboratorios en salud pública- SGP 40% Doce Doceavas",
    "rubro": "2.3.2.02.02.009.19.02.91.21.2123133.23101"
  },
  "2123134": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L1 Desarrollo Institucional y Sectorial - Inspección, vigilancia y control sanitarios- SGP 40% Doce Doceavas",
    "rubro": "2.3.2.02.02.009.19.02.92.21.2123134.23101"
  },
  "2123135": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L2 Acuerdos Sociales y Comunitarios - Participación social con enfoque diferencial para la gobernanza en salud pública- SGP 40%",
    "rubro": "2.3.2.02.02.009.19.02.93.21.2123135.23101"
  },
  "2123136": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L3 Cuidado de la Salud - Gestión de las políticas públicas en salud- SGP 40% Doce Doceavas",
    "rubro": "2.3.2.02.02.009.19.02.95.21.2123136.23101"
  },
  "2123137": {
    "nombreRubro": "SPC - E1 Gobernabilidad y Gobernanza - L3 Cuidado de la Salud - Gestión de las políticas públicas en salud- SGP 40% Ultima Doceava",
    "rubro": "2.3.2.02.02.009.19.02.95.21.2123137.23101"
  },
  "2123140": {
    "nombreRubro": "OGS - FUNCIONAMIENTO",
    "rubro": "2.3.2.02.02.009.19.05.58.21.2123140.23301"
  },
  "2123143": {
    "nombreRubro": "RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN  - ADRES SSF - RENDIMIENTOS FINANCIEROS",
    "rubro": "2.3.2.02.02.009.16.2123143.23001"
  },
  "2123182": {
    "nombreRubro": "Fortalecimiento y protección integral del adulto mayor - ICLD",
    "rubro": "2.3.2.02.02.009.21.2123182.11001"
  },
  "2123183": {
    "nombreRubro": "Fortalecimiento y protección integral del adulto mayor - Estampilla Pro Bienestar del Anciano 30%",
    "rubro": "2.3.2.02.02.009.21.2123183.13001"
  },
  "2123184": {
    "nombreRubro": "Fortalecimiento y protección integral del adulto mayor- SGP  Proposito General Libre Inversion",
    "rubro": "2.3.2.02.02.009.21.2123184.14501"
  },
  "2123185": {
    "nombreRubro": "Fortalecimiento y protección integral del adulto mayor - Estampilla Pro Bienestar del Anciano 70%",
    "rubro": "2.3.2.02.02.009.21.2123185.13001"
  },
  "2123186": {
    "nombreRubro": "SPC-E6 Conocimiento salud - L3 cuidado de la salud - Acciones estrategicas de información en salud - ICLD 60%",
    "rubro": "2.3.2.02.02.009.19.02.123.21.2123186.11001"
  },
  "2123206": {
    "nombreRubro": "Mejoramiento de la infraestructura de atención en salud- ESE ICLD",
    "rubro": "2.3.3.01.02.005.21.2123206.11001"
  },
  "2123601": {
    "nombreRubro": "RB Fortalecimiento y protección integral del adulto mayor - Estampilla Pro Bienestar del Anciano 70%",
    "rubro": "2.3.2.02.02.009.21.2123601.13001"
  },
  "2123602": {
    "nombreRubro": "RB Fortalecimiento y protección integral del adulto mayor - Estampilla Pro Bienestar del Anciano 30%",
    "rubro": "2.3.2.02.02.009.21.2123602.13001"
  },
  "2123603": {
    "nombreRubro": "RB Desarrollo integral de la primera infancia - SGP Primera Infancia",
    "rubro": "2.3.2.02.02.009.21.2123603.14701"
  },
  "2123604": {
    "nombreRubro": "RB Fortalecimiento y protección integral del adulto mayor - ICLD",
    "rubro": "2.3.2.02.02.009.21.2123604.11001"
  },
  "2123605": {
    "nombreRubro": "RB RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN - EXISTENCIA CAJA Y BANCOS RS",
    "rubro": "2.3.2.02.02.009.21.2123605.23005"
  },
  "2123606": {
    "nombreRubro": "RB RS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - TRANSFERENCIAS AFILIACIÓN - RENDIMIENTOS FINANCIEROS RS",
    "rubro": "2.3.2.02.02.009.21.2123606.23005"
  },
  "2123607": {
    "nombreRubro": "RB SPC - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - CUIDADO DE LA SALUD - FAMILIAS, COLECTIVOS Y COMUNIDADES- SGP 60% EXISTENCIAS CAJA Y BANCOS",
    "rubro": "2.3.2.02.02.009.19.02.107.21.2123607.23101"
  },
  "2123608": {
    "nombreRubro": "RB SPC - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - CUIDADO DE LA SALUD - FAMILIAS, COLECTIVOS Y COMUNIDADES- SGP 60% RENDIMIENTOS FINANCIEROS",
    "rubro": "2.3.2.02.02.009.19.02.107.16.2123608.23101"
  },
  "2123609": {
    "nombreRubro": "RB SPC - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - CUIDADO DE LA SALUD - FAMILIAS, COLECTIVOS Y COMUNIDADES- SGP 40% EXISTENCIAS CAJA Y BANCOS",
    "rubro": "2.3.2.02.02.009.19.02.107.21.2123609.23101"
  },
  "2123610": {
    "nombreRubro": "RB SPC - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - CUIDADO DE LA SALUD - FAMILIAS, COLECTIVOS Y COMUNIDADES- SGP 40% RENDIMIENTOS FINANCIEROS",
    "rubro": "2.3.2.02.02.009.19.02.107.21.2123610.23101"
  },
  "2123611": {
    "nombreRubro": "RB PS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - SUBSIDIO A LA OFERTA - EMPRESAS SOCIALES DEL ESTADO- EXISTENCIA CAJA Y BANCOS",
    "rubro": "2.3.2.02.02.009.21.2123611.23202"
  },
  "2123612": {
    "nombreRubro": "RB PS - E4 ATENCIÓN PRIMARIA SALUD - L3 CUIDADO DE LA SALUD - SUBSIDIO A LA OFERTA - EMPRESAS SOCIALES DEL ESTADO- RENDIMIENTOS FINANCIEROS SGP",
    "rubro": "2.3.2.02.02.009.21.2123612.23202"
  },
  "2123613": {
    "nombreRubro": "RB OGS - FUNCIONAMIENTO- EXISTENCIA CAJA Y BANCOS",
    "rubro": "2.3.2.02.02.009.21.2123613.23301"
  },
  "2123614": {
    "nombreRubro": "RB OGS - FUNCIONAMIENTO- RENDIMIENTOS FINANCIEROS",
    "rubro": "2.3.2.02.02.009.21.2123614.23301"
  },
  "2123615": {
    "nombreRubro": "RB OGS- E2 PUEBLOS Y COMUNIDADES - L3 CUIDADO DE LA SALUD - ATENCIÓN INTEGRAL CENTROS VIDA O DÍA ADULTO MAYOR/PERSONAS MAYORES- EXISTENCIA CAJA Y BANCOS",
    "rubro": "2.3.2.02.02.009.21.2123615.23302"
  },
  "2123616": {
    "nombreRubro": "RB OGS - E2 PUEBLOS Y   COMUNIDADES - L3 CUIDADO DE LA SALUD - ATENCIÓN INTEGRAL CENTROS VIDA O DÍA ADULTO MAYOR/PERSONAS MAYORES- RENDIMIENTOS FINANCIEROS",
    "rubro": "2.3.2.02.02.009.21.2123616.23302"
  },
  "2123618": {
    "nombreRubro": "OGS- INVERSION- Diferentes  a los contemplados en las demás subcuentas - RB ICLD",
    "rubro": "2.3.2.02.02.009.19.05.57.21.2123618.11001"
  },
  "2123617": {
    "nombreRubro": "SPC-E1  Gobernabilidad y Gobernanza- L1 Desarrollo Institucional y Sectorial- Vigilancia en Salud Pública y fortalecimiento de los laboratorios en Salud Pública- RB ICLD 40%",
    "rubro": "2.3.2.02.02.009.19.02.91.21.2123617.11001"
  },
  "2123187": {
    "nombreRubro": "SPC-E1 Gobernabilidad y Gobernanza -L1 Desarrollo Institucional y Sectorial - Vigilancia en Salud Pública y fortalecimiento de los laboratorios en salud pública - ICLD 40%",
    "rubro": "2.3.2.02.02.009.19.02.91.21.2123187.11001"
  },
  "VF-196-11001": {
    "nombreRubro": "VIGENCIA FUTURA ACUERDO 196 INGRESOS CORRIENTES DE LIBRE DESTINACION ",
    "rubro": "VF-196-11001"
  },
  "VF-196-31001": {
    "nombreRubro": "VIGENCIA FUTURA ACUERDO 196 PROGRAMA PAE ",
    "rubro": "VF-196-31001"
  },
  "VF-196-12002": {
    "nombreRubro": "VIGENCIA FUTURA ACUERDO 196 ALUMBRADO PÚBLICO ",
    "rubro": "VF-196-12002"
  },
  "VF-196-14501": {
    "nombreRubro": "VIGENCIA FUTURA ACUERDO 196 SGP PROPOSTIO GENERAL LIBRE INVERSION ",
    "rubro": "VF-196-14501"
  },
  "VF-196-51001": {
    "nombreRubro": "VIGENCIA FUTURA ACUERDO 196 RECURSOS DEL CREDITO ",
    "rubro": "VF-196-51001"
  },
  "VF-196-23101": {
    "nombreRubro": "VIGENCIA FUTURA ACUERDO 196 SGP SALUD PÚBLICA ",
    "rubro": "VF-196-23101"
  },
  "VF-196-23101": {
    "nombreRubro": "VIGENCIA FUTURA ACUERDO 196 SGP SALUD PÚBLICA ",
    "rubro": "VF-196-23101"
  },
  "VF-196-13006": {
    "nombreRubro": "VIGENCIA FUTURA ACUERDO 196 TASA PRO-DEPORTE Y RECREACIÓN 1.5% ",
    "rubro": "VF-196-13006"
  },
  "VF-196-14301": {
    "nombreRubro": "VIGENCIA FUTURA ACUERDO SGP PROPOSITO GENERAL DEPORTES ",
    "rubro": "VF-196-14301"
  },
  "VF-EX-196-11001": {
    "nombreRubro": "VIGENCIA FUTURA EXCEPCIONAL ACUERDO 196 ICLD ",
    "rubro": "VF-EX-196-11001"
  },
  "VF-EX-196-13001": {
    "nombreRubro": "VIGENCIA FUTURA EXCEPCIONAL ACUERDO 196 ESTAMPILLA PRO BIENESTAR DEL ANCIANO 30% ",
    "rubro": "VF-EX-196-13001"
  },
  "VF-EX-196-23101": {
    "nombreRubro": "VIGENCIA FUTURA EXCEPCIONAL ACUERDO 196 SGP SALUD PÚBLICA ",
    "rubro": "VF-EX-196-23101"
  },
  "VF-EX-196-12002": {
    "nombreRubro": "VIGENCIA FUTURA EXCEPCIONAL ACUERDO 196 ALUMBRADO PÚBLICO",
    "rubro": "VF-EX-196-12002"
  },
  "VF-EX-196-23202": {
    "nombreRubro": "VIGENCIA FUTURA EXCEPCIONAL ACUERDO 196 SGP OFERTA PRESTACIÓN DE SERVICIOS",
    "rubro": "VF-EX-196-23202"
  },
  "VF-197-11001": {
    "nombreRubro": "VIGENCIA FUTURA  ACUERDO 197 INGRESOS CORRIENTES DE LIBRE DESTINACIÓN",
    "rubro": "VF-197-11001"
  },
  "VF-197-51001": {
    "nombreRubro": "VIGENCIA FUTURA ACUERDO 196 RECURSOS DEL CREDITO ",
    "rubro": "VF-197-51001"
  },

  "1623261": {
    "nombreRubro": "Desarrollo Territorial- ICLD. ",
    "rubro": "2.3.2.02.02.009.16.1623261.11001"
  },

   "231621140": {
    "nombreRubro": "Salud Ocupacional",
    "rubro": "2.1.3.07.02.031.23.231621140.11001"
   },








};



const centrosCostosRubros = {
  // "05001": ["1623116", "1623117", "1623124", "1623125", "1623126", "1623127", "1623128", "1623134", "1623171", "1623207", "1623208", "1623218"],
  // "06001": ["1623176", "1623177", "1623179", "1623180", "1623209", "1623222", "1623602"],
  // "06003": ["1623175"],
  // "06004": ["1623178", "1623608"],
  // "06005": ["1623109", "1623150", "1623151", "1623224"],
  // "07001": ["1623121", "1623158", "1623159", "1623160", "1623161", "1623162", "1623166", "1623167", "1623168", "1623169", "1623170", "1623172", "1623181", "1623186", "1623187", "1623188", "1623189", "1623192", "1623198"],
  // "07002": ["1623173", "1623174"],
  // "07003": ["1623120", "1623165"],
  // "07004": ["1623185"],
  // "07005": ["1623190", "1623191"],
  // "08001": ["1623123", "1623152", "1623157", "1623194", "1623195"],
  //"09001": ["1623107", "1623108", "1623110", "1623111", "1623112", "1623113", "1623114", "1623115", "1623118", "1623201", "1623202", "1623205", "1623210", "1623902", "1623904"],
  //"10001": ["1623193", "1623225", "1623226", "1623227", "1623228", "1623229", "1623230", "1623601", "1623603", "1623604", "1623605", "1623606", "1623607", "1623609", "1623610", "1623611", "1623612", "1623613", "1623614", "1623615", "1623616", "1623617", "1623618", "1623619", "1623620", "1623621", "1623622", "1623623", "1623624", "1623625", "1623626", "1623627", "1623628", "1623629", "1623630", "1623631", "1623632", "1623633", "1623634", "1623635", "1623636", "1623903", "2123601", "2123602", "2123603", "2123604", "2123607", "2123608", "2123609", "2123610", "2123611", "2123612", "2123613", "2123614", "2123615", "2123616"],
  // "11001": ["1623102", "1623119", "1623122", "1623196", "1623197", "1623200"],
  //"12001": ["2123206"],
  // "12002": ["2123182", "2123183", "2123184", "2123185"],
  // "12003": ["2123101", "2123102", "2123103", "2123104", "2123106", "2123107", "2123108", "2123112", "2123113", "2123114", "2123117", "2123118", "2123119", "2123120", "2123121", "2123122", "2123123", "2123124", "2123125", "2123126", "2123127", "2123128", "2123129", "2123130", "2123131", "2123132", "2123133", "2123134", "2123135", "2123136", "2123137", "2123140", "2123143", "2123605", "2123606"],
  // "14001": ["1623106", "1623129", "1623130", "1623131", "1623132", "1623133", "1623223"],
  // "15001": ["1623104", "1623154", "1623155", "1623156"],
  // "16001": ["1623153"],
  // "17001": ["1623135", "1623136", "1623137", "1623138", "1623139", "1623140", "1623141", "1623213"],
  // "18001": ["1623105", "1623142", "1623143", "1623144", "1623145", "1623146", "1623147", "1623148", "1623149", "1623163", "1623164", "1623203", "1623204", "1623219", "1623220", "1623221"],
  // "21001": ["1623101", "1623103", "1623199"]
  // prueba 
};




const formatDateForInput = (value) => {
  if (!value) return "";

  const s = String(value).trim();

  // 1) Si ya viene como "YYYY-MM-DD", devuélvelo tal cual (cero riesgo de TZ)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // 2) Si viene como ISO "YYYY-MM-DDTHH:mm:ss...", toma solo la parte de fecha
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  // 3) Si viene en formato "DD/MM/YYYY" (por si acaso)
  const dmY = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmY) return `${dmY[3]}-${dmY[2]}-${dmY[1]}`;

  // 4) Último recurso: parsear Date pero leyendo en UTC para evitar corrimiento
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";

  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};




export default function ContractForm() {
  const { id: contractId } = useParams();
  const navigate = useNavigate();
  const windowSize = useWindowSize();

  // 🔐 Datos de autenticación desde el contexto
  const { user, userRole: rawUserRole, loading: authLoading, setUser, setUserRole, } = useAuth();
  const userRole = normalizeRole(rawUserRole);

 const appRole = (() => {
  const raw =
    (userRole ||
      user?.rol ||
      user?.role ||
      user?.role_name ||
      "") + "";

  const txt = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // quita tildes

  const id = user?.rolId || user?.role_id || user?.roleId;

  if (id === 1 || txt.includes("solicit")) return "solicitante";
  if (id === 2 || txt.includes("planea")) return "planeacion";
  if (id === 3 || txt.includes("hacienda")) return "hacienda";
  if (id === 4 || txt.includes("jurid")) return "juridica";
  if (id === 5 || txt.includes("alcald")) return "alcalde";
  if (id === 6 || txt.includes("admin")) return "administrador";
  if (id === 7 || txt.includes("presupuesto")) return "presupuesto";
  if (id === 8 || txt.includes("almacen")) return "almacen";
  if (id === 11 || txt.includes("administrador_junior")) return "administrador_junior";
  if (id === 12 || txt.includes("abogado")) return "abogado";
  if (id === 14 || txt.includes("sst")) return "sst";  // ← NUEVO


  return "solicitante";
})();

  // ✅ Whitelist opcional (separa correos por coma en .env: VITE_AUTHORIZED_EMAILS="a@b.com,c@d.com")
  const AUTHORIZED_EMAILS = (import.meta?.env?.VITE_AUTHORIZED_EMAILS || "")
    .split(",")
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);

  // ✅ Defínela UNA sola vez en el componente (la usas 3 veces)
  const isAuthorizedUser = (email) => {
    const normalized = String(email || "").trim().toLowerCase();
    if (!normalized) return false;

    // Si no configuraste whitelist, usamos control por rol
    if (AUTHORIZED_EMAILS.length === 0) {
      return appRole === "alcalde" || appRole === "administrador";
    }

    return AUTHORIZED_EMAILS.includes(normalized);
  };




  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  const [showCdpForm, setShowCdpForm] = useState(false);
  const [showRpForm, setShowRpForm] = useState(false);
  const [showAdquisicionForm, setShowAdquisicionForm] = useState(false);

  const [showEnviarCdpModal, setShowEnviarCdpModal] = useState(false);
  const [showEnviarRpModal, setShowEnviarRpModal] = useState(false);

  const [pdfGenerating, setPdfGenerating] = useState(false);

  const [rubroSelections, setRubroSelections] = useState([]);
  const [programaSelections, setProgramaSelections] = useState([]);
  const [indicadorSelections, setIndicadorSelections] = useState([]);
  const [bpinSelections, setBpinSelections] = useState([]);

  const [cdpFormularios, setCdpFormularios] = useState([]);
  const [rpFormularios, setRpFormularios] = useState([]);
  const [showSolicitudesCDP, setShowSolicitudesCDP] = useState(false);


  const [selectedAlmacenRequest, setSelectedAlmacenRequest] = useState(null);
  const [indicadorPresupuestoSelections, setIndicadorPresupuestoSelections] = useState([]);

  const [showAnulacionModal, setShowAnulacionModal] = useState(false);
  const [anulacionReason, setAnulacionReason] = useState("");

  const [showAnularCdpModal, setShowAnularCdpModal] = useState(false);
  const [anularCdpNumero, setAnularCdpNumero] = useState("");
  const [anularCdpFile, setAnularCdpFile] = useState(null);
  const [showEnviarArlModal, setShowEnviarArlModal] = useState(false)
  const [arlAprobado, setArlAprobado] = useState(false)



  const [presupuestoModal, setPresupuestoModal] = useState({
    isOpen: false,
    mode: null,
    requestType: null,
    existingRequest: null,
    cdpFormulario: null,
    rpFormulario: null,
  });

  const generateCdpButton = {
    background: "linear-gradient(45deg, #d4a373, #e0ac69)",
    color: "#000",
    border: "none",
    padding: "10px 20px",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background 0.3s ease",
  };

  const generateRpButton = {
    background: "linear-gradient(45deg, #d4a373, #e0ac69)",
    color: "#000",
    border: "none",
    padding: "10px 20px",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background 0.3s ease",
  };

  const enviarRpButton = {
    background: "linear-gradient(45deg, #d4a373, #e0ac69)",
    color: "#000",
    border: "none",
    padding: "10px 20px",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background 0.3s ease",
  };

  const enviarCdpButton = {
    background: "linear-gradient(45deg, #d4a373, #e0ac69)",
    color: "#000",
    border: "none",
    padding: "10px 20px",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background 0.3s ease",
  };

  const [contract, setContract] = useState({
    fechaSolicitud: "",
    centroCostos: "",
    secretaria: "",
    tipoContrato: "",
    modalidadSeleccion: "",
    objetoContractual: "",
    duracionEstimada: "",
    fechaInicioContrato: "",
    fechaFinContrato: "",
    nombreContratista: "",
    programa: [],
    programa_aprobado: false,
    indicador: [],
    indicador_aprobado: false,
    bpin: [],
    bpin_aprobado: false,
    naturalezaGasto: "",
    presupuestoTotal: "",
    discriminacionRubros: [],
    rubros: [],
    nombresRubros: [],
    valores: [],
    formaPago: "",
    nombreSolicitante: "",
    cargoSolicitante: "",
    firmaSolicitante: "",
    aprobacion_administrativa: "",
    firmaAdministrativa: "",
    aprobacion_hacienda: "",
    firmaHacienda: "",
    // 👇 usamos solo esta forma para el alcalde
    aprobacion_alcalde: "",
    firma_alcalde: "",
    numeroActa: "",
    fechaComite: "",
    observaciones: "",
    radicado: "",
    current_phase: 1,
    status: "en_proceso",
    // Nuevos campos para Hacienda
    hay_dinero_rubro: null,
    se_puede_ejecutar: null,
    observaciones_hacienda: "",
    motivo_rechazo: "",
    // Campos para Jurídica
    tipo_contrato_aprobado: false,
    modalidad_seleccion_aprobada: false,
    objeto_contractual_aprobado: false,
    duracion_estimada_aprobada: false,
    presupuesto_aprobado: false,
    observaciones_juridica: "",
    // Campos para Alcalde
    firma_solicitante_url: "",
    firma_administrativa_url: "",
    firma_hacienda_url: "",
    firma_alcalde_url: "",
    bancop_url: "",
    bancop_filename: "",
  });

  const normalizedData = contract || {};




  const handleBancopUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Si el contrato es nuevo, guardamos el archivo en el estado local 
    // para que se envíe después en la función de guardado general.
    if (!contractId || contractId === "new" || contractId === "nuevo") {
      setContract((prev) => ({
        ...prev,
        bancop_file: file, // Guardamos el objeto archivo completo
        bancop_filename: file.name,
      }));
      console.log("Archivo Bancop seleccionado localmente (Contrato nuevo).");
      return;
    }

    // Si el contrato YA existe (tiene ID), se sube inmediatamente al servidor
    try {
      const token = getToken(); // JWT

      const formData = new FormData();
      formData.append("bancopFile", file);
      formData.append("contractId", contractId);

      const response = await fetch(`/api/upload/bancop/${contractId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = `Error al subir el archivo (código ${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          const text = await response.text().catch(() => "");
          if (text) console.error("Respuesta de error del servidor:", text);
        }
        throw new Error(errorMessage);
      }

      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error("El servidor no devolvió un JSON válido al subir el archivo Bancop.");
      }

      const { bancop_url, bancop_filename } = data;

      setContract((prev) => ({
        ...prev,
        bancop_url,
        bancop_filename: bancop_filename || file.name,
      }));

      alert("Archivo Bancop subido correctamente.");
    } catch (error) {
      console.error("Error al procesar archivo:", error);
      alert("Error al procesar el archivo: " + error.message);
    }
  };

  // Función para enviar notificaciones cuando los contratos cambian de fase.
  // Asegúrate de que esta función auxiliar esté disponible


  const getAuthToken = () =>
    localStorage.getItem("auth_token") ||     // ✅ PRODUCCIÓN (tu caso)
    localStorage.getItem("authToken") ||      // compat
    localStorage.getItem("auth token") ||     // compat legacy
    "";

  // ✅ Función corregida y completa
  // ✅ Notificaciones desactivadas temporalmente
  // - Evita el 404 de /api/notifications/send
  // - No rompe el flujo del contrato (creación/edición/guardado)
  // - Cuando vuelvas a activar correos, solo restauras el fetch

  const sendContractNotification = async (
    phase,
    contractData,
    action = "notification"
  ) => {
    // Puedes dejar este log si quieres ver cuándo se intentaría notificar
    console.log(
      `[NOTIF DESHABILITADA] Se omitió notificación. phase=${phase}, action=${action}`,
      {
        radicado: contractData?.radicado,
        secretaria: contractData?.secretaria,
        objeto_contractual: contractData?.objeto_contractual,
        nombre_contratista: contractData?.nombre_contratista,
        presupuesto_total: contractData?.presupuesto_total,
      }
    );

    // Devolvemos un resultado consistente para que el resto del código no falle
    return {
      success: true,
      skipped: true,
      message: "Notificaciones desactivadas temporalmente (endpoint/correos pendientes).",
    };
  };



  const fetchContract = useCallback(async (contractId) => {
    setLoading(true);
    setError("");
    try {
      const url = apiConfig.getUrl(
        `${apiConfig.endpoints.contracts}/${contractId}`
      );
      console.log("🔗 Fetch contrato desde:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = "Error al cargar el contrato";
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch { }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (!data) throw new Error("Contrato no encontrado.");

      // ✅ CORRECCIÓN: Buscar el contrato correcto por ID
      let contractData;

      if (data.contracts && Array.isArray(data.contracts)) {
        // Si viene array, buscar por ID
        contractData = data.contracts.find(c => c.id === contractId);

        if (!contractData) {
          throw new Error(`Contrato ${contractId} no encontrado en la respuesta`);
        }
      } else if (data.contract) {
        contractData = data.contract;
      } else {
        contractData = data;
      }

      console.log("✅ Contrato encontrado:", contractData.id, contractData.radicado);

      // 🔍 LOG: Ver datos RAW de la BD
      console.log("📥 DATOS RAW DE LA BD:", contractData);

      // Formatear fechas
      const fechaSolicitudFormatted = toInputDate(contractData.fecha_solicitud);
      const fechaComiteFormatted = toInputDate(contractData.fecha_comite);
      const fechaInicioContratoFormatted = toInputDate(contractData.fecha_inicio_contrato);
      const fechaFinContratoFormatted = toInputDate(contractData.fecha_fin_contrato);

      // 🔍 LOG: Ver valores booleanos RAW antes de convertir
      console.log("🔥 VALORES BOOLEANOS CARGADOS DE LA BD (RAW):", {
        programa_aprobado_raw: contractData.programa_aprobado,
        programa_aprobado_tipo: typeof contractData.programa_aprobado,
        indicador_aprobado_raw: contractData.indicador_aprobado,
        indicador_aprobado_tipo: typeof contractData.indicador_aprobado,
        bpin_aprobado_raw: contractData.bpin_aprobado,
        bpin_aprobado_tipo: typeof contractData.bpin_aprobado,
      });

      // ✅ Convertir explícitamente a booleanos (acepta: true, 1, "1", "true")
      const programa_aprobado =
        contractData.programa_aprobado === true ||
        contractData.programa_aprobado === 1 ||
        contractData.programa_aprobado === "1" ||
        contractData.programa_aprobado === "true";

      const indicador_aprobado =
        contractData.indicador_aprobado === true ||
        contractData.indicador_aprobado === 1 ||
        contractData.indicador_aprobado === "1" ||
        contractData.indicador_aprobado === "true";

      const bpin_aprobado =
        contractData.bpin_aprobado === true ||
        contractData.bpin_aprobado === 1 ||
        contractData.bpin_aprobado === "1" ||
        contractData.bpin_aprobado === "true";

      // ✅ Lo mismo para Hacienda
      const hay_dinero_rubro =
        contractData.hay_dinero_rubro === true ||
        contractData.hay_dinero_rubro === 1 ||
        contractData.hay_dinero_rubro === "1" ||
        contractData.hay_dinero_rubro === "true";

      const se_puede_ejecutar =
        contractData.se_puede_ejecutar === true ||
        contractData.se_puede_ejecutar === 1 ||
        contractData.se_puede_ejecutar === "1" ||
        contractData.se_puede_ejecutar === "true";

      // ✅ Lo mismo para Jurídica
      const tipo_contrato_aprobado =
        contractData.tipo_contrato_aprobado === true ||
        contractData.tipo_contrato_aprobado === 1 ||
        contractData.tipo_contrato_aprobado === "1" ||
        contractData.tipo_contrato_aprobado === "true";

      const modalidad_seleccion_aprobada =
        contractData.modalidad_seleccion_aprobada === true ||
        contractData.modalidad_seleccion_aprobada === 1 ||
        contractData.modalidad_seleccion_aprobada === "1" ||
        contractData.modalidad_seleccion_aprobada === "true";

      const objeto_contractual_aprobado =
        contractData.objeto_contractual_aprobado === true ||
        contractData.objeto_contractual_aprobado === 1 ||
        contractData.objeto_contractual_aprobado === "1" ||
        contractData.objeto_contractual_aprobado === "true";

      const duracion_estimada_aprobada =
        contractData.duracion_estimada_aprobada === true ||
        contractData.duracion_estimada_aprobada === 1 ||
        contractData.duracion_estimada_aprobada === "1" ||
        contractData.duracion_estimada_aprobada === "true";

      const presupuesto_aprobado =
        contractData.presupuesto_aprobado === true ||
        contractData.presupuesto_aprobado === 1 ||
        contractData.presupuesto_aprobado === "1" ||
        contractData.presupuesto_aprobado === "true";

      // 🔍 LOG: Ver valores booleanos DESPUÉS de convertir
      console.log("✅ VALORES BOOLEANOS CONVERTIDOS:", {
        programa_aprobado,
        indicador_aprobado,
        bpin_aprobado,
        hay_dinero_rubro,
        se_puede_ejecutar,
      });

      const normalizedData = {
        id: contractData?.id ?? "",
        created_at: contractData?.created_at ?? null,
        updated_at: contractData?.updated_at ?? null,
        current_phase: contractData?.current_phase ?? "",
        status: contractData?.status ?? "",
        radicado: contractData?.radicado ?? "",

        fechaSolicitud: fechaSolicitudFormatted,
        fechaComite: fechaComiteFormatted,
        fechaInicioContrato: fechaInicioContratoFormatted,
        fechaFinContrato: fechaFinContratoFormatted,
        numeroActa: contractData?.numero_acta ?? "",

        centroCostos: contractData?.centro_costos ?? "",
        tipoContrato: contractData?.tipo_contrato ?? "",
        secretaria: contractData?.secretaria ?? "",
        modalidadSeleccion: contractData?.modalidad_seleccion ?? "",
        objetoContractual: contractData?.objeto_contractual ?? "",
        duracionEstimada: contractData?.duracion_estimada ?? "",
        nombreContratista: contractData?.nombre_contratista ?? "",
        naturalezaGasto: contractData?.naturaleza_gasto ?? "",
        presupuestoTotal: contractData?.presupuesto_total ?? "",
        discriminacionRubros: contractData?.discriminacion_rubros ?? "",
        formaPago: contractData?.forma_pago ?? "",
        nombreSolicitante: contractData?.nombre_solicitante ?? "",
        cargoSolicitante: contractData?.cargo_solicitante ?? "",

        programa: contractData?.programa ? String(contractData.programa).split(",") : [],
        indicador: contractData?.indicador ? String(contractData.indicador).split(",") : [],
        bpin: contractData?.bpin ? String(contractData.bpin).split(",") : [],
        rubros: contractData?.rubro ? String(contractData.rubro).split("||") : [],
        nombresRubros: contractData?.nombre_rubro ? String(contractData.nombre_rubro).split("||") : [],
        valores: contractData?.valor ? String(contractData.valor).split("||") : [],

        // URLs de firmas (desde BD)
        firma_solicitante_url: contractData?.firma_solicitante_url || "",
        firma_administrativa_url: contractData?.firma_administrativa_url || "",
        firma_hacienda_url: contractData?.firma_hacienda_url || "",
        firma_alcalde_url: contractData?.firma_alcalde_url || "",

        // Aliases para tu JSX actual
        firmaSolicitante: contractData?.firma_solicitante_url || contractData?.firma_solicitante || "",
        firmaAdministrativa: contractData?.firma_administrativa_url || contractData?.firma_administrativa || "",
        firmaHacienda: contractData?.firma_hacienda_url || contractData?.firma_hacienda || "",
        firmaAlcalde: contractData?.firma_alcalde_url || contractData?.firma_alcalde || "",

        aprobacion_administrativa: contractData?.aprobacion_administrativa ?? "",
        aprobacion_hacienda: contractData?.aprobacion_hacienda ?? "",
        aprobacion_alcalde: contractData?.aprobacion_alcalde ?? "",

        // booleanos ya convertidos arriba
        programa_aprobado,
        indicador_aprobado,
        bpin_aprobado,

        hay_dinero_rubro,
        se_puede_ejecutar,
        observaciones_hacienda: contractData?.observaciones_hacienda ?? "",

        tipo_contrato_aprobado,
        modalidad_seleccion_aprobada,
        objeto_contractual_aprobado,
        duracion_estimada_aprobada,
        presupuesto_aprobado,
        observaciones_juridica: contractData?.observaciones_juridica ?? "",

        observaciones: contractData?.observaciones ?? "",
        bancop_url: contractData?.bancop_url ?? "",
        bancop_filename: contractData?.bancop_filename ?? "",
      };

      // 🔍 2. VALORES DESPUÉS DE NORMALIZAR
      console.log("✅ VALORES NORMALIZADOS:", {
        firmaAdministrativa: normalizedData.firmaAdministrativa,
        firma_administrativa_url: normalizedData.firma_administrativa_url,
        firmaHacienda: normalizedData.firmaHacienda,
        firma_hacienda_url: normalizedData.firma_hacienda_url,
        firmaAlcalde: normalizedData.firmaAlcalde,
        firma_alcalde_url: normalizedData.firma_alcalde_url,
        firmaSolicitante: normalizedData.firmaSolicitante,
        firma_solicitante_url: normalizedData.firma_solicitante_url,
      });

      // 🔍 3. BOOLEANOS (mantener tu log original)
      console.log("✅ BOOLEANOS EN STATE:", {
        programa_aprobado: normalizedData.programa_aprobado,
        indicador_aprobado: normalizedData.indicador_aprobado,
        bpin_aprobado: normalizedData.bpin_aprobado,
      });

      // 🔍 4. DETECCIÓN DE CORRUPCIÓN
      const urlsCorruptas = [];
      if (normalizedData.firmaAdministrativa?.includes('.png1') || normalizedData.firmaAdministrativa?.includes('.jpeg1')) {
        urlsCorruptas.push('firmaAdministrativa');
      }
      if (normalizedData.firmaHacienda?.includes('.png1') || normalizedData.firmaHacienda?.includes('.jpeg1')) {
        urlsCorruptas.push('firmaHacienda');
      }
      if (normalizedData.firmaAlcalde?.includes('.png1') || normalizedData.firmaAlcalde?.includes('.jpeg1')) {
        urlsCorruptas.push('firmaAlcalde');
      }
      if (normalizedData.firmaSolicitante?.includes('.png1') || normalizedData.firmaSolicitante?.includes('.jpeg1')) {
        urlsCorruptas.push('firmaSolicitante');
      }

      if (urlsCorruptas.length > 0) {
        console.error("❌ URLs CORRUPTAS DETECTADAS:", urlsCorruptas);
      } else {
        console.log("✅ Todas las URLs están limpias (sin .png1 o .jpeg1)");
      }

      // ✅ Actualizar el state
      setContract((prev) => ({
        ...prev,
        ...normalizedData,
      }));

      // ==================== RUBROS ====================
      let rubroSelections = [];
      if (
        contractData.discriminacion_rubros &&
        contractData.rubro &&
        contractData.nombre_rubro &&
        contractData.valor
      ) {
        const d = contractData.discriminacion_rubros.split(",");
        const r = contractData.rubro.split("||");
        const n = contractData.nombre_rubro.split("||");
        const v = contractData.valor.split("||");
        rubroSelections = d.map((codigo, i) => ({
          codigo,
          rubro: r[i] || "",
          nombreRubro: n[i] || "",
          valor: v[i] || "",
        }));
      }
      setRubroSelections(rubroSelections);

      // ==================== PROGRAMAS ====================
      setProgramaSelections(
        contractData.programa
          ? contractData.programa
            .split(",")
            .map((v) => opcionesProgramas.find((o) => o.value === v))
            .filter(Boolean)
          : []
      );

      // ==================== INDICADORES ====================
      setIndicadorSelections(
        contractData.indicador
          ? contractData.indicador
            .split(",")
            .map((v) => opcionesIndicadores.find((o) => o.value === v))
            .filter(Boolean)
          : []
      );

      // ==================== BPIN ====================
      setBpinSelections(
        contractData.bpin
          ? contractData.bpin
            .split(",")
            .map((v) => opcionesBPIN.find((o) => o.value === v))
            .filter(Boolean)
          : []
      );

      // ==================== PRESUPUESTO POR INDICADOR ✅ ====================
      const indPresRaw = contractData.indicador_presupuesto;
      if (indPresRaw) {
        try {
          const parsed = typeof indPresRaw === "string" ? JSON.parse(indPresRaw) : indPresRaw;
          setIndicadorPresupuestoSelections(Array.isArray(parsed) ? parsed : []);
        } catch {
          setIndicadorPresupuestoSelections([]);
        }
      } else {
        setIndicadorPresupuestoSelections([]);
      }

      console.log("✅ Contrato cargado completamente");

    } catch (error) {
      console.error("❌ Error fetching contract:", error);
      setError("Error al cargar el contrato: " + error.message);
    } finally {
      setLoading(false);
    }
  }, []);



  // =====================
  // 1) USUARIO DESDE STORAGE
  // =====================
  useEffect(() => {
    const loadUserInfo = () => {
      try {
        const raw =
          localStorage.getItem("auth_user") ||
          sessionStorage.getItem("auth_user") ||
          localStorage.getItem("auth_user_data") ||
          sessionStorage.getItem("auth_user_data") ||
          localStorage.getItem("user") ||
          sessionStorage.getItem("user") ||
          localStorage.getItem("authUser") ||
          sessionStorage.getItem("authUser") ||
          localStorage.getItem("usuario") ||
          sessionStorage.getItem("usuario");

        if (!raw) return;

        const parsed = JSON.parse(raw);
        const u =
          parsed?.user && typeof parsed.user === "object" ? parsed.user : parsed;

        if (!u?.email && !u?.correo && !u?.username) return;

        setUser?.({
          id: u.id ?? u.user_id ?? null,
          email: u.email || u.correo || u.username,
          full_name: u.full_name || u.nombre || "",
          rol: u.rol || u.role || "",
          secretaria: u.secretaria || u.dependencia || "",
          ...u, // ✅ si querías copiar el resto del usuario
        });
      } catch (e) {
        console.error("Error cargando usuario desde storage:", e);
      }
    };

    loadUserInfo();
  }, [setUser]);

  // =====================
  // 2) CARGAR CONTRATO (EDITAR)
  // =====================
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (contractId && contractId !== "nuevo") {
          setIsEditing(true);
          await fetchContract(contractId);
          if (cancelled) return;
        } else {
          setIsEditing(false);
        }
      } catch (e) {
        console.error("Error cargando contrato en edición:", e);
        if (!cancelled) setIsEditing(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [contractId, fetchContract]);

  // =====================
  // 2.1) CALCULAR DURACIÓN
  // =====================
  useEffect(() => {
    const inicio = contract?.fechaInicioContrato || "";
    const fin = contract?.fechaFinContrato || "";
    const dias = calcularDiasInclusive(inicio, fin);

    setContract((prev) => {
      if (String(prev?.duracionEstimada ?? "") === String(dias ?? "")) return prev;
      return { ...prev, duracionEstimada: dias ?? "" };
    });
  }, [contract?.fechaInicioContrato, contract?.fechaFinContrato]);

  // =====================
// 2.2) VERIFICAR RP APROBADO PARA ARL
// =====================
useEffect(() => {
  if (!contractId || contractId === "nuevo" || !contract?.id) return
  if (contract?.status !== "aprobado") return

  const checkRPAprobado = async () => {
    try {
      const url = apiConfig.getUrl(
        "/api/presupuesto/solicitudes/index.php",
        `contract_id=${encodeURIComponent(contract.id)}`
      )
      const resp = await fetch(url, { credentials: "include" })
      if (!resp.ok) return
      const data = await resp.json()
      const solicitudes = Array.isArray(data) ? data : data.solicitudes || data.data || []
      const rpAprobado = solicitudes.some(
        (s) =>
          String(s.request_type || "").toUpperCase() === "RP" &&
          ["aprobado", "approved"].includes(
            String(s.response_status || "").toLowerCase()
          )
      )
      setArlAprobado(rpAprobado)
    } catch (e) {
      console.warn("No se pudo verificar RP aprobado:", e)
    }
  }

  checkRPAprobado()
}, [contract?.id, contract?.status, contractId])
  // =====================
  // 3) DEBUG HACIENDA
  // =====================
  useEffect(() => {
    if (!contract) return;

    const phase = contract?.current_phase ?? contract?.phase ?? null;

    let canEditAprobacion = false;
    let canEditFirma = false;

    try {
      canEditAprobacion = !!canEditField?.("aprobacion_hacienda");
      canEditFirma = !!canEditField?.("firmaHacienda");
    } catch { }

    console.log("DEBUG HACIENDA", {
      userRole,
      phase,
      isEditing,
      canEditAprobacion,
      canEditFirma,
    });
  }, [userRole, contract?.current_phase, contract?.phase, isEditing]);






  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    console.log(`Campo cambiado: ${name}, valor: ${value}, tipo: ${type}`);

    let newValue;

    if (type === "checkbox") {
      newValue = checked;
    } else if (type === "radio" && (value === "true" || value === "false")) {
      newValue = value === "true";
    } else if (name === "presupuestoTotal") {
      // Eliminar todo excepto  números
      const numericValue = value.replace(/[.,]/g, "");
      newValue = numericValue;
    } else {
      newValue = value;
    }

    setContract((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (name === "aprobacion_alcalde") {
      console.log(`Valor de aprobacion_alcalde actualizado a: ${value}`);
    }
  };

  // Función para depurar el estado del contrato
  const debugContractState = () => {
    console.log("Estado actual del contrato:", {
      id: contractId,
      phase: contract.current_phase,
      aprobacion_administrativa: contract.aprobacion_administrativa,
      aprobacion_hacienda: contract.aprobacion_hacienda,
      aprobacion_alcalde: contract.aprobacionAlcalde,
      userRole: userRole,
    });

    // Intentar obtener el contrato directamente de la base de datos para comparar
    // Asegúrate de que esta función auxiliar esté disponible


    const fetchCurrentContract = async () => {
      try {
        const token = typeof getToken === "function" ? getToken() : "";

        if (!contractId) {
          console.error("No hay contractId para cargar.");
          return;
        }

        if (!token) {
          console.error("No autenticado. Token no disponible.");
          return;
        }

        // ✅ Producción (PHP): index.php?id=...
        const url = `/api/contracts/index.php?id=${encodeURIComponent(contractId)}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        const raw = await response.text();

        if (!response.ok) {
          console.error("Error al obtener contrato desde la API. Status:", response.status);
          console.error("Detalle del error:", raw);
          return;
        }

        let json = null;
        try {
          json = JSON.parse(raw);
        } catch (e) {
          console.error("La respuesta NO es JSON válido:", raw);
          return;
        }

        // ✅ Normalización estricta: esperamos { ok:true, contract:{...} }
        let row = json?.contract ?? null;

        // Si por alguna razón llega como data:
        if (!row && json?.data && typeof json.data === "object") {
          row = json.data;
        }

        // Si por alguna razón llega como array, NO tomes [0] a ciegas:
        if (Array.isArray(row)) {
          row = row.find((x) => String(x?.id) === String(contractId)) || null;
        }

        // Si el backend devolvió lista directamente (caso raro)
        if (!row && Array.isArray(json)) {
          row = json.find((x) => String(x?.id) === String(contractId)) || null;
        }

        if (!row || typeof row !== "object") {
          console.warn("Contrato no existe o respuesta vacía:", json);
          return;
        }

        // ✅ Garantía: el contrato devuelto debe coincidir con el ID pedido
        if (String(row.id) !== String(contractId)) {
          console.error(
            "El backend devolvió un contrato distinto al solicitado.",
            { solicitado: contractId, recibido: row.id, payload: json }
          );
          return;
        }

        console.log("✅ Contrato cargado por ID:", row.id, row.radicado);

        // 👇 Aquí ya puedes setear tu estado real (si quieres)
        // setContract(mapDbToUi(row));

        // Log de depuración que ya tenías:
        console.log("Contrato en la base de datos:", {
          aprobacion_administrativa: row?.aprobacion_administrativa,
          aprobacion_hacienda: row?.aprobacion_hacienda,
          aprobacion_alcalde: row?.aprobacion_alcalde,
        });
      } catch (err) {
        console.error("Error en depuración:", err);
      }
    };

    fetchCurrentContract();
  };

  const canEdit = () => {
    // ✅ Administrador: siempre
    if (userRole === "administrador") return true;

    // ✅ NUEVO: Administrador junior - solo hasta fase 3
    if (userRole === "administrador_junior") {
      const phase = Number(contract?.current_phase ?? 0);
      return phase <= 3;
    }

    // ✅ Nuevo contrato: se permite editar (llenar formulario)
    if (!isEditing) return true;

    // ✅ Solicitante: puede editar SOLO en fase 1 y SOLO si NO hay radicado
    if (userRole === "solicitante") {
      const phase = Number(contract?.current_phase ?? 0);
      const hasRadicado = Boolean(String(contract?.radicado ?? "").trim());
      return phase === 1 && !hasRadicado;
    }

    // ✅ Jurídica: puede editar en fase 3
    if (userRole === "juridica") {
      return Number(contract?.current_phase ?? 0) === 3;
    }

    // ❌ Otros roles: no editan por esta función (se controla por canEditField)
    return false;
  };




  // Función para determinar si un campo especí­fico puede ser editado
  const canEditField = (fieldName) => {
    const rawPhase = contract?.current_phase ?? 1;
    const phase = Number(rawPhase);
    const f = String(fieldName || "").trim();

    const isAnyOf = (name, list) => list.includes(name);

    // 🔹 ADMINISTRADOR: todo
    if (userRole === "administrador") {
      return true;
    }

    // 🔹 ADMINISTRADOR JUNIOR: todo hasta fase 3
    if (userRole === "administrador_junior") {
      return phase < 4;
    }

    // 🔹 NUEVO CONTRATO — SOLICITANTE
    if (!isEditing && userRole === "solicitante") {
      const restrictedFields = [
        "aprobacion_administrativa",
        "firmaAdministrativa",
        "firma_administrativa_url",
        "aprobacion_hacienda",
        "firmaHacienda",
        "firma_hacienda_url",
        "aprobacion_alcalde",
        "firmaAlcalde",
        "firma_alcalde",
        "firma_alcalde_url",
        "numeroActa",
        "fechaComite",
      ];
      return !restrictedFields.includes(f);
    }

    // 🔹 CONTRATO EXISTENTE
    if (isEditing) {
      // 🟦 SOLICITANTE — fase 1 sin radicado
      if (userRole === "solicitante" && phase === 1 && !contract?.radicado) {
        const restrictedFields = [
          "radicado",
          "aprobacion_administrativa",
          "firmaAdministrativa",
          "firma_administrativa_url",
          "aprobacion_hacienda",
          "firmaHacienda",
          "firma_hacienda_url",
          "aprobacion_alcalde",
          "firmaAlcalde",
          "firma_alcalde",
          "firma_alcalde_url",
          "numeroActa",
          "fechaComite",
        ];
        return !restrictedFields.includes(f);
      }

      // 🟡 PLANEACIÓN / VALIDADOR — fase 1
      if ((userRole === "planeacion" || userRole === "validador") && phase === 1) {
        const planeacionFields = [
          "firmaAdministrativa",
          "firma_administrativa_url",
          "aprobacion_administrativa",
          "programa_aprobado",
          "indicador_aprobado",
          "bpin_aprobado",
          "observaciones",
        ];
        return isAnyOf(f, planeacionFields);
      }

      // 🟦 HACIENDA — fase 2
      if (userRole === "hacienda" && phase === 2) {
        const haciendaFields = [
          "hay_dinero_rubro",
          "se_puede_ejecutar",
          "observaciones_hacienda",
          "aprobacion_hacienda",
          "firmaHacienda",
          "firma_hacienda_url",
        ];
        return isAnyOf(f, haciendaFields);
      }

      // 🟣 JURÍDICA — fase 3 o superior
      if (userRole === "juridica" && phase >= 3) {
        const juridicaFields = [
          "tipo_contrato_aprobado",
          "modalidad_seleccion_aprobada",
          "objeto_contractual_aprobado",
          "duracion_estimada_aprobada",
          "presupuesto_aprobado",
          "observaciones_juridica",
          "numeroActa",
          "fechaComite",
        ];
        return isAnyOf(f, juridicaFields);
      }

      // 🟢 ALCALDE — fase 4
      if (userRole === "alcalde" && phase === 4) {
        const alcaldeFields = [
          "aprobacion_alcalde",
          "observaciones",
          "firmaAlcalde",
          "firma_alcalde",
          "firma_alcalde_url",
        ];
        return isAnyOf(f, alcaldeFields);
      }
    }

    // 🔥 DEFAULT — nadie más puede editar
    return false;
  };






  // Función para manejar el rechazo del contrato
  const handleReject = () => {
    setError("");

    let initialReason = "";

    if (userRole === "planeacion") {
      initialReason = contract.observaciones || "";
    } else if (userRole === "hacienda") {
      initialReason = contract.observaciones_hacienda || "";
    } else if (userRole === "juridica") {
      initialReason = contract.observaciones_juridica || "";
    } else if (userRole === "alcalde") {
      initialReason = contract.observaciones || "";
    }

    setRejectionReason(initialReason);
    setShowRejectModal(true);
  };

  const handleConfirmAnulacion = async () => {
    if (!anulacionReason.trim()) {
      alert("Por favor, indique el motivo de la anulación.");
      return;
    }

    try {
      setLoading(true);

      const observacion = `[ANULADO] ${anulacionReason.trim()}`;

      await updateContract(contract.id, {
        status: "anulado",
        observaciones: observacion,
      });

      await insertContractHistory({
        contract_id: contract.id,
        user_id: user.id,
        action: "anulacion",
        phase: contract.current_phase,
        comments: anulacionReason.trim(),
      });

      setContract((prev) => ({
        ...prev,
        status: "anulado",
        observaciones: observacion,
      }));

      setShowAnulacionModal(false);
      setAnulacionReason("");
      alert("Contrato anulado correctamente.");
      navigate("/dashboard");
    } catch (error) {
      setError("Error al anular el contrato: " + (error.message || "Error desconocido"));
    } finally {
      setLoading(false);
    }
  };
  const handleAnularCdpSubmit = async () => {
    if (!anularCdpNumero.trim()) {
      alert("Debe ingresar el número del CDP a anular.");
      return;
    }
    if (!anularCdpFile) {
      alert("Debe adjuntar la necesidad anulada.");
      return;
    }
    try {
      setLoading(true);

      // 1) Subir archivo
      const uploaded = await uploadPresupuestoFile(anularCdpFile, "necesidad_anulada");

      // 2) Crear solicitud tipo ANULAR_CDP
      await createPresupuestoRequest({
        request_type: "ANULAR_CDP",
        attachments: {
          necesidad_anulada: uploaded,
          numero_cdp_anular: anularCdpNumero.trim(),
        },
      });

      alert("Solicitud de anulación CDP enviada correctamente a Presupuesto.");
      setShowAnularCdpModal(false);
      setAnularCdpNumero("");
      setAnularCdpFile(null);
    } catch (error) {
      alert("Error al enviar: " + (error.message || "Error desconocido"));
    } finally {
      setLoading(false);
    }
  };

  const testEmail = async () => {
    try {
      const email = prompt("Ingrese un correo para la prueba:", user?.email || "")

      if (!email) return

      const response = await fetch(
        `${apiConfig.getUrl(apiConfig.endpoints.testEmail)}?email=${encodeURIComponent(email)}`,
      )
      const data = await response.json()

      if (data.success) {
        alert(`✅ Correo de prueba enviado a ${email}. Verifique su bandeja de entrada.`)
      } else {
        alert(`❌ Error al enviar correo: ${data.error} - ${data.details || ""}`)
      }
    } catch (error) {
      console.error("Error al probar correo:", error)
      alert(`Error al probar correo: ${error.message}`)
    }
  }

  const confirmReject = async () => {
    if (!rejectionReason.trim()) {
      alert("Por favor, ingrese un motivo de rechazo");
      return;
    }

    try {
      setLoading(true);

      // 1. Armar el payload de actualización del contrato
      const updateData = {
        status: "rechazado",
      };

      if (userRole === "planeacion") {
        updateData.current_phase = 1;
        updateData.observaciones = rejectionReason;
      } else if (userRole === "hacienda") {
        updateData.current_phase = 2;
        updateData.observaciones_hacienda = rejectionReason;
      } else if (userRole === "juridica") {
        updateData.current_phase = 3;
        updateData.observaciones_juridica = rejectionReason;
      } else if (userRole === "alcalde") {
        updateData.current_phase = 4;
        updateData.aprobacion_alcalde = "rechazado";
        updateData.observaciones = rejectionReason;
      }

      // 2. Armar entrada de historial
      const historyEntry = {
        contract_id: contract.id,
        user_id: user.id,
        action: "reject",
        phase: updateData.current_phase,
        comments: rejectionReason,
      };

      // 3. Actualizar contrato en SQL usando tu helper existente
      await updateContract(contract.id, updateData);

      // 4. Insertar en contract_history
      await insertContractHistory(historyEntry);

      // 5. Listo: cerramos modal y volvemos al dashboard
      alert("El contrato ha sido rechazado correctamente.");
      setShowRejectModal(false);
      navigate("/dashboard");
    } catch (error) {
      console.error("Error al rechazar el contrato:", error);
      setError(
        "Error al rechazar el contrato: " + (error.message || "Error desconocido")
      );
    } finally {
      setLoading(false);
    }
  };





  // Añadir una función para manejar la carga de archivos de firma
  // ContractForm.jsx


  // ...
  const handleFirmaUpload = async (e, firmaType) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    setError(null);

    // =========================
    // Validaciones
    // =========================
    if (!file.type.startsWith("image/")) {
      setError("Seleccione una imagen (JPG/PNG/WebP).");
      try { e.target.value = ""; } catch { }
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Máximo 5MB.");
      try { e.target.value = ""; } catch { }
      return;
    }

    // Preview local
    const previewUrl = URL.createObjectURL(file);

    // =========================
    // Normalizar firmaType (evita errores por strings distintos)
    // =========================
    const normalizeFirmaType = (t) => {
      const x = String(t || "").trim();
      if (!x) return "";

      const map = {
        firmaSolicitante: "firmaSolicitante",
        firma_solicitante: "firmaSolicitante",
        solicitante: "firmaSolicitante",

        firmaAdministrativa: "firmaAdministrativa",
        firma_administrativa: "firmaAdministrativa",
        administrativa: "firmaAdministrativa",

        firmaHacienda: "firmaHacienda",
        firma_hacienda: "firmaHacienda",
        hacienda: "firmaHacienda",

        firmaAlcalde: "firmaAlcalde",
        firma_alcalde: "firmaAlcalde",
        alcalde: "firmaAlcalde",
      };

      return map[x] || x;
    };

    const type = normalizeFirmaType(firmaType);

    // =========================
    // Mapeo firmaType -> columna en BD (URLs)
    // =========================
    const fieldMap = {
      firmaSolicitante: "firma_solicitante_url",
      firmaAdministrativa: "firma_administrativa_url",
      firmaHacienda: "firma_hacienda_url",
      firmaAlcalde: "firma_alcalde_url",
    };

    const urlField = fieldMap[type];
    if (!urlField) {
      console.error("Tipo de firma inválido:", firmaType, "->", type);
      setError(`Tipo de firma inválido: ${String(firmaType)}`);
      try { e.target.value = ""; } catch { }
      return;
    }

    // =========================
    // Contrato NUEVO:
    // - Guardar archivo en memoria (para subir luego cuando exista ID)
    // - Guardar preview SOLO UI en *_preview (NO en firma_*_url)
    // =========================
    if (!contractId || contractId === "new" || contractId === "nuevo") {
      setContract((prev) => ({
        ...prev,
        [`${urlField}_file`]: file,
        [`${urlField}_preview`]: previewUrl, // ✅ preview UI
        // ✅ NO guardes blob dentro del campo real de BD
        [urlField]: prev?.[urlField] || "",
      }));
      try { e.target.value = ""; } catch { }
      return;
    }

    // =========================
    // Contrato EXISTENTE: subir al servidor
    // =========================
    const endpointMap = {
      firmaSolicitante: "/api/contracts/update/firma_solicitante.php",
      firmaAdministrativa: "/api/contracts/update/firma_administrativa.php",
      firmaHacienda: "/api/contracts/update/firma_hacienda.php",
      firmaAlcalde: "/api/contracts/update/firma_alcalde.php",
    };

    const relativePath = endpointMap[type];
    if (!relativePath) {
      const msg = `No hay endpoint configurado para ${type}.`;
      setError(msg);
      console.error(msg);
      try { e.target.value = ""; } catch { }
      return;
    }

    // Base URL robusta (si existe apiConfig)
    const base =
      (typeof apiConfig !== "undefined" &&
        apiConfig &&
        (apiConfig.API_BASE_URL || apiConfig.API_BASE || apiConfig.baseUrl)) ||
      "";

    const uploadUrl = base
      ? `${String(base).replace(/\/$/, "")}${relativePath}`
      : relativePath;

    // Guardar valor anterior para revertir si falla
    const prevValue = contract?.[urlField] || "";

    // Preview inmediato mientras sube
    setContract((prev) => ({ ...prev, [urlField]: previewUrl }));

    try {
      const token = getToken?.() || "";

      const formData = new FormData();
      formData.append("file", file);
      formData.append("contract_id", contractId);

      const resp = await fetch(uploadUrl, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: formData,
        // credentials: "include",
      });

      const rawText = await resp.text().catch(() => "");
      let data = null;

      try {
        data = rawText ? JSON.parse(rawText) : null;
      } catch {
        data = null;
      }

      // =========================
      // Manejo de error: mostrar detalle REAL
      // =========================
      if (!resp.ok || !data?.ok) {
        const backendDetail =
          (data && (data.detail || data.Detail || data.error_detail)) ||
          (data && data.sql_error && Array.isArray(data.sql_error) && (data.sql_error[2] || data.sql_error[1])) ||
          (data && (data.error || data.message)) ||
          "";

        const rawSnippet = rawText
          ? String(rawText).replace(/\s+/g, " ").slice(0, 280)
          : "";

        const msg =
          backendDetail ||
          (rawSnippet ? `Servidor respondió: ${rawSnippet}` : "") ||
          `Error al subir firma (HTTP ${resp.status})`;

        setContract((prev) => ({ ...prev, [urlField]: prevValue }));
        throw new Error(msg);
      }

      // =========================
      // Éxito: leer URL devuelta
      // =========================
      const returnedUrl =
        (data && typeof data[urlField] === "string" && data[urlField]) ||
        (data && typeof data.firma_url === "string" && data.firma_url) ||
        (data && typeof data.firma === "string" && data.firma) ||
        "";

      if (!returnedUrl) {
        setContract((prev) => ({ ...prev, [urlField]: prevValue }));
        throw new Error("El servidor respondió OK pero no devolvió la URL de la firma.");
      }

      const busted = `${returnedUrl}${returnedUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;

      setContract((prev) => ({
        ...prev,
        [urlField]: busted,
        // ya no necesitas preview, pero no hace daño
        [`${urlField}_preview`]: "",
      }));

      if (typeof fetchContractById === "function") {
        await fetchContractById(contractId);
      }
    } catch (err) {
      console.error("Error al subir firma:", err);
      setError(err?.message ? String(err.message) : "Error al subir la firma.");
      setContract((prev) => ({ ...prev, [urlField]: prevValue }));
    } finally {
      try { e.target.value = ""; } catch { }
    }
  };



  const handleReturnToPreviousPhase = async () => {
    if (!window.confirm("¿Está seguro de que desea devolver este contrato a la fase anterior?")) {
      return;
    }

    if (!contract || !contract.id) {
      alert("No hay contrato cargado para devolver de fase.");
      return;
    }

    const currentPhase = Number(contract.current_phase ?? 0);

    if (!currentPhase || currentPhase <= 1) {
      alert("El contrato ya está en la primera fase. No se puede devolver más.");
      return;
    }

    const previousPhase = currentPhase - 1;

    try {
      setLoading(true);
      setError("");

      // ✅ Datos locales solo para refrescar UI después
      const localUpdateData = {
        current_phase: previousPhase,
        status: "en_proceso",
      };

      // ✅ Usar el endpoint de advance para DEVOLVER fase
      const response = await fetch(
        apiConfig.getUrl("/api/contracts/advance/index.php"),
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            contract_id: contract.id,
            from_phase: currentPhase,
            to_phase: previousPhase,
            decision: "aprobado", // el backend detecta devolución por to_phase < from_phase
            observaciones: `Devuelto por ${userRole} desde fase ${currentPhase} a fase ${previousPhase}`,
          }),
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result?.ok) {
        throw new Error(result?.error || `Error HTTP ${response.status}`);
      }

      // ✅ Actualizar estado local con lo que realmente devolvió el backend
      setContract((prev) => ({
        ...prev,
        ...prev,
        ...localUpdateData,
        ...(result.contract || {}),
      }));

      // ✅ Registrar historial adicional solo si lo necesitas en frontend
      try {
        const historyEntry = {
          contract_id: contract.id,
          user_id: user.id,
          action: "return_to_previous_phase",
          phase: previousPhase,
          comments: `Devuelto por ${userRole} desde fase ${currentPhase} a fase ${previousPhase}`,
        };

        await insertContractHistory(historyEntry);
      } catch (historyError) {
        console.error("Error al registrar historial adicional:", historyError);
      }

      // ✅ Notificación a la fase anterior
      try {
        await sendContractNotification(previousPhase, {
          ...contract,
          ...localUpdateData,
          ...(result.contract || {}),
        });
      } catch (notifyError) {
        console.error("Error al enviar notificación:", notifyError);
      }

      alert(`Contrato devuelto exitosamente a la fase ${previousPhase}`);

      // ✅ Salir inmediatamente para evitar que se dispare otro guardado
      navigate("/dashboard");
      return;
    } catch (error) {
      console.error("Error al devolver contrato:", error);
      setError("Error al devolver el contrato: " + (error.message || "Error desconocido"));
    } finally {
      setLoading(false);
    }
  };



  const handleGenerateCdpClick = () => {
    if (!contract || !contract.radicado) {
      alert("Debe guardar el contrato primero antes de generar el CDP");
      return;
    }
    setShowCdpForm(true);
  };

  const handleGenerateRpClick = () => {
    if (!contract || !contract.radicado) {
      alert("Debe guardar el contrato primero antes de generar el RP");
      return;
    }
    setShowRpForm(true);
  };

  const handleGenerateAdquisicionBienesClick = () => {
    if (!contract || !contract.radicado) {
      alert("Debe guardar el contrato primero antes de generar la adquisición de bienes");
      return;
    }

    setSelectedAlmacenRequest(null); // ✅ generar = NO es responder
    setShowAdquisicionForm(true);
  };
  const contratoFirmadoAlcalde =
    !!contract?.firma_alcalde_url ||
    contract?.aprobacion_alcalde === true ||
    Number(contract?.current_phase) >= 5;

  const handleGenerateInexistenciaClick = async () => {
    try {
      const createUrl = apiConfig.getUrl(
        apiConfig.endpoints?.inexistenciaCrear || "/api/inexistencia/solicitudes/index.php"
      );

      const resp = await fetch(createUrl, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ contractId: contract.id, notes: "" }),
      });

      const payload = await resp.json().catch(() => ({}));

      if (!resp.ok) throw new Error(payload?.error || `HTTP ${resp.status}`);

      // Si viene id, perfecto; si no, igual ya quedó creada (y rol 9 la verá en su listado)
      const newId = payload?.id || payload?.data?.id || null;

      alert(
        newId
          ? "Solicitud de inexistencia enviada."
          : "Solicitud enviada."
      );

      // IMPORTANTE: NO navegar aquí.
      // Si quieres, refresca datos del contrato o dashboard:
      // await reloadContract?.();
    } catch (e) {
      alert(e?.message || "No se pudo enviar la solicitud de inexistencia.");
    }
  };



  const handleEnviarCdpClick = () => {
    setShowEnviarCdpModal(true);
  };

  const handleEnviarRpClick = () => {
    setShowEnviarRpModal(true);
  };

  const uploadPresupuestoFile = async (file, name) => {
    if (!file) throw new Error(`Falta archivo: ${name}`);

    const ext = file.name.split(".").pop();
    const fileName = `${name}_${contract?.radicado || "sin_radicado"}_${Date.now()}.${ext}`;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);
    formData.append("fileName", fileName);
    formData.append("contractId", contractId);
    formData.append("radicado", contract?.radicado || "");
    formData.append("folder", "presupuesto");

    // ✅ NO dependas de endpoint undefined
    const uploadPath =
      apiConfig?.endpoints?.uploadPresupuestoAttachment ||
      "/api/presupuesto/attachments/upload/index.php";

    const resp = await fetch(apiConfig.getUrl(uploadPath), {
      method: "POST",
      body: formData,
    });

    const raw = await resp.text().catch(() => "");
    if (!resp.ok) throw new Error(raw || `Error al subir ${name}`);

    let result = {};
    try { result = raw ? JSON.parse(raw) : {}; } catch { }

    if (!result?.url) throw new Error(`El backend no devolvió url para ${name}`);

    return { name: result.originalName || file.name, url: result.url };
  };



  const handleEnviarCdpSubmit = async (data) => {
    try {
      const necesidadFile = data?.attachments?.necesidad;
      const bancopFile = data?.attachments?.bancop;

      if (!necesidadFile || !bancopFile) {
        alert("Debes adjuntar Necesidad y Bancop antes de enviar.");
        return;
      }

      setLoading(true);

      // 1) subir archivos
      const [necesidad, bancop] = await Promise.all([
        uploadPresupuestoFile(necesidadFile, "necesidad"),
        uploadPresupuestoFile(bancopFile, "bancop"),
      ]);

      // 2) crear solicitud (CDP)
      await createPresupuestoRequest({
        request_type: "CDP",
        attachments: { necesidad, bancop },
      });

      alert("CDP enviado correctamente a Presupuesto");
      setShowEnviarCdpModal(false);
    } catch (error) {
      console.error("Error enviando CDP:", error);
      alert("Error al enviar CDP: " + (error?.message || "Error desconocido"));
    } finally {
      setLoading(false);
    }
  };


  const handleEnviarRpSubmit = async (data) => {
    try {
      const rutFile = data?.attachments?.rut;
      const minutaFile = data?.attachments?.minuta;

      if (!rutFile || !minutaFile) {
        alert("Debes adjuntar los archivos RUT y Minuta antes de enviar.");
        return;
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (rutFile.size > maxSize || minutaFile.size > maxSize) {
        alert("Los archivos no pueden superar 10MB cada uno.");
        return;
      }

      setLoading(true);

      // 1) subir archivos
      const [rut, minuta] = await Promise.all([
        uploadPresupuestoFile(rutFile, "rut"),
        uploadPresupuestoFile(minutaFile, "minuta"),
      ]);

      // 2) crear solicitud (RP)
      await createPresupuestoRequest({
        request_type: "RP",
        attachments: { rut, minuta },
      });

      alert("RP enviado correctamente a Presupuesto");
      setShowEnviarRpModal(false);
    } catch (error) {
      console.error("Error enviando RP:", error);
      alert("Error al enviar RP: " + (error?.message || "Error desconocido"));
    } finally {
      setLoading(false);
    }
  };

const handleEnviarArlSubmit = async ({ notas }) => {
  try {
    setLoading(true)
    await createPresupuestoRequest({
      request_type: "ARL",
      attachments: { notas: notas || "" },
    })
    alert("✅ Solicitud ARL enviada correctamente a SST")
    setShowEnviarArlModal(false)
  } catch (error) {
    alert("Error al enviar ARL: " + (error?.message || "Error desconocido"))
  } finally {
    setLoading(false)
  }
}

  const handleViewPresupuestoRequest = async (type) => {
    try {
      setLoading(true);

      if (!contractId) {
        console.error("handleViewPresupuestoRequest: contractId vacío");
        setError("No se encontró el ID del contrato.");
        return;
      }

      // ✅ Endpoints con fallback (evita getUrl(undefined))
      const EP_PRESUPUESTO_REQUESTS =
        apiConfig?.endpoints?.presupuestoRequests || "/api/presupuesto/requests/index.php";

      const EP_CDP_FORMULARIOS =
        apiConfig?.endpoints?.cdpFormularios || "/api/cdp/formularios/index.php";

      const EP_RP_FORMULARIOS =
        apiConfig?.endpoints?.rpFormularios || "/api/rp/formularios/index.php";

      // 1) Obtener la última solicitud de presupuesto (CDP o RP) para este contrato
      let existingRequest = null;

      try {
        const qs = new URLSearchParams({
          contractId: String(contractId),
          requestType: String(type),
          limit: "1",
          order: "sent_at_desc",
        });

        const requestsUrl = `${apiConfig.getUrl(EP_PRESUPUESTO_REQUESTS)}?${qs.toString()}`;

        const requestsResponse = await fetch(requestsUrl, { method: "GET" });

        if (!requestsResponse.ok) {
          const errorData = await requestsResponse.json().catch(() => ({}));
          console.error(
            "Error al obtener solicitud:",
            errorData.message || errorData.error || requestsResponse.statusText
          );
        } else {
          const requestsData = await requestsResponse.json().catch(() => null);

          if (Array.isArray(requestsData) && requestsData.length > 0) {
            existingRequest = requestsData[0];
          } else if (requestsData && typeof requestsData === "object") {
            existingRequest = requestsData;
          }
        }
      } catch (requestsError) {
        console.error("Error al obtener solicitud:", requestsError);
      }

      // 2) Obtener último formulario CDP o RP según el tipo
      let cdpFormulario = null;
      let rpFormulario = null;

      if (type === "CDP") {
        try {
          const qs = new URLSearchParams({
            contractId: String(contractId),
            limit: "1",
            order: "created_at_desc",
          });

          const cdpUrl = `${apiConfig.getUrl(EP_CDP_FORMULARIOS)}?${qs.toString()}`;

          const cdpResponse = await fetch(cdpUrl, { method: "GET" });

          if (!cdpResponse.ok) {
            const errorData = await cdpResponse.json().catch(() => ({}));
            console.error(
              "Error al obtener formulario CDP:",
              errorData.message || errorData.error || cdpResponse.statusText
            );
          } else {
            const cdpData = await cdpResponse.json().catch(() => null);
            if (Array.isArray(cdpData) && cdpData.length > 0) {
              cdpFormulario = cdpData[0];
            } else if (cdpData && typeof cdpData === "object") {
              cdpFormulario = cdpData;
            }
          }
        } catch (cdpError) {
          console.error("Error al obtener formulario CDP:", cdpError);
        }
      }

      if (type === "RP") {
        try {
          const qs = new URLSearchParams({
            contractId: String(contractId),
            limit: "1",
            order: "created_at_desc",
          });

          const rpUrl = `${apiConfig.getUrl(EP_RP_FORMULARIOS)}?${qs.toString()}`;

          const rpResponse = await fetch(rpUrl, { method: "GET" });

          if (!rpResponse.ok) {
            const errorData = await rpResponse.json().catch(() => ({}));
            console.error(
              "Error al obtener formulario RP:",
              errorData.message || errorData.error || rpResponse.statusText
            );
          } else {
            const rpData = await rpResponse.json().catch(() => null);
            if (Array.isArray(rpData) && rpData.length > 0) {
              rpFormulario = rpData[0];
            } else if (rpData && typeof rpData === "object") {
              rpFormulario = rpData;
            }
          }
        } catch (rpError) {
          console.error("Error al obtener formulario RP:", rpError);
        }
      }

      // 3) Abrir el modal con toda la info recopilada
      setPresupuestoModal({
        isOpen: true,
        mode: "review",
        requestType: type,
        existingRequest: existingRequest || null,
        cdpFormulario: cdpFormulario || null,
        rpFormulario: rpFormulario || null,
      });
    } catch (error) {
      console.error("Error al abrir modal de solicitudes:", error);
      setError("Error al abrir modal de solicitudes");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenResponderPresupuesto = async (type) => {
    try {
      console.log("🔍 Buscando solicitudes", type, "para contrato:", contractId);

      // ✅ FIX 1: Endpoint correcto con contract_id
      const requestsUrl = apiConfig.getUrl(
        apiConfig.endpoints.presupuestoRequests || "/api/presupuesto/solicitudes/index.php",
        `contract_id=${encodeURIComponent(contractId)}`
      );

      console.log("📡 URL solicitudes:", requestsUrl);

      const resp = await fetch(requestsUrl, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const raw = await resp.text();
      console.log("📥 Respuesta RAW:", raw);

      let data;
      try {
        data = JSON.parse(raw);
      } catch (e) {
        console.error("❌ No es JSON válido:", raw);
        throw new Error("Respuesta inválida del servidor");
      }

      // ✅ FIX 2: Normalizar respuesta (acepta múltiples formatos)
      let requests = [];
      if (Array.isArray(data)) {
        requests = data;
      } else if (data?.requests && Array.isArray(data.requests)) {
        requests = data.requests;
      } else if (data?.data && Array.isArray(data.data)) {
        requests = data.data;
      } else if (data?.solicitudes && Array.isArray(data.solicitudes)) {
        requests = data.solicitudes;
      }

      console.log("📋 Solicitudes parseadas:", requests.length);

      // ✅ FIX 3: Filtrar por tipo y pendientes
      const filtered = requests.filter((req) => {
        const reqType = String(req.request_type || req.tipo || "").toUpperCase();
        const status = String(req.status || req.estado || "").toLowerCase();
        const responseStatus = String(req.response_status || req.estado_respuesta || "").toLowerCase();

        const isCorrectType = reqType === type.toUpperCase();
        const isPending =
          status === "pending" ||
          status === "pendiente" ||
          status === "";
        const notResponded =
          !responseStatus ||
          responseStatus === "pending" ||
          responseStatus === "pendiente" ||
          responseStatus === "";

        console.log(`📌 Solicitud ${req.id}:`, {
          reqType,
          status,
          responseStatus,
          isCorrectType,
          isPending,
          notResponded,
          match: isCorrectType && isPending && notResponded
        });

        return isCorrectType && isPending && notResponded;
      });

      console.log(`✅ Solicitudes ${type} pendientes:`, filtered.length);

      if (filtered.length === 0) {
        alert(`No hay solicitud ${type} pendiente para este contrato.`);
        return;
      }

      // ✅ FIX 4: Abrir modal con la solicitud
      setPresupuestoModal({
        isOpen: true,
        mode: "review",
        requestType: type,
        existingRequest: filtered[0],
        cdpFormulario: null,
        rpFormulario: null,
      });

    } catch (error) {
      console.error("❌ Error completo:", error);
      alert("Error al cargar solicitudes de " + type + ": " + error.message);
    }
  };

  const handlePresupuestoResponderSubmit = async (data) => {
    const normalizeYMD = (v) => {
      if (!v) return null;
      const s = String(v).trim();
      if (!s) return null;

      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

      const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})T/);
      if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

      const dmY = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (dmY) return `${dmY[3]}-${dmY[2]}-${dmY[1]}`;

      return null;
    };

    try {
      setLoading(true);
      console.log("📤 Respondiendo solicitud:", data);

      const responseStatus =
        data.response_status || (data.approved ? "aprobado" : "negado");
      const isAprobado = responseStatus === "aprobado";

      // ✅ FIX: leer motivoRechazo del campo correcto
      const motivoTexto = String(data.motivoRechazo || data.notes || "").trim();

      const updateData = {
        requestId: data.requestId,
        response_status: responseStatus,
        status: isAprobado ? "aprobado" : "rechazado",
        responded_at: new Date().toISOString(),
        fecha_documento: isAprobado ? normalizeYMD(data.fecha_documento) : null,
        numero_documento: isAprobado ? (data.numero_documento || null) : null,
        response_attachments:
          isAprobado &&
            Array.isArray(data.responseAttachments) &&
            data.responseAttachments.length > 0
            ? data.responseAttachments
            : null,
        motivoRechazo: !isAprobado ? (motivoTexto || null) : null,
        // ✅ FIX: eliminado response_notes (columna no existe en BD)
      };

      console.log("📊 Datos a enviar (con attachments):", updateData);

      const url = apiConfig.getUrl(
        apiConfig.endpoints.presupuestoRequests ||
        "/api/presupuesto/solicitudes/index.php"
      );

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(updateData),
      });

      const contentType = response.headers.get("content-type") || "";
      let result;

      if (contentType.includes("application/json")) {
        result = await response.json();
      } else {
        const text = await response.text();
        console.error("❌ Respuesta no JSON:", text);
        throw new Error("El servidor no devolvió JSON válido");
      }

      console.log("📥 Respuesta del servidor:", result);

      if (!response.ok) {
        throw new Error(result?.message || result?.error || `HTTP ${response.status}`);
      }

      alert(
        `✅ ${presupuestoModal.requestType} ${isAprobado ? "aprobado" : "rechazado"
        } correctamente`
      );

      setPresupuestoModal({
        isOpen: false,
        mode: null,
        requestType: null,
        existingRequest: null,
        cdpFormulario: null,
        rpFormulario: null,
      });
    } catch (error) {
      console.error("❌ Error completo:", error);
      alert("Error al procesar la solicitud: " + (error.message || "Error desconocido"));
    } finally {
      setLoading(false);
    }
  };


  const getExistingRequest = async (type) => {
    try {
      const url = `${apiConfig.getUrl(
        apiConfig.endpoints.presupuestoRequests
      )}?contractId=${encodeURIComponent(contractId)}&requestType=${encodeURIComponent(
        type
      )}&limit=1&order=sent_at_desc`;

      const response = await fetch(url, {
        method: "GET",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(
          "Error obteniendo solicitud:",
          errorData.message || response.statusText
        );
        return null;
      }

      const data = await response.json();

      // El backend puede devolver array o un solo objeto, manejamos ambos casos
      if (Array.isArray(data)) {
        return data.length > 0 ? data[0] : null;
      }

      return data || null;
    } catch (error) {
      console.error("Error obteniendo solicitud:", error);
      return null;
    }
  };


  const handlePresupuestoSubmit = async (data) => {
    try {
      setLoading(true);

      // 👉 Función interna para subir archivos de respuesta a presupuesto
      const uploadResponseFile = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("contractId", contractId);
        formData.append("requestType", presupuestoModal.requestType || "");
        formData.append("radicado", contract?.radicado || "sin_radicado");

        const response = await fetch(
          apiConfig.getUrl(apiConfig.endpoints.uploadPresupuestoAttachment),
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message ||
            errorData.error ||
            `Error al subir archivo de respuesta (HTTP ${response.status})`
          );
        }

        const result = await response.json();

        return {
          name: result.name || file.name,
          url: result.url || result.publicUrl || result.path || "",
        };
      };

      // ✅ FIX: Aceptar AMBOS modos (review o respond)
      if ((presupuestoModal.mode === "review" || presupuestoModal.mode === "respond") && data.requestId) {
        // 💼 Presupuesto respondiendo a una solicitud existente

        const trimmedNotes = (data.notes || data.motivoRechazo || "").trim();

        // ✅ FIX: Leer response_status directamente del modal
        const isApproved = data.response_status === "aprobado";

        const updateData = {
          requestId: data.requestId,
          response_status: data.response_status || (data.approved ? "aprobado" : "negado"),
          status: isApproved ? "aprobado" : "rechazado",
          responded_at: new Date().toISOString(),
        };

        // ✅ NUEVO: guardar fecha_documento y numero_documento (CDP y RP)
        updateData.fecha_documento = data.fecha_documento || null;
        updateData.numero_documento = data.numero_documento || null;

        // ✅ NUEVO: guardar nuevo_resumido y nombre_nuevo_resumido
        updateData.nuevo_resumido = data.nuevo_resumido || null;
        updateData.nombre_nuevo_resumido = data.nombre_nuevo_resumido || null;

        // 📎 ✅ FIX: Los archivos YA vienen subidos desde el modal
        if (data.responseAttachments && data.responseAttachments.length > 0) {
          updateData.response_attachments = data.responseAttachments;
        }

        // Solo guardamos motivoRechazo cuando está negado
        updateData.motivoRechazo = data.motivoRechazo || (!isApproved && trimmedNotes.length > 0 ? trimmedNotes : null);

        // ✅ DEBUG (mira si aquí salen los campos)
        console.log("📤 PUT presupuesto_requests payload:", updateData);

        const updateRequestResponse = await fetch(
          apiConfig.getUrl(apiConfig.endpoints.presupuestoRequests),
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updateData),
          }
        );

        if (!updateRequestResponse.ok) {
          const errData = await updateRequestResponse.json().catch(() => ({}));
          throw new Error(
            errData.message ||
            errData.error ||
            "Error actualizando la solicitud de presupuesto"
          );
        }

        // Guardar motivoRechazo también en el formulario CDP/RP más reciente si está rechazado
        if (!isApproved && trimmedNotes.length > 0) {
          const formEndpoint =
            presupuestoModal.requestType === "CDP"
              ? apiConfig.endpoints.cdpFormularios
              : apiConfig.endpoints.rpFormularios;

          try {
            const latestFormRes = await fetch(
              `${apiConfig.getUrl(formEndpoint)}?contractId=${encodeURIComponent(
                contractId
              )}&limit=1&order=created_at_desc`
            );

            if (!latestFormRes.ok) {
              const err = await latestFormRes.json().catch(() => ({}));
              console.error(
                "❌ Error obteniendo formulario para guardar motivoRechazo:",
                err.message || err.error || latestFormRes.statusText
              );
            } else {
              const formDataRes = await latestFormRes.json();
              const latestForm =
                Array.isArray(formDataRes) && formDataRes.length > 0
                  ? formDataRes[0]
                  : !Array.isArray(formDataRes)
                    ? formDataRes
                    : null;

              if (latestForm && latestForm.id) {
                const updateFormRes = await fetch(
                  `${apiConfig.getUrl(formEndpoint)}/${encodeURIComponent(
                    latestForm.id
                  )}`,
                  {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ motivoRechazo: trimmedNotes }),
                  }
                );

                if (!updateFormRes.ok) {
                  const errForm = await updateFormRes.json().catch(() => ({}));
                  console.error(
                    "❌ Error actualizando motivoRechazo en formulario:",
                    errForm.message || errForm.error || updateFormRes.statusText
                  );
                } else {
                  console.log(
                    "✅ motivoRechazo guardado correctamente en formulario",
                    presupuestoModal.requestType
                  );
                }
              } else {
                console.error("❌ No se encontró formulario para guardar motivoRechazo");
              }
            }
          } catch (e) {
            console.error("❌ Error general guardando motivoRechazo en formulario:", e);
          }
        }

        alert(`✅ ${presupuestoModal.requestType} ${isApproved ? "aprobado" : "rechazado"} correctamente`);
      } else {
        // 📨 Solicitante enviando nueva solicitud
        await createPresupuestoRequest(data);
      }

      setPresupuestoModal({
        isOpen: false,
        mode: null,
        requestType: null,
        existingRequest: null,
        cdpFormulario: null,
        rpFormulario: null,
      });
    } catch (error) {
      console.error("Error:", error);
      alert("Error al procesar la solicitud: " + (error.message || "Error desconocido"));
    } finally {
      setLoading(false);
    }
  };



  const createPresupuestoRequest = async ({ request_type, attachments }) => {
    // ✅ CORRECCIÓN: Usar contract.id del estado en lugar de contractId de useParams
    const actualContractId = contract?.id || contractId;

    if (!actualContractId) throw new Error("Falta contractId");
    if (!user?.id) throw new Error("Falta user.id");

    const requestPath =
      apiConfig?.endpoints?.presupuestoRequests || "/api/presupuesto/requests/index.php";

    const payload = {
      contract_id: actualContractId, // ✅ Usar el ID real del contrato cargado
      request_type,           // "CDP" | "RP"
      attachments,            // URLs (JSON serializable)
      sent_by: user.id,
      status: "pending",
      sent_at: new Date().toISOString(),
    };

    console.log("📤 Enviando solicitud CDP/RP con contract_id:", actualContractId); // ✅ Debug

    const resp = await fetch(apiConfig.getUrl(requestPath), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const raw = await resp.text().catch(() => "");
    if (!resp.ok) throw new Error(raw || "Error al registrar solicitud a Presupuesto");

    try { return raw ? JSON.parse(raw) : { ok: true }; }
    catch { return { ok: true, raw }; }
  };




  const respondPresupuestoRequest = async (data) => {
    try {
      const payload = {
        response_status: data.approved ? "aprobado" : "negado",
        response_attachments: data.responseAttachments || null,
        response_notes: data.notes || null,
        status: "pending",
        responded_at: new Date().toISOString(),
      };

      // Asumiendo estilo REST: /presupuesto-requests/:id
      const response = await fetch(
        apiConfig.getUrl(apiConfig.endpoints.presupuestoRequests, data.requestId),
        {
          method: "PUT", // o "PATCH" si tu backend lo maneja así
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          errorData.error ||
          "Error al actualizar la solicitud de presupuesto"
        );
      }

      const result = await response.json().catch(() => null);
      return result;
    } catch (error) {
      console.error("Error respondiendo solicitud de presupuesto:", error);
      throw error; // lo manejas donde llames respondPresupuestoRequest
    }
  };

  const insertContract = async (contractData) => {
    const url = apiConfig.getUrl("/api/contracts/create/index.php");

    const payload = {
      ...contractData,
      created_by: user?.id || null,
    };

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });

    const contentType = response.headers.get("content-type") || "";
    const data = contentType.includes("application/json")
      ? await response.json().catch(() => null)
      : await response.text().catch(() => "");

    if (!response.ok) {
      const msg =
        (data && (data.error || data.message)) ||
        (typeof data === "string" ? data : "") ||
        `${response.status} ${response.statusText}`;
      throw new Error(`Error creando contrato: ${msg}`);
    }

    return data;
  };



  const insertContractHistory = async (historyEntry) => {
    try {
      // ✅ Compat: acepta contractHistory / contractsHistory / history
      const endpoint =
        apiConfig?.endpoints?.contractHistory ||
        apiConfig?.endpoints?.contractsHistory ||
        apiConfig?.endpoints?.history ||
        "";

      // ✅ Si no existe el endpoint, NO rompemos el flujo
      if (typeof endpoint !== "string" || !endpoint.trim()) {
        console.warn(
          "⚠️ Historial deshabilitado: endpoint contractHistory no definido en apiConfig.endpoints"
        );
        return null;
      }

      const response = await fetch(apiConfig.getUrl(endpoint), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        // ✅ Importante para prod (sesión/cookies)
        credentials: "include",
        body: JSON.stringify(historyEntry ?? {}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(
          "Error al registrar en el historial:",
          errorData.message || errorData.error || response.statusText
        );
        // No lanzamos error para no tumbar el guardado del contrato
        return null;
      }

      const result = await response.json().catch(() => null);
      return result;
    } catch (err) {
      console.error("Error de red al registrar historial:", err);
      // Tampoco relanzamos para no romper el flujo
      return null;
    }
  };
  const updateContract = async (contractId, updateData = {}) => {
    console.log("═══════════════════════════════════════════════════════");
    console.log("🔧 updateContract() - INICIO");
    console.log("Contract ID:", contractId);
    console.log("Update Data:", updateData);
    console.log("═══════════════════════════════════════════════════════");

    const url = apiConfig.getUrl(apiConfig.endpoints.contractsUpdate);
    console.log("📡 URL del endpoint:", url);

    const payload = {
      id: contractId,
      ...(updateData && typeof updateData === "object" ? updateData : {}),
    };

    console.log("📦 Payload completo a enviar:", payload);

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      console.log("📡 HTTP Status:", response.status);
      console.log("📡 HTTP Status Text:", response.statusText);

      const contentType = response.headers.get("content-type") || "";
      console.log("📡 Content-Type:", contentType);

      const responseText = await response.text();
      console.log("📥 Response RAW:", responseText);

      let result;
      try {
        result = JSON.parse(responseText);
        console.log("✅ Response JSON:", result);
      } catch (e) {
        console.error("❌ Error parseando JSON:", e);
        throw new Error(`Respuesta inválida del servidor: ${responseText}`);
      }

      if (!response.ok || !result.ok) {
        console.error("❌ Server returned error:", result);
        throw new Error(result.error || result.message || "Error actualizando contrato");
      }

      console.log("═══════════════════════════════════════════════════════");
      console.log("✅ updateContract() - SUCCESS");
      console.log("═══════════════════════════════════════════════════════");

      return result;
    } catch (error) {
      console.log("═══════════════════════════════════════════════════════");
      console.error("❌ updateContract() - ERROR COMPLETO:", error);
      console.error("❌ Error Stack:", error.stack);
      console.log("═══════════════════════════════════════════════════════");
      throw error;
    }
  };

  const handleSubmit = async (e, saveWithoutAdvancing = false) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // ✅ ==================== LOGS DE DEPURACIÓN ====================
    console.log("🚀 ==========================================");
    console.log("🚀 INICIO handleSubmit");
    console.log("🚀 userRole:", userRole);
    console.log("🚀 isEditing:", isEditing);
    console.log("🔍 Estado de firma del solicitante:", {
      firma_solicitante_url: contract.firma_solicitante_url,
      firma_solicitante_url_file: contract.firma_solicitante_url_file,
      firma_solicitante_url_preview: contract.firma_solicitante_url_preview,
      tipoDeFile: typeof contract.firma_solicitante_url_file,
      esFile: contract.firma_solicitante_url_file instanceof File,
      nombreArchivo: contract.firma_solicitante_url_file?.name || "NO HAY"
    });
    console.log("🚀 ==========================================");
    // ✅ ============================================================

    // ==================== ADMINISTRADOR: GUARDADO UNIVERSAL ====================
    if (userRole === "administrador" && isEditing) {
      try {
        console.log("═══════════════════════════════════════════════════════");
        console.log("🔧 ADMINISTRADOR - Guardando cambios");
        console.log("📌 Contract ID:", contract.id);

        // ✅ Mapeo COMPLETO y CORRECTO de todos los campos
        const adminUpdateData = {
          // Fechas
          fecha_solicitud: contract.fechaSolicitud || null,
          fecha_comite: contract.fechaComite || null,
          fecha_inicio_contrato: contract.fechaInicioContrato || null,
          fecha_fin_contrato: contract.fechaFinContrato || null,

          // Información básica
          centro_costos: contract.centroCostos || null,
          tipo_contrato: contract.tipoContrato || null,
          secretaria: contract.secretaria || null,
          modalidad_seleccion: contract.modalidadSeleccion || null,
          objeto_contractual: contract.objetoContractual || null,
          duracion_estimada: contract.duracionEstimada || null,
          nombre_contratista: contract.nombreContratista || null,

          // Plan de Desarrollo
          programa: Array.isArray(contract.programa) ? contract.programa.join(",") : contract.programa || null,
          programa_aprobado: Boolean(contract.programa_aprobado),
          indicador: Array.isArray(contract.indicador) ? contract.indicador.join(",") : contract.indicador || null,
          indicador_aprobado: Boolean(contract.indicador_aprobado),
          bpin: Array.isArray(contract.bpin) ? contract.bpin.join(",") : contract.bpin || null,
          bpin_aprobado: Boolean(contract.bpin_aprobado),

          // Presupuesto
          naturaleza_gasto: contract.naturalezaGasto || null,
          presupuesto_total: contract.presupuestoTotal ?
            parseFloat(String(contract.presupuestoTotal).replace(/[.,\s]/g, "")) : null,
          discriminacion_rubros: Array.isArray(contract.discriminacionRubros)
            ? contract.discriminacionRubros.join(",")
            : contract.discriminacionRubros || null,
          rubro: Array.isArray(contract.rubros) ? contract.rubros.join("||") : contract.rubros || null,
          nombre_rubro: Array.isArray(contract.nombresRubros)
            ? contract.nombresRubros.join("||")
            : contract.nombresRubros || null,
          valor: Array.isArray(contract.valores) ? contract.valores.join("||") : contract.valores || null,
          forma_pago: contract.formaPago || null,

          // ✅ Presupuesto por indicador
          indicador_presupuesto: JSON.stringify(indicadorPresupuestoSelections),

          // Solicitante
          nombre_solicitante: contract.nombreSolicitante || null,
          cargo_solicitante: contract.cargoSolicitante || null,
          firma_solicitante_url: contract.firma_solicitante_url || null,

          // Aprobaciones y firmas
          aprobacion_administrativa: contract.aprobacion_administrativa || null,
          firma_administrativa_url: contract.firmaAdministrativa_url || contract.firma_administrativa_url || null,

          aprobacion_hacienda: contract.aprobacion_hacienda || null,
          firma_hacienda_url: contract.firmaHacienda_url || contract.firma_hacienda_url || null,
          hay_dinero_rubro: contract.hay_dinero_rubro ?? null,
          se_puede_ejecutar: contract.se_puede_ejecutar ?? null,
          observaciones_hacienda: contract.observaciones_hacienda || null,

          aprobacion_alcalde: contract.aprobacion_alcalde || null,
          firma_alcalde_url: contract.firmaAlcalde_url || contract.firma_alcalde_url || null,

          // Jurídica
          tipo_contrato_aprobado: Boolean(contract.tipo_contrato_aprobado),
          modalidad_seleccion_aprobada: Boolean(contract.modalidad_seleccion_aprobada),
          objeto_contractual_aprobado: Boolean(contract.objeto_contractual_aprobado),
          duracion_estimada_aprobada: Boolean(contract.duracion_estimada_aprobada),
          presupuesto_aprobado: Boolean(contract.presupuesto_aprobado),
          observaciones_juridica: contract.observaciones_juridica || null,
          numero_acta: contract.numeroActa || null,

          // Observaciones generales
          observaciones: contract.observaciones || null,
          radicado: contract.radicado || null,

          // Bancop
          bancop_url: contract.bancop_url || null,
          bancop_filename: contract.bancop_filename || null,

          // Fase y estado
          current_phase: contract.current_phase || 1,
          status: contract.status || "en_proceso",
        };

        console.log("📦 Total de campos a actualizar:", Object.keys(adminUpdateData).length);

        // ✅ ACTUALIZAR EN LA BASE DE DATOS
        const result = await updateContract(contract.id, adminUpdateData);
        console.log("✅ Respuesta del servidor:", result);

        // ✅ RECARGAR EL CONTRATO DESDE LA BD PARA VER LOS CAMBIOS
        console.log("🔄 Recargando contrato desde la base de datos...");
        await fetchContract(contract.id);

        // ✅ Registrar en historial
        try {
          await insertContractHistory({
            contract_id: contract.id,
            user_id: user.id,
            action: "admin_update",
            phase: adminUpdateData.current_phase,
            comments: "Actualización por administrador",
          });
          console.log("✅ Historial registrado");
        } catch (histError) {
          console.warn("⚠️ Error registrando historial:", histError);
        }

        console.log("═══════════════════════════════════════════════════════");
        alert("✅ Cambios guardados correctamente");

        setLoading(false);
        return;

      } catch (error) {
        console.log("═══════════════════════════════════════════════════════");
        console.error("❌ ERROR COMPLETO:", error);
        console.error("❌ Error Stack:", error.stack);
        console.log("═══════════════════════════════════════════════════════");
        setError("Error al guardar: " + (error.message || "Error desconocido"));
        alert("❌ Error al guardar: " + (error.message || "Error desconocido"));
        setLoading(false);
        return;
      }
    }
    if (userRole === "administrador_junior" && isEditing) {
      const phase = Number(contract?.current_phase ?? 0);

      if (phase >= 4) {
        setError("No tiene permisos para modificar contratos en fase de Alcalde o superior.");
        setLoading(false);
        return;
      }

      try {
        const juniorUpdateData = {
          fecha_solicitud: contract.fechaSolicitud || null,
          fecha_comite: contract.fechaComite || null,
          fecha_inicio_contrato: contract.fechaInicioContrato || null,
          fecha_fin_contrato: contract.fechaFinContrato || null,
          centro_costos: contract.centroCostos || null,
          tipo_contrato: contract.tipoContrato || null,
          secretaria: contract.secretaria || null,
          modalidad_seleccion: contract.modalidadSeleccion || null,
          objeto_contractual: contract.objetoContractual || null,
          duracion_estimada: contract.duracionEstimada || null,
          nombre_contratista: contract.nombreContratista || null,
          programa: Array.isArray(contract.programa) ? contract.programa.join(",") : contract.programa || null,
          programa_aprobado: Boolean(contract.programa_aprobado),
          indicador: Array.isArray(contract.indicador) ? contract.indicador.join(",") : contract.indicador || null,
          indicador_aprobado: Boolean(contract.indicador_aprobado),
          bpin: Array.isArray(contract.bpin) ? contract.bpin.join(",") : contract.bpin || null,
          bpin_aprobado: Boolean(contract.bpin_aprobado),
          naturaleza_gasto: contract.naturalezaGasto || null,
          presupuesto_total: contract.presupuestoTotal
            ? parseFloat(String(contract.presupuestoTotal).replace(/[.,\s]/g, ""))
            : null,
          discriminacion_rubros: Array.isArray(contract.discriminacionRubros)
            ? contract.discriminacionRubros.join(",")
            : contract.discriminacionRubros || null,
          rubro: Array.isArray(contract.rubros) ? contract.rubros.join("||") : contract.rubros || null,
          nombre_rubro: Array.isArray(contract.nombresRubros) ? contract.nombresRubros.join("||") : contract.nombresRubros || null,
          valor: Array.isArray(contract.valores) ? contract.valores.join("||") : contract.valores || null,
          forma_pago: contract.formaPago || null,
          indicador_presupuesto: JSON.stringify(indicadorPresupuestoSelections),
          nombre_solicitante: contract.nombreSolicitante || null,
          cargo_solicitante: contract.cargoSolicitante || null,
          firma_solicitante_url: contract.firma_solicitante_url || null,
          aprobacion_administrativa: contract.aprobacion_administrativa || null,
          firma_administrativa_url: contract.firma_administrativa_url || null,
          aprobacion_hacienda: contract.aprobacion_hacienda || null,
          firma_hacienda_url: contract.firma_hacienda_url || null,
          hay_dinero_rubro: contract.hay_dinero_rubro ?? null,
          se_puede_ejecutar: contract.se_puede_ejecutar ?? null,
          observaciones_hacienda: contract.observaciones_hacienda || null,
          tipo_contrato_aprobado: Boolean(contract.tipo_contrato_aprobado),
          modalidad_seleccion_aprobada: Boolean(contract.modalidad_seleccion_aprobada),
          objeto_contractual_aprobado: Boolean(contract.objeto_contractual_aprobado),
          duracion_estimada_aprobada: Boolean(contract.duracion_estimada_aprobada),
          presupuesto_aprobado: Boolean(contract.presupuesto_aprobado),
          observaciones_juridica: contract.observaciones_juridica || null,
          numero_acta: contract.numeroActa || null,
          observaciones: contract.observaciones || null,
          radicado: contract.radicado || null,
          bancop_url: contract.bancop_url || null,
          bancop_filename: contract.bancop_filename || null,
          current_phase: contract.current_phase || 1,
          status: contract.status || "en_proceso",
        };

        await updateContract(contract.id, juniorUpdateData);
        await fetchContract(contract.id);

        await insertContractHistory({
          contract_id: contract.id,
          user_id: user.id,
          action: "admin_junior_update",
          phase: juniorUpdateData.current_phase,
          comments: "Actualización por administrador junior",
        });

        alert("✅ Cambios guardados correctamente");
        setLoading(false);
        return;

      } catch (error) {
        setError("Error al guardar: " + (error.message || "Error desconocido"));
        alert("❌ Error al guardar: " + (error.message || "Error desconocido"));
        setLoading(false);
        return;
      }
    }


    // ✅ VALIDACIÓN DE FIRMAS
    console.log("🔍 Verificando validación de firma...");
    console.log("🔍 Condición: userRole === 'solicitante'?", userRole === "solicitante");
    console.log("🔍 Condición: !contract.firma_solicitante_url?", !contract.firma_solicitante_url);

    if (userRole === "solicitante" && !contract.firma_solicitante_url_file && !contract.firma_solicitante_url) {
      console.log("❌ BLOQUEADO POR VALIDACIÓN - Falta firma_solicitante_url");
      setError("Error: Debe subir su firma antes de guardar el contrato");
      setLoading(false);
      document.getElementById("firma_solicitante")?.parentElement?.parentElement?.scrollIntoView({ behavior: "smooth", block: "center" });
      const firmaContainer = document.getElementById("firma_solicitante")?.parentElement;
      if (firmaContainer) {
        firmaContainer.style.border = "2px solid #f59e0b";
        firmaContainer.style.borderRadius = "4px";
        firmaContainer.style.padding = "4px";
        setTimeout(() => {
          firmaContainer.style.border = "";
          firmaContainer.style.borderRadius = "";
          firmaContainer.style.padding = "";
        }, 5000);
      }
      return;
    }

    if (userRole === "planeacion" && contract.current_phase === 1 && contract.aprobacion_administrativa && !(contract.firmaAdministrativa || contract.firmaAdministrativa_url || contract.firma_administrativa_url)) {
      setError("Error: Debe subir su firma administrativa antes de guardar el contrato");
      setLoading(false);
      document.getElementById("firmaAdministrativa")?.parentElement?.parentElement?.scrollIntoView({ behavior: "smooth", block: "center" });
      const firmaContainer = document.getElementById("firmaAdministrativa")?.parentElement;
      if (firmaContainer) {
        firmaContainer.style.border = "2px solid #f59e0b";
        firmaContainer.style.borderRadius = "4px";
        firmaContainer.style.padding = "4px";
        setTimeout(() => {
          firmaContainer.style.border = "";
          firmaContainer.style.borderRadius = "";
          firmaContainer.style.padding = "";
        }, 5000);
      }
      return;
    }

    if (userRole === "hacienda" && contract.current_phase === 2 && contract.aprobacion_hacienda && !(contract.firmaHacienda || contract.firmaHacienda_url || contract.firma_hacienda_url)) {
      setError("Error: Debe subir su firma de hacienda antes de guardar el contrato");
      setLoading(false);
      document.getElementById("firmaHacienda")?.parentElement?.parentElement?.scrollIntoView({ behavior: "smooth", block: "center" });
      const firmaContainer = document.getElementById("firmaHacienda")?.parentElement;
      if (firmaContainer) {
        firmaContainer.style.border = "2px solid #f59e0b";
        firmaContainer.style.borderRadius = "4px";
        firmaContainer.style.padding = "4px";
        setTimeout(() => {
          firmaContainer.style.border = "";
          firmaContainer.style.borderRadius = "";
          firmaContainer.style.padding = "";
        }, 5000);
      }
      return;
    }

    if (userRole === "alcalde" && contract.current_phase === 4 && contract.aprobacion_alcalde && !(contract.firmaAlcalde || contract.firmaAlcalde_url || contract.firma_alcalde_url)) {
      setError("Error: Debe subir su firma de alcalde antes de guardar el contrato");
      setLoading(false);
      document.getElementById("firmaAlcalde")?.parentElement?.parentElement?.scrollIntoView({ behavior: "smooth", block: "center" });
      const firmaContainer = document.getElementById("firmaAlcalde")?.parentElement;
      if (firmaContainer) {
        firmaContainer.style.border = "2px solid #f59e0b";
        firmaContainer.style.borderRadius = "4px";
        firmaContainer.style.padding = "4px";
        setTimeout(() => {
          firmaContainer.style.border = "";
          firmaContainer.style.borderRadius = "";
          firmaContainer.style.padding = "";
        }, 5000);
      }
      return;
    }

    console.log("✅ Pasó todas las validaciones de firma");

    try {
      console.log("🔍 ESTADO ORIGINAL DEL CONTRATO (camelCase):", {
        objetoContractual: contract.objetoContractual,
        nombreContratista: contract.nombreContratista,
        duracionEstimada: contract.duracionEstimada,
        presupuestoTotal: contract.presupuestoTotal,
        fechaInicioContrato: contract.fechaInicioContrato,
        fechaFinContrato: contract.fechaFinContrato,
      });

      const contractData = {
        fecha_solicitud: contract.fechaSolicitud || null,
        centro_costos: contract.centroCostos || null,
        tipo_contrato: contract.tipoContrato || null,
        secretaria: contract.secretaria || null,
        modalidad_seleccion: contract.modalidadSeleccion || null,
        objeto_contractual: contract.objetoContractual || null,
        duracion_estimada: contract.duracionEstimada || null,
        fecha_inicio_contrato: contract.fechaInicioContrato || null,
        fecha_fin_contrato: contract.fechaFinContrato || null,
        nombre_contratista: contract.nombreContratista || null,
        programa: Array.isArray(contract.programa) ? contract.programa.join(",") : contract.programa || null,
        programa_aprobado: contract.programa_aprobado || false,
        indicador: Array.isArray(contract.indicador) ? contract.indicador.join(",") : contract.indicador || null,
        indicador_aprobado: contract.indicador_aprobado || false,
        bpin: Array.isArray(contract.bpin) ? contract.bpin.join(",") : contract.bpin || null,
        bpin_aprobado: contract.bpin_aprobado || false,
        naturaleza_gasto: contract.naturalezaGasto || null,
        presupuesto_total: contract.presupuestoTotal || null,
        discriminacion_rubros: Array.isArray(contract.discriminacionRubros) ? contract.discriminacionRubros.join(",") : contract.discriminacionRubros || null,
        rubro: Array.isArray(contract.rubros) ? contract.rubros.join("||") : contract.rubros || null,
        nombre_rubro: Array.isArray(contract.nombresRubros) ? contract.nombresRubros.join("||") : contract.nombresRubros || null,
        valor: Array.isArray(contract.valores) ? contract.valores.join("||") : contract.valores || null,
        forma_pago: contract.formaPago || null,

        // ✅ Presupuesto por indicador
        indicador_presupuesto: JSON.stringify(indicadorPresupuestoSelections),

        nombre_solicitante: contract.nombreSolicitante || null,
        cargo_solicitante: contract.cargoSolicitante || null,
        firma_solicitante: contract.firmaSolicitante || null,
        aprobacion_administrativa: contract.aprobacion_administrativa || null,
        firma_administrativa: contract.firmaAdministrativa || null,
        aprobacion_hacienda: contract.aprobacion_hacienda || null,
        firma_hacienda: contract.firmaHacienda || null,
        aprobacion_alcalde: contract.aprobacion_alcalde || null,
        firma_alcalde: contract.firmaAlcalde || null,
        numero_acta: contract.numeroActa || null,
        fecha_comite: contract.fechaComite || null,
        observaciones: contract.observaciones || null,
        radicado: contract.radicado || null,
        hay_dinero_rubro: contract.hay_dinero_rubro || null,
        se_puede_ejecutar: contract.se_puede_ejecutar || null,
        observaciones_hacienda: contract.observaciones_hacienda || null,
        tipo_contrato_aprobado: contract.tipo_contrato_aprobado || false,
        modalidad_seleccion_aprobada: contract.modalidad_seleccion_aprobada || false,
        objeto_contractual_aprobado: contract.objeto_contractual_aprobado || false,
        duracion_estimada_aprobada: contract.duracion_estimada_aprobada || false,
        presupuesto_aprobado: contract.presupuesto_aprobado || false,
        observaciones_juridica: contract.observaciones_juridica || "",
        firma_solicitante_url: contract.firma_solicitante_url || null,
        firma_administrativa_url: contract.firmaAdministrativa_url || contract.firma_administrativa_url || null,
        firma_hacienda_url: contract.firmaHacienda_url || contract.firma_hacienda_url || null,
        firma_alcalde_url: contract.firmaAlcalde_url || contract.firma_alcalde_url || null,
        bancop_url: contract.bancop_url || null,
        bancop_filename: contract.bancop_filename || null,
      };

      const now = new Date();
      const hoyISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      contractData.fecha_solicitud = contract.fechaSolicitud || hoyISO;

      if (!isEditing && (userRole === "solicitante" || userRole === "juridica")) {
        try {
          const yr = new Date().getFullYear();
          const nuevoRadicado = await generateConsecutiveRadicado(token, yr);
          console.log("✅ Radicado generado:", nuevoRadicado);
          contractData.radicado = nuevoRadicado || `0001-${yr}`;
        } catch (error) {
          console.error("❌ Error al generar radicado:", error);
          const yr = new Date().getFullYear();
          contractData.radicado = `0001-${yr}`;
        }
      }

      if (!isEditing) {
        contractData.current_phase = 1;
        contractData.status = "en_proceso";
      }

      const fieldsToRemove = ["id", "created_at", "updated_at"];
      fieldsToRemove.forEach((field) => {
        if (contractData.hasOwnProperty(field)) {
          console.log(`🗑️ Eliminando: ${field}`);
          delete contractData[field];
        }
      });

      const booleanFields = ["programa_aprobado", "indicador_aprobado", "bpin_aprobado", "tipo_contrato_aprobado", "modalidad_seleccion_aprobada", "objeto_contractual_aprobado", "duracion_estimada_aprobada", "presupuesto_aprobado", "hay_dinero_rubro", "se_puede_ejecutar"];

      booleanFields.forEach((field) => {
        if (contractData.hasOwnProperty(field) && contractData[field] !== null) {
          contractData[field] = Boolean(contractData[field]);
        }
      });

      if (contractData.presupuesto_total) {
        const cleanNumber = String(contractData.presupuesto_total).replace(/[.,\s]/g, "");
        contractData.presupuesto_total = parseFloat(cleanNumber) || 0;
      }

      const cleanContractData = Object.fromEntries(
        Object.entries(contractData).filter(([_, v]) => v !== undefined)
      );

      const payload = {
        ...cleanContractData,
        created_by: user.id,
      };

      if (typeof contract.bancop_filename !== "undefined") {
        contractData.bancop_filename = contract.bancop_filename || null;
      }

      // PLANEACIÓN
      if (userRole === "planeacion" && isEditing && Number(contract.current_phase ?? 1) === 1) {
        try {
          const planeacionData = {
            programa_aprobado: contract.programa_aprobado,
            indicador_aprobado: contract.indicador_aprobado,
            bpin_aprobado: contract.bpin_aprobado,
            observaciones: contract.observaciones || "",
            aprobacion_administrativa: contract.aprobacion_administrativa,
            firma_administrativa: contract.firmaAdministrativa || contract.firma_administrativa || "",
            firma_administrativa_url: contract.firmaAdministrativa_url || contract.firma_administrativa_url || "",
            bancop_url: contract.bancop_url || "",
            bancop_filename: contract.bancop_filename || "",
          };

          const allApproved = contract.programa_aprobado === true && contract.indicador_aprobado === true && contract.bpin_aprobado === true && contract.aprobacion_administrativa === "aprobado";

          const statusUpdate = {
            status: allApproved ? "en_proceso" : "rechazado",
            current_phase: allApproved ? 2 : 1,
          };

          const finalUpdateData = { ...planeacionData, ...statusUpdate };

          await updateContract(contractId, finalUpdateData);

          const historyEntry = {
            contract_id: contractId,
            user_id: user.id,
            action: "update",
            phase: statusUpdate.current_phase,
            comments: contract.observaciones || "",
          };

          await insertContractHistory(historyEntry);

          if (Number(statusUpdate.current_phase) === 2 && statusUpdate.status === "en_proceso") {
            try {
              const mergedContractData = { ...contract, ...finalUpdateData };
              await sendContractNotification(3, mergedContractData, "notification");
            } catch (notifyError) {
              console.error("❌ Error al notificar a Hacienda:", notifyError);
            }
          }

          navigate("/dashboard");
          return;
        } catch (error) {
          console.error("❌ Error al actualizar contrato como planeación:", error);
          setError("Error al actualizar el contrato en planeación: " + (error.message || "Error desconocido"));
          setLoading(false);
          return;
        }
      }

      // HACIENDA
      if (userRole === "hacienda" && isEditing && Number(contract.current_phase) === 2) {
        try {
          const asBool = (v) => v === true || v === 1 || v === "1" || String(v).toLowerCase() === "true";
          const hayDinero = asBool(contract.hay_dinero_rubro) ? 1 : 0;
          const sePuede = asBool(contract.se_puede_ejecutar) ? 1 : 0;
          const decision = String(contract.aprobacion_hacienda || "").toLowerCase().trim();

          if (decision !== "aprobado" && decision !== "rechazado") {
            throw new Error(`Debe seleccionar 'Aprobado' o 'Rechazado' antes de continuar.`);
          }

          if (decision === "aprobado" && (hayDinero === 0 || sePuede === 0)) {
            throw new Error("Para APROBAR, debe marcar los campos de disponibilidad presupuestal.");
          }

          const toPhase = decision === "aprobado" ? 3 : 1;
          const nextStatus = decision === "aprobado" ? "en_proceso" : "rechazado";

          const haciendaData = {
            hay_dinero_rubro: hayDinero,
            se_puede_ejecutar: sePuede,
            observaciones_hacienda: contract.observaciones_hacienda || "",
            aprobacion_hacienda: decision,
            current_phase: toPhase,
            status: nextStatus,
            firma_hacienda: contract.firmaHacienda || contract.firma_hacienda || "",
            firma_hacienda_url: contract.firmaHacienda_url || contract.firma_hacienda_url || "",
          };

          await updateContract(contract.id, haciendaData);

          await insertContractHistory({
            contract_id: contract.id,
            user_id: user.id,
            action: decision === "aprobado" ? "advance" : "reject",
            phase: toPhase,
            comments: contract.observaciones_hacienda || "Actualización por Hacienda",
          });

          try {
            const mergedContractData = { ...contract, ...haciendaData };
            await sendContractNotification(toPhase, mergedContractData);
          } catch (notifyError) {
            console.error("Error al enviar notificación:", notifyError);
          }

          navigate("/dashboard");
          return;
        } catch (error) {
          console.error("Error en el proceso de Hacienda:", error);
          setError(error.message || "Error desconocido al actualizar como Hacienda");
          setLoading(false);
          return;
        }
      }

      // JURÍDICA FASE 5
      if (userRole === "juridica" && isEditing && Number(contract.current_phase) === 5) {
        try {
          const numeroActaUI = String(contract.numeroActa ?? "").trim();
          const fechaComiteUI = String(contract.fechaComite ?? "").trim();

          if (!numeroActaUI) throw new Error("Falta el número de acta.");
          if (!fechaComiteUI) throw new Error("Falta la fecha del comité.");

          const updateData = {
            numero_acta: numeroActaUI,
            fecha_comite: fechaComiteUI,
          };

          const updResp = await updateContract(contractId, updateData);
          const updatedRow = updResp?.contract ?? updResp ?? null;

          await insertContractHistory({
            contract_id: contract.id,
            user_id: user.id,
            action: "update_committee_info",
            phase: 5,
            comments: "Actualización de información del comité (fase 5)",
          });

          setContract((prev) => {
            if (updatedRow && typeof updatedRow === "object") {
              return {
                ...prev,
                numeroActa: updatedRow.numero_acta ?? updateData.numero_acta,
                fechaComite: updatedRow.fecha_comite ?? updateData.fecha_comite,
                numero_acta: updatedRow.numero_acta ?? updateData.numero_acta,
                fecha_comite: updatedRow.fecha_comite ?? updateData.fecha_comite,
              };
            }
            return {
              ...prev,
              numeroActa: updateData.numero_acta,
              fechaComite: updateData.fecha_comite,
              numero_acta: updateData.numero_acta,
              fecha_comite: updateData.fecha_comite,
            };
          });

          alert("Información del comité actualizada correctamente");
          navigate("/dashboard");
          return;
        } catch (error) {
          console.error("❌ Error al actualizar info de comité:", error);
          setError("Error al actualizar la información del comité: " + (error?.message || "Error desconocido"));
          setLoading(false);
          return;
        }
      }

      // JURÍDICA FASE 3
      if (userRole === "juridica" && isEditing && Number(contract.current_phase) === 3) {
        try {
          const toBool = (v) => v === true || v === 1 || v === "1" || String(v).toLowerCase() === "true";

          const allApproved = toBool(contract.tipo_contrato_aprobado) && toBool(contract.modalidad_seleccion_aprobada) && toBool(contract.objeto_contractual_aprobado) && toBool(contract.duracion_estimada_aprobada) && toBool(contract.presupuesto_aprobado);

          const juridicaData = {
            tipo_contrato_aprobado: toBool(contract.tipo_contrato_aprobado),
            modalidad_seleccion_aprobada: toBool(contract.modalidad_seleccion_aprobada),
            objeto_contractual_aprobado: toBool(contract.objeto_contractual_aprobado),
            duracion_estimada_aprobada: toBool(contract.duracion_estimada_aprobada),
            presupuesto_aprobado: toBool(contract.presupuesto_aprobado),
            aprobacion_juridica: allApproved ? 1 : 0,
            observaciones_juridica: String(contract.observaciones_juridica ?? contract.observacionesJuridica ?? "").trim(),
            numero_acta: String(contract.numeroActa ?? contract.numero_acta ?? "").trim(),
            fecha_comite: contract.fechaComite ?? contract.fecha_comite ?? null,
          };

          const updResp = await updateContract(contract.id, juridicaData);
          const updatedRow = updResp?.contract ?? updResp ?? null;

          const phaseForHistory = updatedRow?.current_phase != null ? Number(updatedRow.current_phase) : allApproved ? 4 : 3;

          await insertContractHistory({
            contract_id: contract.id,
            user_id: user.id,
            action: "update",
            phase: phaseForHistory,
            comments: juridicaData.observaciones_juridica || "",
          });

          if (allApproved) {
            try {
              const mergedContractData = {
                ...contract,
                ...juridicaData,
                ...(updatedRow && typeof updatedRow === "object" ? updatedRow : {}),
              };
              await sendContractNotification(4, mergedContractData);
            } catch (notifyError) {
              console.error("Error al enviar notificación al alcalde:", notifyError);
            }
          }

          navigate("/dashboard");
          return;
        } catch (error) {
          console.error("Error al actualizar contrato como jurídica:", error);
          setError("Error al actualizar el contrato: " + (error.message || "Error desconocido"));
          setLoading(false);
          return;
        }
      }

      // ALCALDE
      if (userRole === "alcalde" && isEditing && Number(contract.current_phase) === 4) {
        try {
          if (!contract.aprobacion_alcalde) {
            setError("Debe seleccionar 'Aprobado' o 'Rechazado' antes de guardar");
            setLoading(false);
            return;
          }

          const updateData = {
            aprobacion_alcalde: contract.aprobacion_alcalde,
            observaciones: contract.observaciones || "",
            firma_alcalde: contract.firmaAlcalde || contract.firma_alcalde,
            firma_alcalde_url: contract.firmaAlcalde_url || contract.firma_alcalde_url,
          };

          if (contract.aprobacion_alcalde === "aprobado") {
            updateData.status = "aprobado";
            updateData.current_phase = 5;
          } else {
            updateData.status = "rechazado";
            updateData.current_phase = 4;
          }

          await updateContract(contract.id, updateData);

          await insertContractHistory({
            contract_id: contract.id,
            user_id: user.id,
            action: "update",
            phase: updateData.current_phase,
            comments: contract.observaciones || "",
          });

          if (contract.aprobacion_alcalde === "aprobado") {
            try {
              const mergedContractData = { ...contract, ...updateData };
              await sendContractNotification(5, mergedContractData, "approval");
            } catch (notifyError) {
              console.error("Error al enviar notificación final:", notifyError);
            }
          }

          navigate("/dashboard");
          return;
        } catch (error) {
          console.error("Error completo al actualizar como alcalde:", error);
          setError("Error al actualizar el contrato: " + (error.message || "Error desconocido"));
          setLoading(false);
          return;
        }
      }

      let result;

      // SOLICITANTE EDITANDO
      if (isEditing && userRole === "solicitante") {
        await updateContract(contractId, cleanContractData);
        result = { success: true };
      }
      // CONTRATO NUEVO
      else if (!isEditing) {
        const userId = user.id;
        if (!userId) {
          throw new Error("No hay usuario activo. Por favor inicie sesión nuevamente.");
        }

        const realId = (contract?.id && contract.id !== "nuevo") ? String(contract.id) : (contractId && contractId !== "nuevo") ? String(contractId) : null;

        if (realId) {
          await updateContract(realId, cleanContractData);
          result = { success: true };
        } else {
          // ✅ DEBUG: Ver estado del contrato antes de enviar
          console.log("═══════════════════════════════════════════════════════");
          console.log("🔍 DEBUG - Estado del contrato ANTES de enviar:");
          console.log("contract.firma_solicitante_url_file:", contract.firma_solicitante_url_file);
          console.log("Es un File?", contract.firma_solicitante_url_file instanceof File);
          console.log("contract.firma_solicitante_url:", contract.firma_solicitante_url);
          console.log("contract.firma_solicitante_url_preview:", contract.firma_solicitante_url_preview);
          console.log("Objeto contract completo (solo firmas):", {
            firma_solicitante_url: contract.firma_solicitante_url,
            firma_solicitante_url_file: contract.firma_solicitante_url_file,
            firma_solicitante_url_preview: contract.firma_solicitante_url_preview,
            firmaSolicitante: contract.firmaSolicitante,
          });
          console.log("═══════════════════════════════════════════════════════");

          const fd = new FormData();

          Object.entries(cleanContractData).forEach(([k, v]) => {
            if (v !== undefined && v !== null) {
              fd.append(k, String(v));
            }
          });

          fd.append("created_by", String(userId));

          if (contract.firma_solicitante_url_file instanceof File) {
            fd.append("firma_solicitante", contract.firma_solicitante_url_file);
            console.log("✅ Enviando firma del solicitante al backend:", {
              nombre: contract.firma_solicitante_url_file.name,
              tamaño: contract.firma_solicitante_url_file.size,
              tipo: contract.firma_solicitante_url_file.type
            });
          } else {
            console.log("❌ NO se envía firma: firma_solicitante_url_file no es un File o está vacío");
          }

          if (contract.firmaAdministrativa instanceof File) fd.append("firma_administrativa", contract.firmaAdministrativa);
          if (contract.firmaHacienda instanceof File) fd.append("firma_hacienda", contract.firmaHacienda);
          if (contract.firmaAlcalde instanceof File) fd.append("firma_alcalde", contract.firmaAlcalde);

          // ✅ DEBUG: Ver qué se envía en el FormData
          console.log("📤 ENVIANDO AL BACKEND:");
          for (let [key, value] of fd.entries()) {
            if (value instanceof File) {
              console.log(`  - ${key}: [FILE] ${value.name} (${value.size} bytes)`);
            } else {
              console.log(`  - ${key}: ${value}`);
            }
          }

          const resp = await fetch(apiConfig.getUrl("/api/contracts/create/index.php"), {
            method: "POST",
            body: fd,
            credentials: "include",
          });

          const txt = await resp.text();
          console.log("📥 RESPUESTA RAW DEL BACKEND:", txt);

          let data;
          try {
            data = JSON.parse(txt);
          } catch {
            data = { ok: false, error: txt };
          }

          if (!resp.ok || !data?.ok) {
            throw new Error(data?.error || "Error creando contrato");
          }

          const newContract = data.contract;

          if (!newContract || !newContract.id) {
            throw new Error("Backend no devolvió ID del contrato");
          }

          try {
            if (typeof setContract === "function") {
              setContract((prev) => ({ ...prev, ...newContract }));
            }
          } catch { }

          // ✅ Recargar el contrato desde la BD para asegurar que se vea la firma
          if (newContract.id && typeof fetchContract === "function") {
            await fetchContract(newContract.id);
          }

          try {
            if (typeof setContractId === "function") {
              setContractId(String(newContract.id));
            }
          } catch { }

          result = { success: true, contract: newContract };

          try {
            await sendContractNotification(1, newContract, "notification");
          } catch (notifyError) {
            console.error("Error al enviar notificación inicial:", notifyError);
          }
        }
      }

      // HISTORIAL
      if (result?.success && userRole !== "alcalde") {
        const historyEntry = {
          contract_id: isEditing ? contractId : result.contract?.id,
          user_id: user.id,
          action: isEditing ? "update" : "create",
          phase: isEditing ? cleanContractData.current_phase : 1,
          comments: userRole === "hacienda" ? contract.observaciones_hacienda : userRole === "juridica" ? contract.observaciones_juridica : contract.observaciones,
        };
        console.log("📝 Historial:", historyEntry);
      }

      navigate("/dashboard");
    } catch (error) {
      console.error("❌ ERROR GUARDANDO CONTRATO:", error);
      setError("Error al guardar el contrato: " + (error.message || "Verifique los datos e intente nuevamente"));
    } finally {
      setLoading(false);
    }
  };





  const handleSaveWithoutAdvancing = (e) => {
    e.preventDefault();

    handleSubmit(e, true);
  };

  // Ejemplo de helper genérico que venimos usando
  // import { executeQuery } from "../services/db"; 

  const testDbConnection = async () => {
    try {
      setLoading(true);

      // 1. Probar conexión básica: SELECT 1
      const pingResult = await executeQuery({
        sql: "SELECT 1 AS ok",
        params: [],
      });

      if (!pingResult?.success) {
        alert(`Error probando la conexión a la base de datos: ${pingResult?.error || "Error desconocido"}`);
        return;
      }

      // 2. (Opcional) Listar algunas tablas para verificar permisos
      const tablesResult = await executeQuery({
        sql: `
        SELECT table_name
        FROM information_schema.tables
        WHERE table_schema = 'public'
        ORDER BY table_name
      `,
        params: [],
      });

      if (!tablesResult?.success) {
        alert(`Conexión establecida pero no se pudieron listar tablas: ${tablesResult?.error || "Error desconocido"}`);
        return;
      }

      const tables = tablesResult.rows || [];

      alert(
        `Conexión SQL exitosa.\n` +
        `Tablas en el esquema 'public': ${tables.length}` +
        (tables.length > 0 ? `\nEjemplo: ${tables.slice(0, 5).map(t => t.table_name).join(", ")}` : "")
      );
    } catch (error) {
      console.error("Error al probar la conexión SQL:", error);
      alert(`Error al probar la conexión: ${error.message || "Error desconocido"}`);
    } finally {
      setLoading(false);
    }
  };




  if (loading && !contract) {
    return <div className="container mx-auto p-4">Cargando...</div>;
  }
  // Renderiza CDPGenerator si showCdpForm es true
  if (showCdpForm) {
    return (
      <div className="main-content">
        <CDPGenerator
          contratoFirmado={{
            ...contract,
            // Pasar la firma del alcalde solo a usuarios autorizados
            firma_alcalde_url: (user && isAuthorizedUser(user.email)) ? contract.firma_alcalde_url : null
          }}
          onClose={() => setShowCdpForm(false)}
          user={user}
        />
        <button
          onClick={() => setShowCdpForm(false)}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            padding: '0.5rem 1rem',
            borderRadius: '0.25rem',
            border: 'none',
            cursor: 'pointer',
            marginTop: '1rem'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
        >
          Volver a la Necesidad de Contratación
        </button>
      </div>
    );
  }
  // Asegúrate de tener arriba:
  // import AdquisicionBienes from "./AdquisicionBienes";

  if (showAdquisicionForm) {
    const mode = selectedAlmacenRequest ? "respuesta" : "solicitud";

    return (
      <AdquisicionBienes
        contratoFirmado={contract}
        mode={mode}
        requestId={selectedAlmacenRequest?.id || null}
        onClose={() => {
          setShowAdquisicionForm(false);
          setSelectedAlmacenRequest(null);
        }}
      />
    );
  }



  // Renderiza RPGenerator si showCdpForm es true
  if (showRpForm) {
    return (
      <div className="main-content">
        <RPGenerator
          contratoFirmado={{
            ...contract,
            // ✅ CORRECCIÓN: Asegurar que presupuesto_total esté disponible en snake_case
            presupuesto_total: contract.presupuestoTotal || contract.presupuesto_total,
            // Pasar la firma del alcalde solo a usuarios autorizados
            firma_alcalde_url: (user && isAuthorizedUser(user.email)) ? contract.firma_alcalde_url : null
          }}
          onClose={() => setShowRpForm(false)}
          user={user}
        />
        <button
          onClick={() => setShowRpForm(false)}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            padding: '0.5rem 1rem',
            borderRadius: '0.25rem',
            border: 'none',
            cursor: 'pointer',
            marginTop: '1rem'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
        >
          Volver a la Necesidad de Contratación
        </button>
      </div>
    );
  }
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "linear-gradient(to bottom, #064e40, #0d9488)",
        padding: "1rem", // Reducido de 2rem a 1rem para móviles
        boxSizing: "border-box", // Asegura que el padding no afecte el ancho total
      }}
    >
      <div
        className="mb-6 flex items-center justify-between"
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          width: "100%",
          flexDirection: window.innerWidth < 768 ? "column" : "row", // Cambia a columna en móviles
          gap: window.innerWidth < 768 ? "1rem" : "0", // Añade espacio entre elementos en móviles
        }}
      >
        <h1 style={{ color: "white" }} className="text-2xl font-bold">
          {isEditing ? "Ver/Editar Contrato" : "Nuevo Contrato"}
        </h1>

        <button
          onClick={() => navigate("/dashboard")}
          className="rounded bg-white px-4 py-2 text-gray-700 hover:bg-gray-100"
          style={{ width: window.innerWidth < 768 ? "100%" : "auto" }} // Botón de ancho completo en móviles
        >
          Volver
        </button>
      </div>

      {error && (
        <div
          className="mb-4 rounded-md bg-red-50 p-4 text-sm text-red-700"
          style={{ maxWidth: "1000px", margin: "0 auto", width: "100%" }}
        >
          {error}
        </div>
      )}

      <div className="container" style={{ backgroundColor: "white", maxWidth: "1000px", margin: "0 auto" }}>
        <div className="form-header">
          <img
            src={logo || "/placeholder.svg"}
            alt="Logo"
            className="logo"
            style={{ height: "100px", width: "auto" }}
          />
          <h2>NECESIDADES DE CONTRATACION</h2>
          <div className="form-id">
            <span style={{ display: "block" }}>Código: A-CBS-F-021</span>
            <span style={{ display: "block" }}>Versión: 07</span>
            <span style={{ display: "block" }}>Fecha Actualización: 14-01-2025</span>
          </div>

        </div>

        <form onSubmit={handleSubmit} className="form-content">
          <table className="form-table">
            <tbody>
              <tr>
                <td>
                  <div className="form-group">
                    <label htmlFor="fechaSolicitud" className="required">
                      Fecha de Solicitud
                    </label>
                    <input
                      type="date"
                      id="fechaSolicitud"
                      name="fechaSolicitud"
                      className="form-control"
                      value={contract.fechaSolicitud || ""}
                      onChange={handleChange}
                      required
                      disabled={!canEdit()}
                    />
                  </div>
                </td>
                <td>
                  <div className="form-group">
                    <label htmlFor="centroCostos" className="required">
                      Centro de Costos
                    </label>
                    <Select
                      id="centroCostos"
                      name="centroCostos"
                      options={centroCostosOptions}
                      value={
                        contract.centroCostos
                          ? centroCostosOptions.find(
                            (opt) => opt.value === contract.centroCostos
                          ) || null
                          : null
                      }
                      onChange={(selectedOption) => {
                        handleChange({
                          target: {
                            name: "centroCostos",
                            value: selectedOption ? selectedOption.value : "",
                            type: "select",
                          },
                        });
                      }}
                      isDisabled={!canEdit()}
                      placeholder="Seleccionar..."
                      isSearchable={true}
                      className="form-control-select"
                      classNamePrefix="select"
                      styles={{
                        control: (baseStyles) => ({
                          ...baseStyles,
                          minHeight: "38px",
                          height: "38px",
                          borderRadius: "4px",
                          borderColor: "#ccc",
                          boxShadow: "none",
                          "&:hover": {
                            borderColor: "#004d99",
                          },
                        }),
                        valueContainer: (baseStyles) => ({
                          ...baseStyles,
                          padding: "0 8px",
                          fontSize: window.innerWidth < 480 ? "12px" : "14px", // Fuente más pequeña en móviles
                        }),
                        input: (baseStyles) => ({
                          ...baseStyles,
                          margin: "0",
                          padding: "0",
                        }),
                        indicatorSeparator: () => ({
                          display: "none",
                        }),
                        dropdownIndicator: (baseStyles) => ({
                          ...baseStyles,
                          padding: window.innerWidth < 480 ? "0 4px" : "0 8px", // Padding reducido en móviles
                        }),
                        menu: (baseStyles) => ({
                          ...baseStyles,
                          marginTop: "0",
                          borderRadius: "4px",
                          boxShadow: "0 2px 5px rgba(0,0,0,0.15)",
                          zIndex: 9999, // Asegura que el menú aparezca por encima de otros elementos
                        }),
                        option: (baseStyles, state) => ({
                          ...baseStyles,
                          backgroundColor: state.isSelected
                            ? "#004d99"
                            : state.isFocused
                              ? "#f0f4f8"
                              : null,
                          color: state.isSelected ? "white" : "#333",
                          padding: window.innerWidth < 480 ? "6px 8px" : "8px 12px", // Padding reducido en móviles
                          fontSize: window.innerWidth < 480 ? "12px" : "14px", // Fuente más pequeña en móviles
                        }),
                      }}
                    />

                  </div>
                </td>
                <td>
                  {/* Secretarí­a */}
                  <div className="form-group">
                    <label htmlFor="secretaria" className="required">Secretaría</label>
                    <select
                      id="secretaria"
                      name="secretaria"
                      className="form-control"
                      value={contract.secretaria}
                      onChange={handleChange}
                      required
                      disabled={!canEdit()}
                    >
                      <option value="">Seleccionar...</option>
    <option value="Departamento Administrativo de Planeación">Departamento Administrativo de Planeación</option>
    <option value="Secretaría de Salud">Secretaría de Salud</option>
    <option value="Dirección Financiera">Dirección Financiera</option>
    <option value="Personería">Personería </option>
    <option value="Alcaldía">Alcaldía</option>
    <option value="Secretaría de Agricultura y Medio Ambiente">Secretaría de Agricultura y Medio Ambiente</option>
    <option value="Secretaría de Gobierno">Secretaría de Gobierno</option>
    <option value="Secretaría de Servicios Administrativos">Secretaría de Servicios Administrativos</option>
    <option value="Oficina de Comunicaciones">Oficina de Comunicaciones</option>
    <option value="Secretaría de Movilidad">Secretaría de Movilidad</option>
    <option value="Secretaría de Hacienda">Secretaría de Hacienda</option>
    <option value="Secretaría de Infraestructura">Secretaría de Infraestructura</option>
    <option value="Secretaría de Educación y Cultura">Secretaría de Educación y Cultura</option>
    <option value="Secretaría de Desarrollo y Bienestar Social">Secretaría de Desarrollo y Bienestar Social</option>
    <option value="Invicop">Invicop</option>
    <option value="Inder Copacabana">Inder Copacabana</option>
    <option value="Oficina Jurídica">Oficina Jurídica</option>
    <option value="Dirección Técnica de Control Interno">Dirección Técnica de Control Interno</option>
  </select>
                  </div>
                </td>
                <td>
                  {/* Campo de radicado - visible para planeación, hacienda, juridica o si ya tiene radicado */}
                  {(userRole === "planeacion" ||
                    userRole === "solicitante" ||
                    userRole === "hacienda" ||
                    userRole === "juridica" ||
                    userRole === "administrador" ||
                    userRole === "administrador_junior" ||
                    userRole === "alcalde" ||
                    (isEditing && contract.radicado)) && (
                      <div className="form-group">
                        <label htmlFor="radicado" className="block text-sm font-medium text-gray-700">
                          Número de Radicado {userRole === "solicitante" && <span className="text-red-500">*</span>}
                        </label>
                        <input
                          type="text"
                          id="radicado"
                          name="radicado"
                          value={contract.radicado}
                          onChange={handleChange}
                          required={userRole === "solicitante"}
                          readOnly={userRole === "solicitante"}
                          disabled={!canEditField("radicado")}
                          className="form-control"
                          style={userRole === "solicitante" ? { backgroundColor: "#f3f4f6" } : {}}
                        />
                        {userRole === "solicitante" && <p className="mt-1 text-xs text-gray-500"></p>}
                      </div>
                    )}
                </td>
              </tr>
            </tbody>
          </table>
          <div className="form-section">
            <h3 className="form-section-title">Información Jurídica</h3>
            <table className="form-table">
              <tbody>
                {/* Tipo de contrato + evaluación */}
                <tr>
                  <td>
                    <div className="form-group">
                      <label htmlFor="tipoContrato" className="required">
                        Tipo de Contrato
                      </label>
                      <select
                        id="tipoContrato"
                        name="tipoContrato"
                        className="form-control"
                        value={contract.tipoContrato}
                        onChange={handleChange}
                        required
                        disabled={!canEdit()}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="1-prestaciondeservicios">
                          1-Prestación de servicios persona natural o jurídica
                        </option>
                        <option value="2-suministros">2-Suministro</option>
                        <option value="3-compraventa">3-Compraventa</option>
                        <option value="4-arrendamiento">4-Arrendamiento</option>
                        <option value="5-obra">5-Obra</option>
                        <option value="6-comodato">6-Comodato</option>
                        <option value="7-iteradministrativo">7-Interadministrativo</option>
                        <option value="8-concesion">8-Concesión</option>
                        <option value="9-mandato">9-Mandato</option>
                        <option value="10-leasing">10-Leasing</option>
                        <option value="11-fiducia">11-Fiducia</option>
                        <option value="12-consultoria">12-Consultoría</option>
                        <option value="13-Seguros">13-Seguros</option>
                      </select>

                      {(userRole === "juridica" ||
                        userRole === "administrador" ||
                        userRole === "administrador_junior") &&
                        isEditing &&
                        Number(contract.current_phase) === 3 && (
                          <div className="mt-2">
                            <div className="flex items-center space-x-4">
                              <span className="text-sm font-medium text-gray-700">
                                Evaluación:
                              </span>

                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="tipo_contrato_aprobado_radio"
                                  value="true"
                                  checked={contract.tipo_contrato_aprobado === true}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setContract((prev) => ({
                                      ...prev,
                                      tipo_contrato_aprobado: true,
                                    }));
                                  }}
                                  disabled={!canEditField("tipo_contrato_aprobado")}
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#16a34a" }}
                                />
                                <span className="ml-2 text-sm text-green-700">
                                  Aprobado
                                </span>
                              </label>

                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="tipo_contrato_aprobado_radio"
                                  value="false"
                                  checked={contract.tipo_contrato_aprobado === false}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setContract((prev) => ({
                                      ...prev,
                                      tipo_contrato_aprobado: false,
                                    }));
                                  }}
                                  disabled={!canEditField("tipo_contrato_aprobado")}
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#dc2626" }}
                                />
                                <span className="ml-2 text-sm text-red-700">
                                  Rechazado
                                </span>
                              </label>
                            </div>
                          </div>
                        )}
                    </div>
                  </td>

                  {/* Modalidad de selección + evaluación */}
                  <td>
                    <div className="form-group">
                      <label htmlFor="modalidadSeleccion" className="required">
                        Modalidad de Selección
                      </label>
                      <select
                        id="modalidadSeleccion"
                        name="modalidadSeleccion"
                        className="form-control"
                        value={contract.modalidadSeleccion}
                        onChange={handleChange}
                        required
                        disabled={!canEdit()}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="CCE-01">
                          CCE-01 Solicitud de información a los Proveedores
                        </option>
                        <option value="CCE-02">CCE-02 Licitación pública</option>
                        <option value="CCE-17">
                          CCE-17 Licitación pública (Obra pública)
                        </option>
                        <option value="CCE-03">
                          CCE-03 Concurso de méritos con precalificación
                        </option>
                        <option value="CCE-04">
                          CCE-04 Concurso de méritos abierto
                        </option>
                        <option value="CCE-05">
                          CCE-05 Contratación directa (con ofertas)
                        </option>
                        <option value="CCE-06">
                          CCE-06 Selección abreviada menor cuantía
                        </option>
                        <option value="CCE-18-">
                          CCE-18 Selección Abreviada Menor Cuantía sin Manifestación de
                          Interés
                        </option>
                        <option value="CCE-07">
                          CCE-07 Selección abreviada subasta inversa
                        </option>
                        <option value="CCE-10">CCE-10 Mínima cuantía</option>
                        <option value="CCE-11||01">
                          CCE-11||01 Contratación régimen especial - Selección de
                          comisionista
                        </option>
                        <option value="CCE-11||02">
                          CCE-11||02 Contratación régimen especial - Enajenación de bienes
                          para intermediarios idóneos
                        </option>
                        <option value="CCE-11||03">
                          CCE-11||03 Contratación régimen especial - Régimen especial
                        </option>
                        <option value="CCE-11||04">
                          CCE-11||04 Contratación régimen especial - Banco multilateral y
                          organismos multilaterales
                        </option>
                        <option value="CCE-15||01">
                          CCE-15||01 Contratación régimen especial (con ofertas) -
                          Selección de comisionista
                        </option>
                        <option value="CCE-15||02">
                          CCE-15||02 Contratación régimen especial (con ofertas) -
                          Enajenación de bienes para intermediarios idóneos
                        </option>
                        <option value="CCE-15||03">
                          CCE-15||03 Contratación régimen especial (con ofertas) - Régimen
                          especial
                        </option>
                        <option value="CCE-15||04">
                          CCE-15||04 Contratación régimen especial (con ofertas) - Banco
                          multilateral y organismos multilaterales
                        </option>
                        <option value="CCE-16">CCE-16 Contratación directa.</option>
                        <option value="CCE-99">
                          CCE-99 Selección abreviada - acuerdo marco
                        </option>
                      </select>

                      {(userRole === "juridica" || userRole === "administrador" || userRole === "administrador_junior") &&
                        isEditing &&
                        Number(contract.current_phase) === 3 && (
                          <div className="mt-2">
                            <div className="flex items-center space-x-4">
                              <span className="text-sm font-medium text-gray-700">
                                Evaluación:
                              </span>

                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="modalidad_seleccion_aprobada"
                                  value="true"
                                  checked={contract.modalidad_seleccion_aprobada === true}
                                  onChange={() =>
                                    setContract((prev) => ({
                                      ...prev,
                                      modalidad_seleccion_aprobada: true,
                                    }))
                                  }
                                  disabled={!canEditField("modalidad_seleccion_aprobada")}
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#16a34a" }}
                                />
                                <span className="ml-2 text-sm text-green-700">
                                  Aprobado
                                </span>
                              </label>

                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="modalidad_seleccion_aprobada"
                                  value="false"
                                  checked={contract.modalidad_seleccion_aprobada === false}
                                  onChange={() =>
                                    setContract((prev) => ({
                                      ...prev,
                                      modalidad_seleccion_aprobada: false,
                                    }))
                                  }
                                  disabled={!canEditField("modalidad_seleccion_aprobada")}
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#dc2626" }}
                                />
                                <span className="ml-2 text-sm text-red-700">
                                  Rechazado
                                </span>
                              </label>
                            </div>
                          </div>
                        )}
                    </div>
                  </td>
                </tr>

                {/* Objeto contractual + evaluación */}
                <tr>
                  <td colSpan="2">
                    <div className="form-group">
                      <label htmlFor="objetoContractual" className="required">
                        Objeto Contractual
                      </label>
                      <textarea
                        id="objetoContractual"
                        name="objetoContractual"
                        className="form-control"
                        placeholder="Describe el contrato aquí..."
                        value={contract.objetoContractual}
                        onChange={handleChange}
                        required
                        disabled={!canEdit()}
                      />

                      {(userRole === "juridica" || userRole === "administrador") &&
                        isEditing &&
                        Number(contract.current_phase) === 3 && (
                          <div className="mt-2">
                            <div className="flex items-center space-x-4">
                              <span className="text-sm font-medium text-gray-700">
                                Evaluación:
                              </span>

                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="objeto_contractual_aprobado"
                                  value="true"
                                  checked={contract.objeto_contractual_aprobado === true}
                                  onChange={() =>
                                    setContract((prev) => ({
                                      ...prev,
                                      objeto_contractual_aprobado: true,
                                    }))
                                  }
                                  disabled={!canEditField("objeto_contractual_aprobado")}
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#16a34a" }}
                                />
                                <span className="ml-2 text-sm text-green-700">
                                  Aprobado
                                </span>
                              </label>

                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="objeto_contractual_aprobado"
                                  value="false"
                                  checked={contract.objeto_contractual_aprobado === false}
                                  onChange={() =>
                                    setContract((prev) => ({
                                      ...prev,
                                      objeto_contractual_aprobado: false,
                                    }))
                                  }
                                  disabled={!canEditField("objeto_contractual_aprobado")}
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#dc2626" }}
                                />
                                <span className="ml-2 text-sm text-red-700">
                                  Rechazado
                                </span>
                              </label>
                            </div>
                          </div>
                        )}
                    </div>
                  </td>
                </tr>

                {/* Duración estimada + evaluación y nombre contratista */}
                <tr>
                  <td>
                    <div className="form-group">
                      <label className="required">Duración Estimada (días)</label>

                      <div className="flex flex-wrap items-end gap-3">
                        <div style={{ minWidth: 175 }}>
                          <label className="text-xs text-gray-600">Fecha Inicio</label>
                          <input
                            type="date"
                            id="fechaInicioContrato"
                            name="fechaInicioContrato"
                            className="form-control"
                            value={contract.fechaInicioContrato || ""}
                            onChange={handleChange}
                            disabled={!canEdit()}
                            max={contract.fechaFinContrato || undefined}
                          />
                        </div>

                        <div style={{ minWidth: 175 }}>
                          <label className="text-xs text-gray-600">Fecha Fin</label>
                          <input
                            type="date"
                            id="fechaFinContrato"
                            name="fechaFinContrato"
                            className="form-control"
                            value={contract.fechaFinContrato || ""}
                            onChange={handleChange}
                            disabled={!canEdit()}
                            min={contract.fechaInicioContrato || undefined}
                          />
                        </div>

                        <div style={{ minWidth: 140 }}>
                          <label className="text-xs text-gray-600">Total</label>
                          <input
                            type="text"
                            id="duracionEstimada"
                            name="duracionEstimada"
                            className="form-control"
                            value={contract.duracionEstimada || ""}
                            readOnly
                            disabled
                            placeholder="0"
                            style={{ fontWeight: 700, textAlign: "center" }}
                          />
                        </div>

                        <div className="text-sm text-gray-600" style={{ paddingBottom: 6 }}>
                          días
                        </div>
                      </div>

                      {(userRole === "juridica" || userRole === "administrador") &&
                        isEditing &&
                        Number(contract.current_phase) === 3 && (
                          <div className="mt-2">
                            <div className="flex items-center space-x-4">
                              <span className="text-sm font-medium text-gray-700">
                                Evaluación:
                              </span>

                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="duracion_estimada_aprobada"
                                  value="true"
                                  checked={contract.duracion_estimada_aprobada === true}
                                  onChange={() =>
                                    setContract((prev) => ({
                                      ...prev,
                                      duracion_estimada_aprobada: true,
                                    }))
                                  }
                                  disabled={!canEditField("duracion_estimada_aprobada")}
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#16a34a" }}
                                />
                                <span className="ml-2 text-sm text-green-700">
                                  Aprobado
                                </span>
                              </label>

                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="duracion_estimada_aprobada"
                                  value="false"
                                  checked={contract.duracion_estimada_aprobada === false}
                                  onChange={() =>
                                    setContract((prev) => ({
                                      ...prev,
                                      duracion_estimada_aprobada: false,
                                    }))
                                  }
                                  disabled={!canEditField("duracion_estimada_aprobada")}
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#dc2626" }}
                                />
                                <span className="ml-2 text-sm text-red-700">
                                  Rechazado
                                </span>
                              </label>
                            </div>
                          </div>
                        )}
                    </div>
                  </td>

                  <td>
                    <div className="form-group">
                      <label htmlFor="nombreContratista">Nombre Contratista</label>
                      <input
                        type="text"
                        id="nombreContratista"
                        name="nombreContratista"
                        className="form-control"
                        placeholder="Ingrese Nombre Completo"
                        value={contract.nombreContratista}
                        onChange={handleChange}
                        disabled={!canEdit()}
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="form-section">
            <h3 className="form-section-title">Información Presupuestal</h3>
            <table className="form-table">
              <tbody>
                <tr>
                  <td>
                    <div className="form-group">
                      <label htmlFor="naturaleza_gasto" className="required">
                        Tipo Naturaleza
                      </label>
                      <select
                        id="naturaleza_gasto"
                        name="naturalezaGasto"
                        className="form-control"
                        value={contract.naturalezaGasto || ""}
                        onChange={handleChange}
                        required
                        disabled={!canEdit()}
                      >
                        {/* Usar la misma función que controla los demás campos */}
                        <option value="">Seleccionar...</option>
                        <option value="Funcionamiento">Funcionamiento</option>
                        <option value="Inversión">Inversión</option>
                        <option value="Servicio de la Deuda">Servicio de la Deuda</option>
                      </select>

                      {/* Evaluación tipo de contrato - sólo Jurídica */}
                      {(userRole === "juridica" ||
                        userRole === "administrador" ||
                        userRole === "administrador_junior") &&
                        isEditing &&
                        Number(contract.current_phase) === 3 && (
                          <div className="mt-2">
                            <div className="flex items-center space-x-4">
                              <span className="text-sm font-medium text-gray-700">Evaluación:</span>
                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="tipo_contrato_aprobado"
                                  value="true"
                                  checked={contract.tipo_contrato_aprobado === true}
                                  onChange={() =>
                                    setContract((prev) => ({ ...prev, tipo_contrato_aprobado: true }))
                                  }
                                  disabled={!canEditField("tipo_contrato_aprobado")}
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#16a34a" }}
                                />
                                <span className="ml-2 text-sm text-green-700">Aprobado</span>
                              </label>
                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="tipo_contrato_aprobado"
                                  value="false"
                                  checked={contract.tipo_contrato_aprobado === false}
                                  onChange={() =>
                                    setContract((prev) => ({ ...prev, tipo_contrato_aprobado: false }))
                                  }
                                  disabled={!canEditField("tipo_contrato_aprobado")}
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#dc2626" }}
                                />
                                <span className="ml-2 text-sm text-red-700">Rechazado</span>
                              </label>
                            </div>
                          </div>
                        )}
                    </div>
                  </td>

                  <td>
                    <div className="form-group">
                      <label htmlFor="presupuestoTotal" className="required">
                        Presupuesto Total
                      </label>
                      <input
                        type="text"
                        id="presupuestoTotal"
                        name="presupuestoTotal"
                        className="form-control"
                        placeholder="Ingrese el presupuesto"
                        value={
                          contract.presupuestoTotal
                            ? Number(contract.presupuestoTotal).toLocaleString("es-CO")
                            : ""
                        }
                        onChange={handleChange}
                        required
                        disabled={!canEdit()}
                      />

                      {/* Evaluación presupuesto - sólo Jurídica */}
                      {(userRole === "juridica" ||
                        userRole === "administrador" ||
                        userRole === "administrador_junior") &&
                        isEditing &&
                        Number(contract.current_phase) === 3 && (
                          <div className="mt-2">
                            <div className="flex items-center space-x-4">
                              <span className="text-sm font-medium text-gray-700">Evaluación:</span>
                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="presupuesto_aprobado"
                                  value="true"
                                  checked={contract.presupuesto_aprobado === true}
                                  onChange={() =>
                                    setContract((prev) => ({
                                      ...prev,
                                      presupuesto_aprobado: true,
                                    }))
                                  }
                                  disabled={!canEditField("presupuesto_aprobado")}
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#16a34a" }}
                                />
                                <span className="ml-2 text-sm text-green-700">Aprobado</span>
                              </label>
                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="presupuesto_aprobado"
                                  value="false"
                                  checked={contract.presupuesto_aprobado === false}
                                  onChange={() =>
                                    setContract((prev) => ({
                                      ...prev,
                                      presupuesto_aprobado: false,
                                    }))
                                  }
                                  disabled={!canEditField("presupuesto_aprobado")}
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#dc2626" }}
                                />
                                <span className="ml-2 text-sm text-red-700">Rechazado</span>
                              </label>
                            </div>
                          </div>
                        )}

                      {/* Evaluación de Hacienda - edita sólo Hacienda (y NO se duplica para admin) */}
                      {(userRole === "hacienda") && isEditing && Number(contract.current_phase) === 2 && (
                        <div className="mt-4 border-t pt-4">
                          <h4 className="mb-2 font-semibold text-gray-700">Evaluación de Hacienda</h4>

                          <div className="mb-3">
                            <p className="mb-2 text-sm font-medium text-gray-700">
                              ¿Hay dinero en el rubro?
                            </p>
                            <div className="flex items-center space-x-4">
                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="hay_dinero_rubro"
                                  value="true"
                                  checked={contract.hay_dinero_rubro === true}
                                  onChange={() =>
                                    setContract((prev) => ({ ...prev, hay_dinero_rubro: true }))
                                  }
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#16a34a" }}
                                />
                                <span className="ml-2 text-sm text-green-700">Sí</span>
                              </label>
                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="hay_dinero_rubro"
                                  value="false"
                                  checked={contract.hay_dinero_rubro === false}
                                  onChange={() =>
                                    setContract((prev) => ({ ...prev, hay_dinero_rubro: false }))
                                  }
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#dc2626" }}
                                />
                                <span className="ml-2 text-sm text-red-700">No</span>
                              </label>
                            </div>
                          </div>

                          <div className="mb-3">
                            <p className="mb-2 text-sm font-medium text-gray-700">
                              ¿Se puede ejecutar el dinero en el rubro?
                            </p>
                            <div className="flex items-center space-x-4">
                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="se_puede_ejecutar"
                                  value="true"
                                  checked={contract.se_puede_ejecutar === true}
                                  onChange={() =>
                                    setContract((prev) => ({ ...prev, se_puede_ejecutar: true }))
                                  }
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#16a34a" }}
                                />
                                <span className="ml-2 text-sm text-green-700">Sí</span>
                              </label>
                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  name="se_puede_ejecutar"
                                  value="false"
                                  checked={contract.se_puede_ejecutar === false}
                                  onChange={() =>
                                    setContract((prev) => ({ ...prev, se_puede_ejecutar: false }))
                                  }
                                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                  style={{ accentColor: "#dc2626" }}
                                />
                                <span className="ml-2 text-sm text-red-700">No</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Mostrar sólo el resultado de Hacienda para el resto de roles
                (ni Hacienda ni Administrador, para que admin no lo vea duplicado) */}
                      {isEditing &&
                        Number(contract.current_phase) >= 2 &&
                        userRole !== "hacienda" &&
                        userRole !== "administrador" &&
                        (contract.hay_dinero_rubro !== null ||
                          contract.se_puede_ejecutar !== null) && (
                          <div className="mt-4 border-t pt-4">
                            <h4 className="mb-2 font-semibold text-gray-700">
                              Evaluación de Hacienda
                            </h4>

                            <div className="mb-3">
                              <p className="mb-1 text-sm font-medium text-gray-700">
                                ¿Hay dinero en el rubro?
                              </p>
                              <div className="text-sm">
                                {contract.hay_dinero_rubro === true ? (
                                  <span className="rounded-full bg-green-100 px-2 py-1 text-green-700">
                                    Sí
                                  </span>
                                ) : contract.hay_dinero_rubro === false ? (
                                  <span className="rounded-full bg-red-100 px-2 py-1 text-red-700">
                                    No
                                  </span>
                                ) : (
                                  <span className="text-gray-500">Sin evaluar</span>
                                )}
                              </div>
                            </div>

                            <div>
                              <p className="mb-1 text-sm font-medium text-gray-700">
                                ¿Se puede ejecutar el dinero en el rubro?
                              </p>
                              <div className="text-sm">
                                {contract.se_puede_ejecutar === true ? (
                                  <span className="rounded-full bg-green-100 px-2 py-1 text-green-700">
                                    Sí
                                  </span>
                                ) : contract.se_puede_ejecutar === false ? (
                                  <span className="rounded-full bg-red-100 px-2 py-1 text-red-700">
                                    No
                                  </span>
                                ) : (
                                  <span className="text-gray-500">Sin evaluar</span>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="form-section">
            <h3 className="form-section-title">Información Plan de Desarrollo Municipal</h3>
            <table className="form-table">
              <tbody>
                <tr>
                  <td>
                    {/* PROGRAMA */}
                    <div className="mb-6">
                      <label htmlFor="programa" className="block text-sm font-medium text-gray-700 mb-2">
                        Programa
                      </label>
                      <div className="flex items-center">
                        <Select
                          id="programa"
                          name="programa"
                          options={opcionesProgramas.map((option) => ({
                            value: option.value,
                            label: option.label,
                          }))}
                          value={null}
                          onChange={(selectedOption) => {
                            if (selectedOption) {
                              setProgramaSelections([...programaSelections, selectedOption]);
                              setContract((prev) => ({
                                ...prev,
                                programa: [...prev.programa, selectedOption.value],
                              }));
                            }
                          }}
                          className="basic-select w-full"
                          classNamePrefix="select"
                          isDisabled={!canEdit()}
                          placeholder="Seleccionar..."
                          noOptionsMessage={() => "No hay opciones disponibles"}
                        />
                        <button
                          type="button"
                          className="ml-2 px-3 py-2 bg-blue-600 text-white rounded-md"
                          onClick={() => { }}
                          disabled={!canEdit()}
                        >
                          +
                        </button>
                      </div>

                      {/* Tabla para mostrar selecciones de programa */}
                      {programaSelections && programaSelections.length > 0 ? (
                        <div className="mt-3 border rounded-md overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Programa
                                </th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                  Acción
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {programaSelections.map((item, index) => (
                                <tr key={index}>
                                  <td className="px-3 py-2 text-sm text-gray-900">{item.label}</td>
                                  <td className="px-3 py-2 text-right">
                                    <button
                                      type="button"
                                      className="text-red-600 hover:text-red-900"
                                      onClick={() => {
                                        const newSelections = [...programaSelections];
                                        newSelections.splice(index, 1);
                                        setProgramaSelections(newSelections);
                                        setContract((prev) => ({
                                          ...prev,
                                          programa: prev.programa.filter((p) => p !== item.value),
                                        }));
                                      }}
                                      disabled={!canEdit()}
                                    >
                                      ×
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="mt-3 text-sm text-gray-500 italic">
                          No hay programas seleccionados
                        </div>
                      )}

                      {/* ✅ EVALUACIÓN DE PROGRAMA - CORREGIDO */}
                      {(userRole === "planeacion" ||
                        userRole === "administrador" ||
                        userRole === "administrador_junior") &&
                        isEditing && (
                          <div className="mt-2">
                            <div className="flex items-center">
                              <span className="text-sm font-medium text-gray-700 mr-4">
                                Evaluación:
                              </span>
                              <div className="flex items-center space-x-6">
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    name="programa_aprobado"
                                    value="true"
                                    checked={contract.programa_aprobado === true}
                                    onChange={() =>
                                      setContract((prev) => ({
                                        ...prev,
                                        programa_aprobado: true,
                                      }))
                                    }
                                    disabled={!canEditField("programa_aprobado")}
                                    className="h-5 w-5 border-gray-300 text-green-600 focus:ring-green-500"
                                    style={{ accentColor: "#16a34a" }}
                                  />
                                  <span className="ml-2 text-sm font-medium text-green-700">
                                    Aprobado
                                  </span>
                                </label>

                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    name="programa_aprobado"
                                    value="false"
                                    checked={contract.programa_aprobado === false}
                                    onChange={() =>
                                      setContract((prev) => ({
                                        ...prev,
                                        programa_aprobado: false,
                                      }))
                                    }
                                    disabled={!canEditField("programa_aprobado")}
                                    className="h-5 w-5 border-gray-300 text-red-600 focus:ring-red-500"
                                    style={{ accentColor: "#dc2626" }}
                                  />
                                  <span className="ml-2 text-sm font-medium text-red-700">
                                    Rechazado
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        )}

                      {/* ✅ MOSTRAR SOLO LECTURA PARA HACIENDA */}
                      {userRole === "hacienda" && isEditing && (
                        <div className="mt-2">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-700 mr-4">Evaluación:</span>
                            <span
                              className={`text-sm font-medium ${contract.programa_aprobado === true
                                ? "text-green-700"
                                : contract.programa_aprobado === false
                                  ? "text-red-700"
                                  : "text-gray-500"
                                }`}
                            >
                              {contract.programa_aprobado === true
                                ? "✓ Aprobado"
                                : contract.programa_aprobado === false
                                  ? "✗ Rechazado"
                                  : "Sin evaluar"}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* INDICADOR */}
                    <div className="mb-6">
                      <label htmlFor="indicador" className="block text-sm font-medium text-gray-700 mb-2">
                        Indicador
                      </label>
                      <div className="flex items-center">
                        <Select
                          id="indicador"
                          name="indicador"
                          options={opcionesIndicadores.map((option) => ({
                            value: option.value,
                            label: option.label,
                          }))}
                          value={null}
                          onChange={(selectedOption) => {
                            if (selectedOption) {
                              setIndicadorSelections([...indicadorSelections, selectedOption]);
                              setContract((prev) => ({
                                ...prev,
                                indicador: [...prev.indicador, selectedOption.value],
                              }));
                            }
                          }}
                          className="basic-select w-full"
                          classNamePrefix="select"
                          isDisabled={!canEdit()}
                          placeholder="Seleccionar..."
                          noOptionsMessage={() => "No hay opciones disponibles"}
                        />
                        <button
                          type="button"
                          className="ml-2 px-3 py-2 bg-blue-600 text-white rounded-md"
                          onClick={() => { }}
                          disabled={!canEdit()}
                        >
                          +
                        </button>
                      </div>

                      {/* Tabla para mostrar selecciones de indicador */}
                      {indicadorSelections && indicadorSelections.length > 0 ? (
                        <div className="mt-3 border rounded-md overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Indicador
                                </th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                  Acción
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {indicadorSelections.map((item, index) => (
                                <tr key={index}>
                                  <td className="px-3 py-2 text-sm text-gray-900">{item.label}</td>
                                  <td className="px-3 py-2 text-right">
                                    <button
                                      type="button"
                                      className="text-red-600 hover:text-red-900"
                                      onClick={() => {
                                        const newSelections = [...indicadorSelections];
                                        newSelections.splice(index, 1);
                                        setIndicadorSelections(newSelections);
                                        setContract((prev) => ({
                                          ...prev,
                                          indicador: prev.indicador.filter((i) => i !== item.value),
                                        }));
                                      }}
                                      disabled={!canEdit()}
                                    >
                                      ×
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="mt-3 text-sm text-gray-500 italic">
                          No hay indicadores seleccionados
                        </div>
                      )}

                      {/* ✅ EVALUACIÓN DE INDICADOR - CORREGIDO */}
                      {(userRole === "planeacion" ||
                        userRole === "administrador" ||
                        userRole === "administrador_junior") &&
                        isEditing && (
                          <div className="mt-2">
                            <div className="flex items-center">
                              <span className="text-sm font-medium text-gray-700 mr-4">
                                Evaluación:
                              </span>
                              <div className="flex items-center space-x-6">
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    name="indicador_aprobado"
                                    value="true"
                                    checked={contract.indicador_aprobado === true}
                                    onChange={() =>
                                      setContract((prev) => ({
                                        ...prev,
                                        indicador_aprobado: true,
                                      }))
                                    }
                                    disabled={!canEditField("indicador_aprobado")}
                                    className="h-5 w-5 border-gray-300 text-green-600 focus:ring-green-500"
                                    style={{ accentColor: "#16a34a" }}
                                  />
                                  <span className="ml-2 text-sm font-medium text-green-700">
                                    Aprobado
                                  </span>
                                </label>

                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    name="indicador_aprobado"
                                    value="false"
                                    checked={contract.indicador_aprobado === false}
                                    onChange={() =>
                                      setContract((prev) => ({
                                        ...prev,
                                        indicador_aprobado: false,
                                      }))
                                    }
                                    disabled={!canEditField("indicador_aprobado")}
                                    className="h-5 w-5 border-gray-300 text-red-600 focus:ring-red-500"
                                    style={{ accentColor: "#dc2626" }}
                                  />
                                  <span className="ml-2 text-sm font-medium text-red-700">
                                    Rechazado
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        )}

                      {/* ✅ MOSTRAR SOLO LECTURA PARA HACIENDA */}
                      {userRole === "hacienda" && isEditing && (
                        <div className="mt-2">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-700 mr-4">Evaluación:</span>
                            <span
                              className={`text-sm font-medium ${contract.indicador_aprobado === true
                                ? "text-green-700"
                                : contract.indicador_aprobado === false
                                  ? "text-red-700"
                                  : "text-gray-500"
                                }`}
                            >
                              {contract.indicador_aprobado === true
                                ? "✓ Aprobado"
                                : contract.indicador_aprobado === false
                                  ? "✗ Rechazado"
                                  : "Sin evaluar"}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* BPIN */}
                    <div className="mb-6">
                      <label htmlFor="bpin" className="block text-sm font-medium text-gray-700 mb-2">
                        BPIN
                      </label>
                      <div className="flex items-center">
                        <Select
                          id="bpin"
                          name="bpin"
                          options={opcionesBPIN.map((option) => ({
                            value: option.value,
                            label: option.label,
                          }))}
                          value={null}
                          onChange={(selectedOption) => {
                            if (selectedOption) {
                              setBpinSelections([...bpinSelections, selectedOption]);
                              setContract((prev) => ({
                                ...prev,
                                bpin: [...prev.bpin, selectedOption.value],
                              }));
                            }
                          }}
                          className="basic-select w-full"
                          classNamePrefix="select"
                          isDisabled={!canEdit()}
                          placeholder="Seleccionar..."
                          noOptionsMessage={() => "No hay opciones disponibles"}
                        />
                        <button
                          type="button"
                          className="ml-2 px-3 py-2 bg-blue-600 text-white rounded-md"
                          onClick={() => { }}
                          disabled={!canEdit()}
                        >
                          +
                        </button>
                      </div>

                      {/* Tabla para mostrar selecciones de BPIN */}
                      {bpinSelections && bpinSelections.length > 0 ? (
                        <div className="mt-3 border rounded-md overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  BPIN
                                </th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                  Acción
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {bpinSelections.map((item, index) => (
                                <tr key={index}>
                                  <td className="px-3 py-2 text-sm text-gray-900">{item.label}</td>
                                  <td className="px-3 py-2 text-right">
                                    <button
                                      type="button"
                                      className="text-red-600 hover:text-red-900"
                                      onClick={() => {
                                        const newSelections = [...bpinSelections];
                                        newSelections.splice(index, 1);
                                        setBpinSelections(newSelections);
                                        setContract((prev) => ({
                                          ...prev,
                                          bpin: prev.bpin.filter((b) => b !== item.value),
                                        }));
                                      }}
                                      disabled={!canEdit()}
                                    >
                                      ×
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="mt-3 text-sm text-gray-500 italic">No hay BPIN seleccionados</div>
                      )}

                      {/* ✅ EVALUACIÓN DE BPIN - CORREGIDO */}
                      {(userRole === "planeacion" ||
                        userRole === "administrador" ||
                        userRole === "administrador_junior") &&
                        isEditing && (
                          <div className="mt-2">
                            <div className="flex items-center">
                              <span className="text-sm font-medium text-gray-700 mr-4">
                                Evaluación:
                              </span>
                              <div className="flex items-center space-x-6">
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    name="bpin_aprobado"
                                    value="true"
                                    checked={contract.bpin_aprobado === true}
                                    onChange={() =>
                                      setContract((prev) => ({ ...prev, bpin_aprobado: true }))
                                    }
                                    disabled={!canEditField("bpin_aprobado")}
                                    className="h-5 w-5 border-gray-300 text-green-600 focus:ring-green-500"
                                    style={{ accentColor: "#16a34a" }}
                                  />
                                  <span className="ml-2 text-sm font-medium text-green-700">
                                    Aprobado
                                  </span>
                                </label>

                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    name="bpin_aprobado"
                                    value="false"
                                    checked={contract.bpin_aprobado === false}
                                    onChange={() =>
                                      setContract((prev) => ({ ...prev, bpin_aprobado: false }))
                                    }
                                    disabled={!canEditField("bpin_aprobado")}
                                    className="h-5 w-5 border-gray-300 text-red-600 focus:ring-red-500"
                                    style={{ accentColor: "#dc2626" }}
                                  />
                                  <span className="ml-2 text-sm text-red-700">
                                    Rechazado
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        )}

                      {/* ✅ MOSTRAR SOLO LECTURA PARA HACIENDA */}
                      {userRole === "hacienda" && isEditing && (
                        <div className="mt-2">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-700 mr-4">Evaluación:</span>
                            <span
                              className={`text-sm font-medium ${contract.bpin_aprobado === true
                                ? "text-green-700"
                                : contract.bpin_aprobado === false
                                  ? "text-red-700"
                                  : "text-gray-500"
                                }`}
                            >
                              {contract.bpin_aprobado === true
                                ? "✓ Aprobado"
                                : contract.bpin_aprobado === false
                                  ? "✗ Rechazado"
                                  : "Sin evaluar"}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>


          <div className="form-section">
            <h3 className="form-section-title">Discriminación de Rubros Presupuestales</h3>

            {/* Selector fuera de la tabla para evitar fila vací­a */}
            <div style={{ marginBottom: '10px' }}>
              <label htmlFor="rubroSelect">Agregar rubro</label>
              <select
                id="rubroSelect"
                name="discriminacionRubros"
                className="form-control"
                value=""
                onChange={(e) => {

                  const selectedCode = e.target.value;
                  if (selectedCode && opcionesRubros[selectedCode]) {
                    const newSelection = {
                      codigo: selectedCode,
                      rubro: opcionesRubros[selectedCode].rubro,
                      nombreRubro: opcionesRubros[selectedCode].nombreRubro,
                      valor: ""
                    };
                    setRubroSelections([...rubroSelections, newSelection]);

                    setContract(prev => ({
                      ...prev,
                      discriminacionRubros: [...prev.discriminacionRubros, selectedCode],
                      rubros: [...prev.rubros, opcionesRubros[selectedCode].rubro],
                      nombresRubros: [...prev.nombresRubros, opcionesRubros[selectedCode].nombreRubro],
                      valores: [...prev.valores, ""]
                    }));
                  }
                }}
                disabled={!canEdit()}
              >
                <option value="">Seleccionar...</option>
                {(() => {
                  const centroCostosBase = contract.centroCostos ? contract.centroCostos.substring(0, 5) : "";
                  const rubrosFiltrados =
                    centroCostosBase && centrosCostosRubros[centroCostosBase]
                      ? centrosCostosRubros[centroCostosBase]
                      : null;

                  if (rubrosFiltrados) {
                    return rubrosFiltrados.map((codigo) => (
                      <option key={codigo} value={codigo}>
                        {codigo}
                      </option>
                    ));
                  } else {
                    return Object.keys(opcionesRubros).map((codigo) => (
                      <option key={codigo} value={codigo}>
                        {codigo}
                      </option>
                    ));
                  }
                })()}
              </select>
            </div>

            <table className="form-table">
              <thead>
                <tr>
                  <th>Resumido</th>
                  <th>Rubro</th>
                  <th>Nombre rubro</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {rubroSelections.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <div className="flex items-center">
                        <span className="mr-2">{item.codigo}</span>
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-900"
                          onClick={() => {
                            // 1) Actualizar la lista que se usa para pintar la tabla
                            const newSelections = [...rubroSelections];
                            newSelections.splice(index, 1);
                            setRubroSelections(newSelections);

                            // 2) Actualizar el estado principal del contrato
                            setContract((prev) => {
                              const discrim = Array.isArray(prev.discriminacionRubros)
                                ? prev.discriminacionRubros.filter((_, i) => i !== index)
                                : [];

                              const rubros = Array.isArray(prev.rubros)
                                ? prev.rubros.filter((_, i) => i !== index)
                                : [];

                              const nombresRubros = Array.isArray(prev.nombresRubros)
                                ? prev.nombresRubros.filter((_, i) => i !== index)
                                : [];

                              const valores = Array.isArray(prev.valores)
                                ? prev.valores.filter((_, i) => i !== index)
                                : [];

                              return {
                                ...prev,
                                discriminacionRubros: discrim,
                                rubros,
                                nombresRubros,
                                valores,
                              };
                            });
                          }}
                          disabled={!canEdit()}
                        >
                          ×
                        </button>
                      </div>
                    </td>
                    <td>
                      <textarea
                        className="form-control"
                        value={item.rubro}
                        readOnly
                        disabled
                      />
                    </td>
                    <td>
                      <textarea
                        className="form-control"
                        value={item.nombreRubro}
                        readOnly
                        disabled
                      />
                    </td>
                    <td>
                      <textarea
                        className="form-control"
                        value={item.valor || ""}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          const cleanedValue = inputValue.replace(/[.,]/g, "");

                          const updatedSelections = [...rubroSelections];
                          updatedSelections[index].valor = inputValue; // visible al usuario
                          setRubroSelections(updatedSelections);

                          const newValores = [...contract.valores];
                          newValores[index] = cleanedValue; // valor limpio sin puntos/comas
                          setContract(prev => ({
                            ...prev,
                            valores: newValores
                          }));
                        }}
                        disabled={!canEdit()}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>


            <div className="form-group" style={{ marginTop: "15px" }}>
              <label htmlFor="formaPago" className="required">
                Forma de Pago
              </label>
              <select
                id="formaPago"
                name="formaPago"
                className="form-control"
                value={contract.formaPago}
                onChange={handleChange}
                required
                disabled={!canEdit()}
              >
                <option value="">Seleccionar...</option>
                <option value="unico_pago">Único Pago</option>
                <option value="mensual">Mensual</option>
              </select>
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Presupuesto Asociado al Indicador</h3>

            {/* Selector */}
            <div style={{ marginBottom: "10px" }}>
              <label htmlFor="indicadorPresupuestoSelect">Agregar indicador</label>
              <select
                id="indicadorPresupuestoSelect"
                className="form-control"
                value=""
                onChange={(e) => {
                  const selectedCode = e.target.value;
                  if (!selectedCode) return;

                  // Evitar duplicados
                  const yaExiste = indicadorPresupuestoSelections.some(
                    (item) => item.codigo === selectedCode
                  );
                  if (yaExiste) {
                    alert("Este indicador ya fue agregado.");
                    return;
                  }

                  // Verificar que aún quede porcentaje disponible
                  const totalActual = indicadorPresupuestoSelections.reduce(
                    (acc, item) => acc + (parseFloat(item.porcentaje) || 0), 0
                  );
                  if (totalActual >= 100) {
                    alert("Ya se ha distribuido el 100% del presupuesto. No puede agregar más indicadores.");
                    return;
                  }

                  const newItem = {
                    codigo: selectedCode,
                    porcentaje: "",
                    total: "",
                  };

                  setIndicadorPresupuestoSelections((prev) => [...prev, newItem]);
                }}
                disabled={!canEdit()}
              >
                <option value="">Seleccionar indicador...</option>
                {indicadorSelections.map((ind) => (
                  <option key={ind.value} value={ind.value}>
                    {ind.value}
                  </option>
                ))}
              </select>
              {indicadorSelections.length === 0 && (
                <p className="mt-1 text-xs text-gray-500 italic">
                  Primero seleccione indicadores en la sección "Información Plan de Desarrollo Municipal"
                </p>
              )}

              {/* Indicador visual del porcentaje restante */}
              {indicadorPresupuestoSelections.length > 0 && (() => {
                const totalActual = indicadorPresupuestoSelections.reduce(
                  (acc, item) => acc + (parseFloat(item.porcentaje) || 0), 0
                );
                const restante = 100 - totalActual;
                const esExacto = Math.round(totalActual * 100) / 100 === 100;
                const excede = totalActual > 100;



              })()}
            </div>

            {/* Tabla */}
            <table className="form-table">
              <thead>
                <tr>
                  <th>Indicador</th>
                  <th>Porcentaje (%)</th>
                  <th>Total ($)</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {indicadorPresupuestoSelections.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", color: "#9ca3af", fontStyle: "italic" }}>
                      No hay indicadores agregados
                    </td>
                  </tr>
                ) : (
                  indicadorPresupuestoSelections.map((item, index) => (
                    <tr key={index}>
                      {/* Código del indicador - solo lectura */}
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          value={item.codigo}
                          readOnly
                          disabled
                          style={{ backgroundColor: "#f3f4f6", fontWeight: 600 }}
                        />
                      </td>

                      {/* Porcentaje */}
                      <td>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="0"
                          min="0"
                          max="100"
                          step="0.01"
                          value={item.porcentaje}
                          onChange={(e) => {
                            const nuevoPorcentaje = e.target.value;
                            const nuevoValorNum = parseFloat(nuevoPorcentaje) || 0;

                            // Calcular total de los demás sin esta fila
                            const totalSinEste = indicadorPresupuestoSelections.reduce(
                              (acc, sel, i) => i === index ? acc : acc + (parseFloat(sel.porcentaje) || 0), 0
                            );

                            // Bloquear si supera 100%
                            if (totalSinEste + nuevoValorNum > 100) {
                              const maxPermitido = (100 - totalSinEste).toFixed(2);
                              alert(`No puede superar el 100%. El máximo permitido para este indicador es ${maxPermitido}%.`);
                              return;
                            }

                            const presupuestoBase = parseFloat(
                              String(contract.presupuestoTotal || "0").replace(/[.,\s]/g, "")
                            ) || 0;

                            const nuevoTotal =
                              nuevoPorcentaje !== ""
                                ? ((nuevoValorNum / 100) * presupuestoBase).toFixed(0)
                                : "";

                            setIndicadorPresupuestoSelections((prev) =>
                              prev.map((sel, i) =>
                                i === index
                                  ? { ...sel, porcentaje: nuevoPorcentaje, total: nuevoTotal }
                                  : sel
                              )
                            );
                          }}
                          disabled={!canEdit()}
                        />
                      </td>

                      {/* Total calculado - solo lectura */}
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          value={
                            item.total !== ""
                              ? Number(item.total).toLocaleString("es-CO")
                              : ""
                          }
                          readOnly
                          disabled
                          style={{ backgroundColor: "#f3f4f6" }}
                          placeholder="Calculado automáticamente"
                        />
                      </td>

                      {/* Eliminar fila */}
                      <td style={{ textAlign: "center" }}>
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-900"
                          style={{ fontSize: "20px", fontWeight: "bold", background: "none", border: "none", cursor: "pointer" }}
                          onClick={() =>
                            setIndicadorPresupuestoSelections((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          disabled={!canEdit()}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              {/* Fila de totales si hay filas */}
              {indicadorPresupuestoSelections.length > 0 && (() => {
                const totalPct = indicadorPresupuestoSelections.reduce(
                  (acc, item) => acc + (parseFloat(item.porcentaje) || 0), 0
                );
                const totalVal = indicadorPresupuestoSelections.reduce(
                  (acc, item) => acc + (parseFloat(item.total) || 0), 0
                );
                const excede = totalPct > 100;
                const esExacto = Math.round(totalPct * 100) / 100 === 100;
                return (
                  <tfoot>
                    <tr>
                      <td style={{ fontWeight: 700 }}>Total</td>
                      <td style={{
                        fontWeight: 700,
                        color: excede ? "#dc2626" : esExacto ? "#16a34a" : "#d97706"
                      }}>
                        {totalPct.toFixed(2)}%
                      </td>
                      <td style={{ fontWeight: 700 }}>
                        ${Number(totalVal).toLocaleString("es-CO")}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                );
              })()}
            </table>
          </div>



          <div className="form-section">
            <h3 className="form-section-title">Datos del Solicitante</h3>
            <table className="form-table">
              <tbody>
                <tr>
                  <td>
                    <div className="form-group">
                      <label htmlFor="nombreSolicitante" className="required">
                        Nombre
                      </label>
                      <input
                        type="text"
                        id="nombreSolicitante"
                        name="nombreSolicitante"
                        className="form-control"
                        placeholder="Ingrese su nombre"
                        value={contract.nombreSolicitante}
                        onChange={handleChange}
                        required
                        disabled={!canEdit()}
                      />
                    </div>
                  </td>

                  <td>
                    <div className="form-group">
                      <label htmlFor="cargoSolicitante" className="required">
                        Cargo
                      </label>
                      <input
                        type="text"
                        id="cargoSolicitante"
                        name="cargoSolicitante"
                        className="form-control"
                        placeholder="Ingrese su cargo"
                        value={contract.cargoSolicitante}
                        onChange={handleChange}
                        required
                        disabled={!canEdit()}
                      />
                    </div>
                  </td>

                  <td>
                    {/* Firma del solicitante */}
                    <div className="form-group">
                      <label htmlFor="firma_solicitante">Firma</label>

                      <div className="firma-container">
                        {/* ✅ Mostrar preview local O URL del servidor */}
                        {((normalizedData && (normalizedData.firma_solicitante_url || normalizedData.firma_solicitante_url_preview))) ? (
                          <div className="firma-preview">
                            <img
                              src={
                                normalizedData.firma_solicitante_url ||
                                normalizedData.firma_solicitante_url_preview
                              }
                              alt="Firma del solicitante"
                              className="firma-image"
                              onLoad={(e) =>
                                console.log("✅ IMG SOLICITANTE CARGADA:", e.currentTarget.src)
                              }
                              onError={(e) => {
                                console.error("❌ IMG SOLICITANTE FALLÓ:", e.currentTarget.src);
                                e.currentTarget.onerror = null;
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          </div>
                        ) : (
                          <div className="firma-placeholder">Sin firma</div>
                        )}

                        {canEdit() && (
                          <div className="firma-upload">
                            <input
                              type="file"
                              id="firma_solicitante"
                              accept="image/*"
                              className="firma-input-file"
                              onChange={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleFirmaUpload(e, "firmaSolicitante");
                              }}
                            />

                            <button
                              type="button"
                              className="firma-upload-button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                document.getElementById("firma_solicitante")?.click();
                              }}
                            >
                              {/* ✅ Cambiar texto según haya firma (local o servidor) */}
                              {(normalizedData &&
                                (normalizedData.firma_solicitante_url ||
                                  normalizedData.firma_solicitante_url_preview))
                                ? "Cambiar firma"
                                : "Subir firma"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>




                    
                  </td>

                </tr>
              </tbody>
            </table>
          </div>


          {/* Modificar la secciÃƒÂ³n de aprobaciones en el formulario para mostrar correctamente los valores guardados */}
          <div className="form-section">
            <h3 className="form-section-title">Aprobaciones</h3>

            <table className="form-table">
              <tbody>
                {/* Departamento Administrativo */}
                <tr>
                  <td width="33%">
                    <div className="form-group">
                      <label htmlFor="aprobacionAdministrativa">Departamento Administrativo De Planeación</label>

                      {canEditField("aprobacion_administrativa") ? (
                        <select
                          id="aprobacion_administrativa"
                          name="aprobacion_administrativa"
                          className="form-control"
                          value={contract.aprobacion_administrativa}
                          onChange={handleChange}
                        >
                          <option value="">Seleccionar...</option>
                          <option value="aprobado">Aprobado</option>
                          <option value="rechazado">Rechazado</option>
                        </select>
                      ) : (
                        <select
                          id="aprobacion_administrativa"
                          name="aprobacion_administrativa"
                          className="form-control"
                          value={contract.aprobacion_administrativa}
                          onChange={handleChange}
                          disabled
                          style={{
                            backgroundColor: "#f3f4f6",
                            color: contract.aprobacion_administrativa ? "#374151" : "#9ca3af",
                          }}
                        >
                          <option value="">Seleccionar...</option>
                          <option value="aprobado">Aprobado</option>
                          <option value="rechazado">Rechazado</option>
                        </select>
                      )}
                    </div>
                  </td>

                  {/* ===================== ADMINISTRATIVA ===================== */}
                  <td width="33%">
                    <div className="form-group">
                      <label>&nbsp;</label>

                      <div className="firma-container">
                        {contract.firma_administrativa_url ? (
                          <div className="firma-preview">
                            <img
                              src={contract.firma_administrativa_url}
                              alt="Firma administrativa"
                              className="firma-image"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          </div>
                        ) : (
                          <div className="firma-placeholder">Sin firma</div>
                        )}

                        {canEditField("firma_administrativa_url") && (
                          <div className="firma-upload">
                            <input
                              type="file"
                              id="firmaAdministrativa"
                              accept="image/*"
                              onChange={(e) => handleFirmaUpload(e, "firmaAdministrativa")}
                              className="firma-input-file"
                            />
                            <label htmlFor="firmaAdministrativa" className="firma-upload-button">
                              {contract.firma_administrativa_url ? "Cambiar firma" : "Subir firma"}
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>

                {/* ===================== HACIENDA ===================== */}
                <tr>
                  <td width="33%">
                    <div className="form-group">
                      <label htmlFor="aprobacion_hacienda">Secretaría de Hacienda</label>

                      {userRole === "hacienda" && Number(contract.current_phase) === 2 ? (
                        <select
                          id="aprobacion_hacienda"
                          name="aprobacion_hacienda"
                          className="form-control"
                          value={contract.aprobacion_hacienda || ""}
                          onChange={handleChange}
                          disabled={!canEditField("aprobacion_hacienda")}
                        >
                          <option value="">Seleccionar...</option>
                          <option value="aprobado">Aprobado</option>
                          <option value="rechazado">Rechazado</option>
                        </select>
                      ) : (
                        <select
                          id="aprobacion_hacienda"
                          name="aprobacion_hacienda"
                          className="form-control"
                          value={contract.aprobacion_hacienda || ""}
                          onChange={handleChange}
                          disabled
                          style={{
                            backgroundColor: "#f3f4f6",
                            color: contract.aprobacion_hacienda ? "#374151" : "#9ca3af",
                          }}
                        >
                          <option value="">Seleccionar...</option>
                          <option value="aprobado">Aprobado</option>
                          <option value="rechazado">Rechazado</option>
                        </select>
                      )}
                    </div>
                  </td>

                  <td width="33%">
                    <div className="form-group">
                      <label>&nbsp;</label>

                      <div className="firma-container">
                        {contract.firma_hacienda_url ? (
                          <div className="firma-preview">
                            <img
                              src={contract.firma_hacienda_url}
                              alt="Firma de hacienda"
                              className="firma-image"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          </div>
                        ) : (
                          <div className="firma-placeholder">Sin firma</div>
                        )}

                        {/* ✅ Igual que Administrativa/Alcalde:
            - El admin ya tiene el botón de "Guardar cambios"
            - Aquí solo pedimos canEditField para mostrar el upload
        */}
                        {canEditField("firma_hacienda_url") && (
                          <div className="firma-upload">
                            <input
                              type="file"
                              id="firmaHacienda"
                              accept="image/*"
                              onChange={(e) => handleFirmaUpload(e, "firmaHacienda")}
                              className="firma-input-file"
                            />
                            <label htmlFor="firmaHacienda" className="firma-upload-button">
                              {contract.firma_hacienda_url ? "Cambiar firma" : "Subir firma"}
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>

                {/* ===================== ALCALDE ===================== */}
                <tr>
                  <td width="33%">
                    <div className="form-group">
                      <label htmlFor="aprobacion_alcalde">Alcalde Municipal</label>

                      {(userRole === "alcalde" || userRole === "administrador") &&
                        Number(contract.current_phase) === 4 ? (
                        <div className="flex items-center space-x-4">
                          <label className="inline-flex items-center">
                            <input
                              type="radio"
                              name="aprobacion_alcalde_radio"
                              value="aprobado"
                              checked={contract.aprobacion_alcalde === "aprobado"}
                              onChange={() =>
                                setContract((prev) => ({ ...prev, aprobacion_alcalde: "aprobado" }))
                              }
                              className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                              style={{ accentColor: "#16a34a" }}
                            />
                            <span className="ml-2 text-sm text-green-700">Aprobado</span>
                          </label>

                          <label className="inline-flex items-center">
                            <input
                              type="radio"
                              name="aprobacion_alcalde_radio"
                              value="rechazado"
                              checked={contract.aprobacion_alcalde === "rechazado"}
                              onChange={() =>
                                setContract((prev) => ({ ...prev, aprobacion_alcalde: "rechazado" }))
                              }
                              className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                              style={{ accentColor: "#dc2626" }}
                            />
                            <span className="ml-2 text-sm text-red-700">Rechazado</span>
                          </label>
                        </div>
                      ) : (
                        <select
                          id="aprobacion_alcalde"
                          name="aprobacion_alcalde"
                          className="form-control"
                          value={contract.aprobacion_alcalde || ""}
                          onChange={handleChange}
                          disabled={!canEditField("aprobacion_alcalde")}
                        >
                          <option value="">Seleccionar...</option>
                          <option value="aprobado">Aprobado</option>
                          <option value="rechazado">Rechazado</option>
                        </select>
                      )}
                    </div>
                  </td>

                  <td width="33%">
                    <div className="form-group">
                      <label>&nbsp;</label>

                      <div className="firma-container">
                        {contract.firma_alcalde_url ? (
                          <div className="firma-preview">
                            <img
                              src={contract.firma_alcalde_url}
                              alt="Firma del alcalde"
                              className="firma-image"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          </div>
                        ) : (
                          <div className="firma-placeholder">Sin firma</div>
                        )}

                        {canEditField("firma_alcalde_url") && (
                          <div className="firma-upload">
                            <input
                              type="file"
                              id="firmaAlcalde"
                              accept="image/*"
                              onChange={(e) => handleFirmaUpload(e, "firmaAlcalde")}
                              className="firma-input-file"
                            />
                            <label htmlFor="firmaAlcalde" className="firma-upload-button">
                              {contract.firma_alcalde_url ? "Cambiar firma" : "Subir firma"}
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>




          <div className="form-section">
            <h3 className="form-section-title">Información del Comité de Contratación</h3>
            <table className="form-table">
              <tbody>
                <tr>
                  <td>
                    <div className="form-group">
                      <label htmlFor="numeroActa">Número de Acta</label>
                      <input
                        type="text"
                        id="numeroActa"
                        name="numeroActa"
                        className="form-control"
                        placeholder="Ingrese el número de acta"
                        value={contract.numeroActa}
                        onChange={handleChange}
                        disabled={!canEditField("numeroActa")} // Ã¢Å“â€¦ Debe usar canEditField
                        style={!canEditField("numeroActa") ? { backgroundColor: "#f3f4f6", color: "#9ca3af" } : {}}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="form-group">
                      <label htmlFor="fechaComite">Fecha del Comité</label>
                      <input
                        type="date"
                        id="fechaComite"
                        name="fechaComite"
                        className="form-control"
                        value={contract.fechaComite}
                        onChange={handleChange}
                        disabled={!canEditField("fechaComite")} // Ã¢Å“â€¦ Debe usar canEditField
                        style={!canEditField("fechaComite") ? { backgroundColor: "#f3f4f6", color: "#9ca3af" } : {}}
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="form-section">
            <h3 className="form-section-title">Observaciones</h3>
            {userRole === "hacienda" && isEditing && contract.current_phase === 2 ? (
              <div>
                <label htmlFor="observaciones_hacienda" className="block text-sm font-medium text-gray-700">
                  Observaciones de Hacienda
                </label>
                <textarea
                  id="observaciones_hacienda"
                  name="observaciones_hacienda"
                  className="form-control"
                  placeholder="Ingrese observaciones de Hacienda..."
                  value={contract.observaciones_hacienda}
                  onChange={handleChange}
                  rows="4"
                />
              </div>
            ) : userRole === "juridica" && isEditing && contract.current_phase === 3 ? (
              <div>
                <label htmlFor="observaciones_juridica" className="block text-sm font-medium text-gray-700">
                  Observaciones de Jurídica
                </label>
                <textarea
                  id="observaciones_juridica"
                  name="observaciones_juridica"
                  className="form-control"
                  placeholder="Ingrese observaciones de Jurídica..."
                  value={contract.observaciones_juridica}
                  onChange={handleChange}
                  rows="4"
                  disabled={!canEditField("observaciones_juridica")}
                />
              </div>
            ) : userRole === "alcalde" && isEditing && contract.current_phase === 4 ? (
              <div>
                <label htmlFor="observaciones" className="block text-sm font-medium text-gray-700">
                  Observaciones del Alcalde
                </label>
                <textarea
                  id="observaciones"
                  name="observaciones"
                  className="form-control"
                  placeholder="Ingrese observaciones del Alcalde..."
                  value={contract.observaciones}
                  onChange={handleChange}
                  rows="4"
                />
              </div>
            ) : (
              <textarea
                name="observaciones"
                className="form-control"
                placeholder="Ingrese observaciones adicionales..."
                value={contract.observaciones}
                onChange={handleChange}
                rows="4"
                disabled={!canEdit() && !canEditField("observaciones")}
              />
            )}

            {/* Mostrar observaciones de Solicitante/Planeación si existen */}
            {isEditing && contract.observaciones && userRole !== "solicitante" && userRole !== "planeacion" && (
              <div className="mt-4 rounded-md bg-green-50 p-4">
                <h4 className="mb-2 font-semibold text-green-700">Observaciones de Solicitante/Planeación:</h4>
                <p className="text-sm text-green-700">{contract.observaciones}</p>
              </div>
            )}

            {/* Mostrar observaciones de Hacienda si existen */}
            {isEditing && contract.current_phase >= 2 && userRole !== "hacienda" && contract.observaciones_hacienda && (
              <div className="mt-4 rounded-md bg-blue-50 p-4">
                <h4 className="mb-2 font-semibold text-blue-700">Observaciones de Hacienda:</h4>
                <p className="text-sm text-blue-700">{contract.observaciones_hacienda}</p>
              </div>
            )}

            {/* Mostrar observaciones de Hacienda si existen */}
            {isEditing && contract.current_phase >= 2 && userRole !== "hacienda" && contract.observaciones_hacienda && (
              <div className="mt-4 rounded-md bg-blue-50 p-4">
                <h4 className="mb-2 font-semibold text-blue-700">Observaciones de Hacienda:</h4>
                <p className="text-sm text-blue-700">{contract.observaciones_hacienda}</p>
              </div>
            )}

            {/* Mostrar observaciones de JurÃƒÂ­dica si existen */}
            {isEditing &&
              contract?.current_phase >= 3 &&
              userRole !== "juridica" &&
              contract?.observaciones_juridica && (
                <div className="mt-4 rounded-md bg-purple-50 p-4">
                  <h4 className="mb-2 font-semibold text-purple-700">Observaciones de Jurídica:</h4>
                  <p className="text-sm text-purple-700">{contract.observaciones_juridica}</p>
                </div>
              )}
          </div>

          <div
  className="form-actions"
  style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "1rem" }}
>
  {contratoFirmadoAlcalde &&
    contract.status !== "anulado" &&
    (userRole === "solicitante" || userRole === "administrador") && (
      <button
        type="button"
        onClick={() => {
          setAnulacionReason("");
          setShowAnulacionModal(true);
        }}
        style={{
          backgroundColor: "#7c3aed",
          color: "white",
          padding: "0.5rem 1rem",
          borderRadius: "0.25rem",
          fontSize: "14px",
          fontWeight: 500,
          border: "none",
          cursor: "pointer",
          height: "40px",
        }}
      >
        Anular contrato
      </button>
    )}

 
 

  {(userRole === "administrador" || userRole === "administrador_junior") &&
    (() => {
      const isJuniorBlocked =
        userRole === "administrador_junior" && Number(contract?.current_phase) >= 4;

      return (
        <button
          type="button"
          onClick={handleSaveWithoutAdvancing}
          className="btn btn-secondary"
          disabled={isJuniorBlocked}
          style={{
            backgroundColor: isJuniorBlocked ? "#9ca3af" : "#6366f1",
            color: "white",
            padding: "0.5rem 1rem",
            borderRadius: "0.25rem",
            marginLeft: "0.5rem",
            cursor: isJuniorBlocked ? "not-allowed" : "pointer",
          }}
          title={isJuniorBlocked ? "Sin permisos en esta fase" : ""}
        >
          Guardar cambios
        </button>
      );
    })()}

  {(userRole === "administrador" || userRole === "administrador_junior") &&
    contract &&
    (() => {
      const isJuniorBlocked =
        userRole === "administrador_junior" && Number(contract?.current_phase) >= 4;

      return (
        <button
          type="button"
          onClick={handleReturnToPreviousPhase}
          className="btn btn-secondary"
          disabled={isJuniorBlocked}
          style={{
            backgroundColor: isJuniorBlocked ? "#9ca3af" : "#f59e0b",
            color: "white",
            padding: "0.5rem 1rem",
            borderRadius: "0.25rem",
            marginLeft: "0.5rem",
            cursor: isJuniorBlocked ? "not-allowed" : "pointer",
          }}
          title={isJuniorBlocked ? "Sin permisos en esta fase" : ""}
        >
          Devolver fase
        </button>
      );
    })()}

  <button
    type="button"
    onClick={() =>
      generateContractPDF(
        {
          ...contract,
          indicador_presupuesto: JSON.stringify(indicadorPresupuestoSelections),
        },
        `contrato_${contract.radicado || "nuevo"}.pdf`
      )
    }
    style={{
      backgroundColor: "#3b82f6",
      color: "white",
      padding: "0.5rem 1rem",
      borderRadius: "0.25rem",
      fontSize: "14px",
      fontWeight: 500,
      border: "none",
      cursor: "pointer",
      height: "40px",
    }}
  >
    Descargar PDF
  </button>

  {(userRole === "solicitante" || (user && isAuthorizedUser(user.email))) &&
    contract?.status === "aprobado" && (
      <>
        <button
          type="button"
          onClick={handleGenerateCdpClick}
          style={{
            background: "linear-gradient(45deg, rgb(143, 219, 161), rgb(92, 204, 96))",
            color: "black",
            padding: "0.5rem 1rem",
            borderRadius: "0.25rem",
            fontSize: "14px",
            fontWeight: 500,
            border: "none",
            cursor: "pointer",
            height: "40px",
          }}
          disabled={!contract || !contract.radicado || pdfGenerating}
        >
          Generar CDP
        </button>

        <button
          type="button"
          onClick={handleGenerateRpClick}
          style={{
            background: "linear-gradient(45deg, rgb(143, 219, 161), rgb(92, 204, 96))",
            color: "black",
            padding: "0.5rem 1rem",
            borderRadius: "0.25rem",
            fontSize: "14px",
            fontWeight: 500,
            border: "none",
            cursor: "pointer",
            height: "40px",
          }}
          disabled={!contract || !contract.radicado || pdfGenerating}
        >
          Generar RP
        </button>

        <button
          type="button"
          onClick={handleEnviarCdpClick}
          style={{
            background: "linear-gradient(45deg, rgb(143, 219, 161), rgb(92, 204, 96))",
            color: "black",
            padding: "0.5rem 1rem",
            borderRadius: "0.25rem",
            fontSize: "14px",
            fontWeight: 500,
            border: "none",
            cursor: "pointer",
            height: "40px",
          }}
        >
          Enviar CDP
        </button>

        <button
          type="button"
          onClick={handleEnviarRpClick}
          style={{
            background: "linear-gradient(45deg, rgb(143, 219, 161), rgb(92, 204, 96))",
            color: "black",
            padding: "0.5rem 1rem",
            borderRadius: "0.25rem",
            fontSize: "14px",
            fontWeight: 500,
            border: "none",
            cursor: "pointer",
            height: "40px",
          }}
        >
          Enviar RP
        </button>

        <button
          type="button"
          onClick={handleGenerateAdquisicionBienesClick}
          style={{
            background: "linear-gradient(45deg, rgb(143, 219, 161), rgb(92, 204, 96))",
            color: "black",
            padding: "0.5rem 1rem",
            borderRadius: "0.25rem",
            fontSize: "14px",
            fontWeight: 500,
            border: "none",
            cursor: "pointer",
            height: "40px",
          }}
        >
          Generar Adquisición de bienes
        </button>

        <button
          type="button"
          onClick={handleGenerateInexistenciaClick}
          disabled={!contratoFirmadoAlcalde}
          style={{
            background: contratoFirmadoAlcalde
              ? "linear-gradient(45deg, rgb(143, 219, 161), rgb(92, 204, 96))"
              : "linear-gradient(45deg, rgb(200, 200, 200), rgb(160, 160, 160))",
            color: "black",
            padding: "0.5rem 1rem",
            borderRadius: "0.25rem",
            fontSize: "14px",
            fontWeight: 500,
            border: "none",
            cursor: contratoFirmadoAlcalde ? "pointer" : "not-allowed",
            height: "40px",
            opacity: contratoFirmadoAlcalde ? 1 : 0.75,
          }}
          title={
            contratoFirmadoAlcalde
              ? "Enviar solicitud de certificación de inexistencia"
              : "Disponible cuando el contrato esté firmado por el Alcalde"
          }
        >
          Generar Certificación de Inexistencia
        </button>
      </>
    )}

  {(userRole === "solicitante" || (user && isAuthorizedUser(user.email))) &&
    (contract?.status === "aprobado" || contract?.status === "anulado") && (
      <button
        type="button"
        onClick={() => {
          setAnularCdpNumero("");
          setAnularCdpFile(null);
          setShowAnularCdpModal(true);
        }}
        style={{
          background: "linear-gradient(45deg, rgb(143, 219, 161), rgb(92, 204, 96))",
          color: "black",
          padding: "0.5rem 1rem",
          borderRadius: "0.25rem",
          fontSize: "14px",
          fontWeight: 500,
          border: "none",
          cursor: "pointer",
          height: "40px",
        }}
      >
        Anular CDP
      </button>
    )}

    {/* ── BOTÓN SOLICITAR ARL (solicitante) ── */}
{(userRole === "solicitante" || (user && isAuthorizedUser(user.email))) &&
  contract?.status === "aprobado" &&
  arlAprobado && (
    <button
      type="button"
      onClick={() => setShowEnviarArlModal(true)}
      style={{
        background: "linear-gradient(45deg, #f97316, #fb923c)",
        color: "white",
        padding: "0.5rem 1rem",
        borderRadius: "0.25rem",
        fontSize: "14px",
        fontWeight: 500,
        border: "none",
        cursor: "pointer",
        height: "40px",
      }}
    >
       Solicitar ARL
    </button>
  )}

{/* ── BOTONES SST ── */}
{userRole === "sst" && (
  <>
    <button
      type="button"
      onClick={() => handleOpenResponderPresupuesto("ARL")}
      style={{
        background: "linear-gradient(45deg, #f97316, #fb923c)",
        color: "white",
        padding: "0.5rem 1rem",
        borderRadius: "0.25rem",
        fontSize: "14px",
        fontWeight: 500,
        border: "none",
        cursor: "pointer",
        height: "40px",
      }}
    >
       Responder ARL
    </button>

    <button
      type="button"
      onClick={() => navigate("/dashboard")}
      style={{
        background: "#e5e7eb",
        color: "#111827",
        padding: "0.5rem 1rem",
        borderRadius: "0.25rem",
        fontSize: "14px",
        fontWeight: 600,
        border: "none",
        cursor: "pointer",
        height: "40px",
      }}
    >
      Volver
    </button>
  </>
)}

 {userRole === "presupuesto" && (
  <>
    {/* SOLO cuando está aprobado */}
    {contract?.status === "aprobado" && (
      <>
        <button
          type="button"
          onClick={() => handleOpenResponderPresupuesto("CDP")}
          onMouseEnter={(e) => {
            e.currentTarget.style.background =
              "linear-gradient(45deg, rgb(143, 219, 161), rgb(92, 204, 96))";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background =
              "linear-gradient(45deg, rgb(36, 154, 51), rgb(159, 221, 173))";
          }}
          style={{
            background: "linear-gradient(45deg, rgb(36, 154, 51), rgb(159, 221, 173))",
            color: "white",
            padding: "0.5rem 1rem",
            borderRadius: "0.25rem",
            fontSize: "14px",
            fontWeight: 500,
            border: "none",
            cursor: "pointer",
            height: "40px",
          }}
        >
          Responder CDP
        </button>

        <button
          type="button"
          onClick={() => handleOpenResponderPresupuesto("RP")}
          onMouseEnter={(e) => {
            e.currentTarget.style.background =
              "linear-gradient(45deg, rgb(143, 219, 161), rgb(92, 204, 96))";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background =
              "linear-gradient(45deg, rgb(36, 154, 51), rgb(159, 221, 173))";
          }}
          style={{
            background: "linear-gradient(45deg, rgb(36, 154, 51), rgb(159, 221, 173))",
            color: "white",
            padding: "0.5rem 1rem",
            borderRadius: "0.25rem",
            fontSize: "14px",
            fontWeight: 500,
            border: "none",
            cursor: "pointer",
            height: "40px",
          }}
        >
          Responder RP
        </button>
      </>
    )}

    {/* SOLO cuando está anulado */}
    {contract?.status === "anulado" && (
      <button
        type="button"
        onClick={() => handleOpenResponderPresupuesto("ANULAR_CDP")}
        onMouseEnter={(e) => {
          e.currentTarget.style.background =
            "linear-gradient(45deg, rgb(143, 219, 161), rgb(92, 204, 96))";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background =
            "linear-gradient(45deg, rgb(36, 154, 51), rgb(159, 221, 173))";
        }}
        style={{
          background: "linear-gradient(45deg, rgb(36, 154, 51), rgb(159, 221, 173))",
          color: "white",
          padding: "0.5rem 1rem",
          borderRadius: "0.25rem",
          fontSize: "14px",
          fontWeight: 500,
          border: "none",
          cursor: "pointer",
          height: "40px",
        }}
      >
        Responder Anulación CDP
      </button>
    )}

    {/* Siempre visible */}
    <button
      type="button"
      onClick={() => navigate("/dashboard")}
      style={{
        background: "#e5e7eb",
        color: "#111827",
        padding: "0.5rem 1rem",
        borderRadius: "0.25rem",
        fontSize: "14px",
        fontWeight: 600,
        border: "none",
        cursor: "pointer",
        height: "40px",
      }}
    >
      Volver
    </button>
  </>
)}

  {canEdit() ||
  canEditField("radicado") ||
  canEditField("programa_aprobado") ||
  canEditField("indicador_aprobado") ||
  canEditField("bpin_aprobado") ||
  canEditField("hay_dinero_rubro") ||
  canEditField("se_puede_ejecutar") ||
  canEditField("observaciones_hacienda") ||
  canEditField("aprobacion_hacienda") ||
  canEditField("firmaHacienda") ||
  canEditField("tipo_contrato_aprobado") ||
  canEditField("modalidad_seleccion_aprobada") ||
  canEditField("objeto_contractual_aprobado") ||
  canEditField("duracion_estimada_aprobada") ||
  canEditField("presupuesto_aprobado") ||
  canEditField("observaciones_juridica") ||
  canEditField("aprobacion_alcalde") ||
  canEditField("numeroActa") ||
  canEditField("fechaComite") ? (
    <button
      type="submit"
      className="btn btn-primary"
      disabled={loading}
      style={{
        backgroundColor: "#22c55e",
        borderColor: "#22c55e",
      }}
    >
      {loading ? "Guardando..." : "Guardar"}
    </button>
  ) : null}
</div>

          {/* Cierre del formulario */}
        </form>

        <div className="form-footer">
          <p>Página 1 de 1 • Oficina Jurídica</p>
        </div>
      </div>

      {showRejectModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              width: "90%",
              maxWidth: "500px",
              height: "90vh",
              display: "flex",
              flexDirection: "column",
              borderRadius: "8px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "16px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600 }}>Motivo de Rechazo</h3>
              <button
                onClick={() => setShowRejectModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div
              style={{
                padding: "16px",
                overflowY: "auto",
                flexGrow: 1,
              }}
            >
              <p style={{ marginBottom: "12px", fontSize: "14px", color: "#4b5563" }}>
                Por favor, indique el motivo por el cual está rechazando este contrato:
              </p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Escriba aquí el motivo del rechazo..."
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #d1d5db",
                  minHeight: "100px",
                  fontSize: "14px",
                }}
              />
            </div>

            {/* Footer */}
            <div
              style={{
                padding: "16px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                backgroundColor: "#f9fafb",
              }}
            >
              <button
                onClick={() => setShowRejectModal(false)}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#e5e7eb",
                  color: "#374151",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmReject}
                disabled={!rejectionReason.trim()}
                style={{
                  padding: "8px 16px",
                  backgroundColor: rejectionReason.trim() ? "#dc2626" : "#f87171",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: rejectionReason.trim() ? "pointer" : "not-allowed",
                  opacity: rejectionReason.trim() ? 1 : 0.7,
                }}
              >
                Confirmar Rechazo
              </button>
            </div>
          </div>
        </div>
      )}

      {showAnulacionModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 9999,
        }}>
          <div style={{
            backgroundColor: "white", width: "90%", maxWidth: "500px",
            borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
            display: "flex", flexDirection: "column",
          }}>
            {/* Header */}
            <div style={{
              padding: "16px", borderBottom: "1px solid #e5e7eb",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              backgroundColor: "#f5f3ff", borderRadius: "8px 8px 0 0",
            }}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#5b21b6" }}>
                Anular contrato
              </h3>
              <button
                onClick={() => setShowAnulacionModal(false)}
                style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#6b7280" }}
              >×</button>
            </div>

            {/* Body */}
            <div style={{ padding: "16px" }}>
              <p style={{ marginBottom: "12px", fontSize: "14px", color: "#4b5563" }}>
                Esta acción marcará el contrato como <strong>ANULADO</strong> y
                aparecerá una marca de agua en el PDF. Indique el motivo:
              </p>
              <textarea
                value={anulacionReason}
                onChange={(e) => setAnulacionReason(e.target.value)}
                placeholder="Escriba aquí el motivo de la anulación..."
                style={{
                  width: "100%", padding: "8px", borderRadius: "4px",
                  border: "1px solid #d1d5db", minHeight: "100px",
                  fontSize: "14px", boxSizing: "border-box",
                }}
              />
            </div>

            {/* Footer */}
            <div style={{
              padding: "16px", borderTop: "1px solid #e5e7eb",
              display: "flex", justifyContent: "flex-end", gap: "8px",
              backgroundColor: "#f9fafb", borderRadius: "0 0 8px 8px",
            }}>
              <button
                onClick={() => setShowAnulacionModal(false)}
                style={{
                  padding: "8px 16px", backgroundColor: "#e5e7eb",
                  color: "#374151", border: "none", borderRadius: "4px",
                  fontSize: "14px", cursor: "pointer",
                }}
              >Cancelar</button>
              <button
                onClick={handleConfirmAnulacion}
                disabled={!anulacionReason.trim() || loading}
                style={{
                  padding: "8px 16px",
                  backgroundColor: anulacionReason.trim() ? "#7c3aed" : "#c4b5fd",
                  color: "white", border: "none", borderRadius: "4px",
                  fontSize: "14px", fontWeight: 500,
                  cursor: anulacionReason.trim() ? "pointer" : "not-allowed",
                }}
              >
                {loading ? "Anulando..." : "Confirmar anulación"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAnularCdpModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 9999,
        }}>
          <div style={{
            backgroundColor: "white", width: "90%", maxWidth: "500px",
            borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
            display: "flex", flexDirection: "column",
          }}>
            {/* Header */}
            <div style={{
              padding: "16px", borderBottom: "1px solid #e5e7eb",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              backgroundColor: "#fef3c7", borderRadius: "8px 8px 0 0",
            }}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#92400e" }}>
                Anular CDP
              </h3>
              <button
                onClick={() => setShowAnularCdpModal(false)}
                style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#6b7280" }}
              >×</button>
            </div>

            {/* Body */}
            <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>
                  Número del CDP a anular *
                </label>
                <input
                  type="text"
                  value={anularCdpNumero}
                  onChange={(e) => setAnularCdpNumero(e.target.value)}
                  placeholder="Ej: CDP-1020"
                  style={{
                    width: "100%", padding: "8px", borderRadius: "4px",
                    border: "1px solid #d1d5db", fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>
                  Necesidad anulada (PDF) *
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setAnularCdpFile(e.target.files?.[0] || null)}
                  style={{ fontSize: "14px" }}
                />
                {anularCdpFile && (
                  <p style={{ marginTop: "4px", fontSize: "12px", color: "#6b7280" }}>
                    {anularCdpFile.name}
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: "16px", borderTop: "1px solid #e5e7eb",
              display: "flex", justifyContent: "flex-end", gap: "8px",
              backgroundColor: "#f9fafb", borderRadius: "0 0 8px 8px",
            }}>
              <button
                onClick={() => setShowAnularCdpModal(false)}
                style={{
                  padding: "8px 16px", backgroundColor: "#e5e7eb",
                  color: "#374151", border: "none", borderRadius: "4px",
                  fontSize: "14px", cursor: "pointer",
                }}
              >Cancelar</button>
              <button
                onClick={handleAnularCdpSubmit}
                disabled={!anularCdpNumero.trim() || !anularCdpFile || loading}
                style={{
                  padding: "8px 16px",
                  backgroundColor: (anularCdpNumero.trim() && anularCdpFile) ? "#d97706" : "#fcd34d",
                  color: "white", border: "none", borderRadius: "4px",
                  fontSize: "14px", fontWeight: 500,
                  cursor: (anularCdpNumero.trim() && anularCdpFile) ? "pointer" : "not-allowed",
                }}
              >
                {loading ? "Enviando..." : "Enviar a Presupuesto"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEnviarCdpModal && (
        <EnviarCDP
          userRole={userRole}
          solicitud={null}
          cdpFormulario={null}
          contratoFirmado={contract}
          contractId={contract?.id}
          onClose={() => setShowEnviarCdpModal(false)}
          onSubmit={handleEnviarCdpSubmit}
        />
      )}

      {showEnviarRpModal && (
        <EnviarRP
          contratoFirmado={contract}
          onClose={() => setShowEnviarRpModal(false)}
          onSubmit={handleEnviarRpSubmit}
        />
      )}
      {showEnviarArlModal && (
  <EnviarARL
    contratoFirmado={contract}
    onClose={() => setShowEnviarArlModal(false)}
    onSubmit={handleEnviarArlSubmit}
  />
)}
      <PresupuestoModal
        isOpen={presupuestoModal.isOpen}
        mode={presupuestoModal.mode}
        requestType={presupuestoModal.requestType}
        existingRequest={presupuestoModal.existingRequest}
        onSubmit={handlePresupuestoResponderSubmit}
        onClose={() =>
          setPresupuestoModal({
            isOpen: false,
            mode: null,
            requestType: null,
            existingRequest: null,
            cdpFormulario: null,
            rpFormulario: null,
          })
        }
      />
    </div>
  )
}

